import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChartLayout, EquityContent, MethodologyContent, OverviewContent, RelevanceContent } from '../charts/ChartLayout';
// ChartRendererSupabase is already imported above in this file; keep single import
import { useLocation } from '../../contexts/LocationContext';
import { useAdminPageSettings } from '../../hooks/useAdminPageSettings';
import { fetchHeatPumps } from '../../services/heatPumpService';
import { supabaseClient } from '../../services/supabase';
import { setBuildingsEnergyOverrideFromEnergyPage } from '../../services/buildingsEnergyDataService';
import { fetchCaliforniaStateEnergyActivityFromGhgInventory } from '../../services/californiaGhgInventoryEnergyActivityService';
import { CALIFORNIA_STATE_GEOFIPS } from '../../services/californiaGhgInventoryVmtService';
import {
  downloadChartAsPNG,
  downloadChartJsInstanceAsPNG,
  downloadDataAsCSV,
  formatFilename,
} from '../../utils/downloadUtils';
import {
  changeDirectionWord,
  compareLocalToCalifornia,
  formatCount,
  formatCurrencyUsd,
  formatMw,
  formatPercentagePointChange,
  formatSignedPercentChange,
  impliedEmissionsChangePercent,
  latestCommonPositiveYear,
  latestCompletePositiveYear,
  latestPositiveYearFromSeries,
  latestPositiveYearPair,
  mergePerCapitaCaliforniaDatasets,
  percentChangeLabel,
  priorCompletePositiveYear,
  sectorSharePercent,
  unitsByYearFromRecords,
  yoyPercentChange,
} from '../../utils/buildingsMetricHelpers';
import { BurbankBuildingsReconciledChart } from '../charts/BurbankBuildingsReconciledChart';
import {
  burbankElecTabToRev2View,
  burbankElecTabUsesReconciledChart,
  type BurbankBuildingsElecTab,
} from '../../domains/capAnchoredActivity/burbankBuildingsElectricityViews';
import { formatActivityUnits } from '../../domains/capAnchoredActivity/canonical/formatExperimentValues';
import { useBurbankReconciledCanonical } from '../../hooks/useBurbankReconciledCanonical';
import type { Chart as ChartJS } from 'chart.js';
import {
  ENERGY_SECTOR_COLORS,
  applyUserObservedAnchors,
  chartSeriesHasEstimatedYears,
  provenanceByYearFromChartDatasets,
  type EnergySeriesProvenance,
} from '../../utils/energySeriesAnchors';
import { ChartRendererSupabase } from '../charts/ChartRendererSupabase';
import { DataTable } from '../charts/DataTable';
import {
  COMMERCIAL_MODEL_OPTIONS,
  CommercialExtrapolationModel,
  RESIDENTIAL_MODEL_OPTIONS,
  ResidentialExtrapolationModel,
  classifyEnergyRowProvenance,
  formatCommercialModelLabelWithSmape,
  fetchElectricitySeries,
  fetchNaturalGasSeries
} from '../charts/EnergyUseSupabase';
import { HeatPumpCostChart } from '../charts/HeatPumpCostChart';
import { HeatPumpTechnologyChart } from '../charts/HeatPumpTechnologyChart';
import { HeatPumpUnitsChart } from '../charts/HeatPumpUnitsChart';
import { HeatPumpPerCapitaChart } from '../charts/HeatPumpPerCapitaChart';
import { KeyMetric, KeyMetrics } from '../charts/KeyMetrics';
import fetchSolarPVCapacitySupabase from '../charts/SolarPVCapacitySupabase';
import fetchSolarPVCostSupabase from '../charts/SolarPVCostSupabase';
import fetchSolarPVProjectCountSupabase from '../charts/SolarPVProjectCountSupabase';
import fetchSolarPVBatteryCostSupabase from '../charts/SolarPVBatteryCostSupabase';
import fetchSolarPVInstallersSupabase from '../charts/SolarPVInstallersSupabase';
import fetchSolarPVInstallersCostSupabase from '../charts/SolarPVInstallersCostSupabase';
import { PageHeader } from '../layout/PageHeader';
import { fetchAllPvSize } from '../../services/allPvSizeService';
import { CumulativeSolarCapacityChart } from '../charts/CumulativeSolarCapacityChart';
import { SolarPerCapitaComparisonChart } from '../charts/SolarPerCapitaComparisonChart';
import CarbonIntensityChart from '../charts/CarbonIntensityChart';
import { ElectricityPricesByGeographyChart, type ElectricityPricesChartView } from '../charts/ElectricityPricesByGeographyChart';
import {
  effectivePricePerKwh,
  getLatestAveragePrice,
  type ElectricityPricesByGeographyResult,
} from '../../services/electricityPricesByGeographyService';
import CaliforniaBayRENTool from '../../domains/bayren/components/CaliforniaBayRENTool';
import { CustomSectionsForPage } from '../customContent/CustomSectionsForPage';
import { CustomDatasetsForPage } from '../customContent/CustomDatasetsForPage';
import { DataReviewBanner } from '../charts/DataReviewBanner';
import { isElectricityDataUnderReview, getLocationNameUnderReview, isElectricityDataUnderReviewByName, getGeofipsFromLocationName } from '../../utils/dataReviewLocations';
import BusinessEstablishmentsSection from '../charts/BusinessEstablishmentsSection';
import { HousingToEmployees } from '../charts/HousingToEmployees';
import GenericMapTiered, { type MapMetrics } from '../charts/GenericMapTiered';
import UrbanHeatIslandMap from '../charts/UrbanHeatIslandMap';
import SingleFamilyDetached from '../charts/SingleFamilyDetached';
import MarketRentChart from '../charts/MarketRentChart';
import HouseHeatingFuelSupabase, { type HeatingFuelMetrics } from '../charts/HouseHeatingFuelSupabase';
import {
  buildHeatingFuelAuthoredBundle,
  heatingFuelOverviewDescription,
  type HeatingFuelViewId,
} from '../../domains/narrative/heatingFuelNarrative';
import LeadEnergyCostBurdenSection from '../charts/LeadEnergyCostBurdenSection';
import {
  buildElectricityAuthoredBundle,
  buildElectricityKeyInsights,
  buildElectricityMethodologySteps,
  electricityOverviewDescription,
  type ElectricityViewId,
} from '../../domains/narrative/electricityConsumptionNarrative';
import {
  buildNaturalGasAuthoredBundle,
  buildNaturalGasKeyInsights,
  buildNaturalGasOverviewDescription,
} from '../../domains/narrative/naturalGasConsumptionNarrative';
import { buildNaturalGasNarrativeInputFromCharts } from '../../domains/narrative/buildNaturalGasNarrativeInput';
import {
  fetchHeatPumpCostFacts,
  fetchHeatPumpPerCapitaFacts,
  heatPumpTechnologyShareEligible,
  type HeatPumpCostFacts,
  type HeatPumpPerCapitaFacts,
} from '../../domains/narrative/heatPumpNarrativeFacts';
import {
  DGSTATS_COVERAGE_UNAVAILABLE_LABEL,
  isSolarCoverageBlocked,
  resolveDgStatsCoverageForGeofips,
  resolveSolarDgStatsCoverageFacts,
  type DgStatsCoverageFacts,
  type DgStatsCoverageStatus,
} from '../../services/solarDgStatsCoverage';
import {
  isSolarCostViewHeld,
  isSolarBatteryCostViewHeld,
  isSolarInstallerCostViewHeld,
  isSolarCostDerivedViewHeld,
  SOLAR_BATTERY_COST_HOLD_MESSAGE,
  SOLAR_COST_HOLD_PUBLIC_MESSAGE,
  SOLAR_INSTALLER_COST_HOLD_MESSAGE,
} from '../../services/solarDgStatsLineage';
import {
  buildSolarInstallationsAuthoredBundle,
  type SolarPvViewId,
} from '../../domains/narrative/solarInstallationsNarrative';
import {
  buildSolarInstallationNarrativeFacts,
} from '../../domains/narrative/solarInstallationsNarrativeFacts';
import { SOLAR_INSTALLER_MIN_SAMPLE } from '../../domains/narrative/solarCostStatistics';
import {
  computeSolarCumulativeSnapshot,
  computeSolarPerCapitaSnapshot,
  SOLAR_ANALYSIS_CUTOFF_YEAR,
  type SolarCumulativeSnapshot,
  type SolarPerCapitaSnapshot,
} from '../../services/solarViewMetrics';
import {
  buildSolarCapacityExportRows,
  buildSolarCumulativeExportRows,
  buildSolarInstallerCapacityExportRows,
  buildSolarPerCapitaExportRows,
  isSolarPublicExportView,
} from '../../services/solarPublicExports';
import {
  fetchGasProvenanceForView,
  type GasFormatProvenanceRow,
} from '../../services/gasFormatProvenanceFetch';
import { geofipsVariantsForEnergyFormat } from '../../utils/energyFormatQuery';
import {
  buildUrbanHeatIslandAuthoredBundle,
  urbanHeatIslandOverviewDescription,
} from '../../domains/narrative/urbanHeatIslandNarrative';
import { summarizeUrbanHeatIslandTracts } from '../../domains/narrative/urbanHeatIslandNarrativeFacts';
import { buildCarbonSourceStatusSentence } from '../../domains/narrative/carbonIntensityProvenance';
import { buildCarbonIntensityVerifiedMethodology } from '../../domains/narrative/carbonIntensityMethodologyStructured';
import { buildElectricityPricesVerifiedMethodology } from '../../domains/narrative/electricityPricesMethodologyStructured';
import {
  buildCarbonIntensityAuthoredBundle,
  buildCarbonIntensityKeyInsights,
  carbonIntensityOverviewDescription,
} from '../../domains/narrative/carbonIntensityNarrative';
import {
  buildElectricityPricesAuthoredBundle,
  buildElectricityPricesKeyInsights,
  electricityPricesOverviewDescription,
} from '../../domains/narrative/electricityPricesNarrative';
import {
  resolveMethodologyContentProps,
  structuredFromAuthoredBundle,
} from '../../domains/narrative/methodologyContentProps';
import { AuthoredEquityPanel, AuthoredRelevancePanel } from '../../domains/narrative/narrativeSectionContent';
import SectionWrapper, { useSectionReveal } from './SectionWrapper';
import AffordableHousingAprSection from '../charts/AffordableHousingAprSection';
import { resolveEia861PilotForGeofips } from '../../domains/eia861UtilityElectricity/registry';
import { electricitySectorLabelForGeofips } from '../../domains/eia861UtilityElectricity/municipalUtilityElectricity';
import {
  buildElectricityStackedTotalFromEia861Pilot,
  eia861FirstTrendBaselineYear,
} from '../../domains/eia861UtilityElectricity/buildEnergyChartFromEia861';
import { buildEia861CanonicalElectricitySeries } from '../../domains/eia861UtilityElectricity/buildEia861CanonicalSeries';
import {
  formatElectricityTooltipPrimary,
  formatGwhAxisTickFromKwh,
  formatGwhHeadline,
} from '../../utils/formatElectricityGwh';

import electricityBg from '../../assets/buildings/electricity-bg.jpg';
import businessEstablishmentsBg from '../../assets/buildings/business-establishments-bg.jpg';
import solarPvBg from '../../assets/buildings/solar-pv-bg.jpg';
import heatPumpBg from '../../assets/buildings/heat-pump-bg.jpg';
import carbonIntensityBg from '../../assets/buildings/carbon-intensity-real-bg.jpg';
import electricityPricesBg from '../../assets/buildings/electricity-prices-real-bg.jpg';
import naturalGasRealBg from '../../assets/buildings/natural-gas-consumption-bg.jpg';
import heatingFuelBg from '../../assets/buildings/heating-fuel-real-bg.jpg';
import housingToEmployeesBg from '../../assets/buildings/housing-to-employees-bg.jpg';
import singleFamilyBg from '../../assets/buildings/single-family-bg.jpg';
import ownerOccupiedBg from '../../assets/buildings/owner-occupied-bg.jpg';
import marketRentBg from '../../assets/buildings/market-rent-bg.jpg';
import californiaBg from '../../assets/buildings/california-bg.jpg';

const BUILDINGS_SECTION_BG: Record<string, string> = {
  electricity_consumption: electricityBg,
  carbon_intensity: carbonIntensityBg,
  electricity_prices: electricityPricesBg,
  energy_cost_burden: electricityPricesBg,
  natural_gas_consumption: naturalGasRealBg,
  heating_fuel: heatingFuelBg,
  solar_pv: solarPvBg,
  heat_pump: heatPumpBg,
  business_establishments: businessEstablishmentsBg,
  housing_to_employees: housingToEmployeesBg,
  single_family_detached: singleFamilyBg,
  owner_occupied_map: ownerOccupiedBg,
  urban_heat_island_map: heatPumpBg,
  housing_rent_category: marketRentBg,
  affordable_housing_production: californiaBg,
  bayren_tool: californiaBg,
};

const BUILDINGS_SECTION_IDS = Object.keys(BUILDINGS_SECTION_BG);

const THERMS_TO_GJ = 0.1055;

const sumDatasetsByYear = (labels: number[], datasets: any[]): Map<number, number> => {
  const map = new Map<number, number>();
  labels.forEach((year: number, i: number) => {
    let total = 0;
    datasets.forEach((dataset: any) => { total += (dataset.data?.[i] || 0); });
    map.set(Number(year), total);
  });
  return map;
};

const makeDownloadPNG = (selector: string, label: string, location: string) => {
  const canvas = document.querySelector(`${selector} canvas`) as HTMLCanvasElement;
  if (canvas) {
    const filename = formatFilename(location, label);
    const link = document.createElement('a');
    link.download = `${filename}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  }
};

const buildChartOptions = (overrides: {
  type?: string;
  title: string;
  tooltipSuffix?: string;
  tooltipFormat?: (v: number) => string;
  xStacked?: boolean;
  yStacked?: boolean;
  yTitle?: string;
  yTickFormat?: (v: any) => string;
}) => ({
  ...(overrides.type ? { type: overrides.type } : {}),
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom' as const,
      labels: { padding: 10, usePointStyle: true, pointStyle: 'rect' }
    },
    title: { display: true, text: overrides.title },
    tooltip: {
      callbacks: {
        label: (ctx: any) => {
          const v = Number(ctx.parsed.y);
          if (overrides.tooltipFormat) {
            return `${ctx.dataset.label}: ${Number.isFinite(v) ? overrides.tooltipFormat(v) : v}`;
          }
          const formatted = Number.isFinite(v) ? v.toLocaleString() : v;
          return overrides.tooltipSuffix
            ? `${ctx.dataset.label}: ${formatted} ${overrides.tooltipSuffix}`
            : `${ctx.dataset.label}: ${formatted}`;
        }
      }
    }
  },
  scales: {
    x: {
      ...(overrides.xStacked ? { stacked: true } : {}),
      title: { display: true, text: 'Year' }
    },
    y: {
      ...(overrides.yStacked ? { stacked: true } : {}),
      beginAtZero: true,
      title: { display: true, text: overrides.yTitle || '' },
      ...(overrides.yTickFormat ? { ticks: { callback: overrides.yTickFormat } } : {})
    }
  },
  layout: { padding: { bottom: 20 } }
});

const buildTableData = (
  resChartData: any,
  comChartData: any,
  location: string
) => {
  const resLabels = (resChartData.labels || []) as number[];
  const comLabels = (comChartData.labels || []) as number[];
  const resYears = new Set(resLabels);
  const comYears = new Set(comLabels);
  const years: number[] = Array.from(resYears)
    .filter((year: number) => comYears.has(year))
    .sort((a, b) => a - b);
  const resMap = sumDatasetsByYear(resLabels, resChartData.datasets || []);
  const comMap = sumDatasetsByYear(comLabels, comChartData.datasets || []);
  const resProv = provenanceByYearFromChartDatasets(resLabels, resChartData.datasets || []);
  const comProv = provenanceByYearFromChartDatasets(comLabels, comChartData.datasets || []);
  return years.map((year) => ({
    year,
    residential: Math.round(Number(resMap.get(year) || 0)),
    commercial: Math.round(Number(comMap.get(year) || 0)),
    total: Math.round(Number(resMap.get(year) || 0) + Number(comMap.get(year) || 0)),
    location,
    residentialProvenance: resProv.get(year) || 'extrapolated',
    commercialProvenance: comProv.get(year) || 'extrapolated',
  }));
};

function useFilteredChartOptions(
  sectionId: string,
  options: { id: string; value: string; label: string }[],
  view: string,
  setView: (v: any) => void,
  isChartEnabled: (section: string, chart: string) => boolean,
  getChartOrder: (section: string, chart: string) => number
): { id: string; value: string; label: string }[] {
  const allowed = options
    .filter((option) => isChartEnabled(sectionId, option.id))
    .sort((a, b) => getChartOrder(sectionId, a.id) - getChartOrder(sectionId, b.id));
  React.useEffect(() => {
    if (allowed.length && !allowed.some((opt) => opt.value === view)) {
      setView(allowed[0].value);
    }
  }, [allowed, view, setView]);
  return allowed;
}

function useCommercialModelOptions(
  comData: any,
  energyType: 'electricity' | 'naturalGas',
  extrapModel: CommercialExtrapolationModel,
  setExtrapModel: (v: CommercialExtrapolationModel) => void
) {
  const available = React.useMemo(() => {
    const allowed = comData?.availableCommercialModels;
    const allowSet =
      Array.isArray(allowed) && allowed.length > 0 ? new Set(allowed) : null;
    const opts = COMMERCIAL_MODEL_OPTIONS.filter((o) =>
      allowSet ? allowSet.has(o.value) : false
    );
    return opts.map((o) => ({
      ...o,
      label: formatCommercialModelLabelWithSmape(
        o.value,
        energyType,
        comData?.commercialObservedInterpolatedYears
      )
    }));
  }, [comData, energyType]);
  React.useEffect(() => {
    if (!available.length) return;
    if (!available.some((o) => o.value === extrapModel)) {
      const fallback =
        available.find((o) => o.value === 'average')?.value ||
        available[0].value;
      setExtrapModel(fallback);
    }
  }, [available, extrapModel, setExtrapModel]);
  return available;
}

function useEditableDataTable(
  energyType: 'electricity' | 'naturalGas',
  resData: any,
  comData: any,
  stackedData: any,
  setResData: (v: any) => void,
  setComData: (v: any) => void,
  setStackedData: (v: any) => void,
  currentLocation: string
) {
  const [showDataTable, setShowDataTable] = React.useState(false);
  const [tableData, setTableData] = React.useState<any[]>([]);
  const [originalData, setOriginalData] = React.useState<any[]>([]);
  const [modifiedResYears, setModifiedResYears] = React.useState<Set<number>>(new Set());
  const [modifiedComYears, setModifiedComYears] = React.useState<Set<number>>(new Set());
  const pristineRef = React.useRef<{ res: any; com: any; stacked: any } | null>(null);

  const modifiedYears = React.useMemo(() => {
    const s = new Set<number>();
    modifiedResYears.forEach((y) => s.add(y));
    modifiedComYears.forEach((y) => s.add(y));
    return s;
  }, [modifiedResYears, modifiedComYears]);

  const toggleDataTable = () => setShowDataTable((prev) => !prev);

  React.useEffect(() => {
    pristineRef.current = null;
    setModifiedResYears(new Set());
    setModifiedComYears(new Set());
  }, [currentLocation]);

  React.useEffect(() => {
    if (
      originalData.length > 0 &&
      modifiedYears.size === 0 &&
      resData &&
      comData &&
      !pristineRef.current
    ) {
      pristineRef.current = {
        res: resData,
        com: comData,
        stacked: stackedData,
      };
    }
  }, [originalData, resData, comData, stackedData, modifiedYears]);

  const rebuildSectorDatasets = (
    chartState: any,
    years: number[],
    values: number[],
    sources: EnergySeriesProvenance[],
    sector: 'residential' | 'commercial',
    modYears: Set<number>
  ) => {
    if (!chartState?.chartData) return chartState;
    const colors = ENERGY_SECTOR_COLORS[sector];
    const existing = chartState.chartData.datasets || [];
    const findExisting = (prov: EnergySeriesProvenance) =>
      existing.find((ds: any) =>
        String(ds.label || '')
          .toLowerCase()
          .includes(prov === 'observed' ? 'observed' : prov === 'interpolated' ? 'interpolat' : 'extrapola')
      );

    const datasets = (['observed', 'interpolated', 'extrapolated'] as EnergySeriesProvenance[]).map(
      (prov) => {
        const prev = findExisting(prov);
        return {
          type: 'bar',
          ...(prev || {}),
          label:
            prev?.label ||
            (prov === 'observed'
              ? 'Observed'
              : prov === 'interpolated'
                ? 'Interpolated'
                : 'Extrapolated'),
          data: years.map((_, i) => (sources[i] === prov ? values[i] : 0)),
          backgroundColor: years.map((y, i) => {
            if (sources[i] !== prov) return colors[prov];
            if (prov === 'observed' && modYears.has(y)) return colors.userModified;
            return colors[prov];
          }),
          borderColor: '#374151',
          borderWidth: 1,
        };
      }
    );

    return {
      ...chartState,
      chartData: {
        ...chartState.chartData,
        labels: years,
        datasets,
      },
    };
  };

  const updateCharts = (
    data: any[],
    resSources: EnergySeriesProvenance[],
    comSources: EnergySeriesProvenance[],
    modResYears: Set<number>,
    modComYears: Set<number>
  ) => {
    if (!resData || !comData) return;
    const years = data.map((d) => d.year);
    const resValues = data.map((d) => d.residential);
    const comValues = data.map((d) => d.commercial);

    const nextRes = rebuildSectorDatasets(resData, years, resValues, resSources, 'residential', modResYears);
    const nextCom = rebuildSectorDatasets(comData, years, comValues, comSources, 'commercial', modComYears);
    setResData(nextRes);
    setComData(nextCom);

    const stackedChartData = {
      labels: years,
      datasets: [
        {
          label: 'Residential',
          data: resValues,
          backgroundColor: years.map((y) =>
            modResYears.has(y)
              ? ENERGY_SECTOR_COLORS.residential.userModified
              : energyType === 'electricity'
                ? '#1e40af'
                : '#166534'
          ),
          borderColor: energyType === 'electricity' ? '#1e40af' : '#166534',
          borderWidth: 1,
          stack: 'total',
        },
        {
          label: 'Commercial',
          data: comValues,
          backgroundColor: years.map((y) =>
            modComYears.has(y)
              ? ENERGY_SECTOR_COLORS.commercial.userModified
              : energyType === 'electricity'
                ? '#3b82f6'
                : '#22c55e'
          ),
          borderColor: energyType === 'electricity' ? '#3b82f6' : '#22c55e',
          borderWidth: 1,
          stack: 'total',
        },
      ],
    };
    setStackedData({
      ...(stackedData || {}),
      chartData: stackedChartData,
      chartOptions: stackedData?.chartOptions,
      latestYear: years.length ? Math.max(...years) : undefined,
      latestValue: years.length ? resValues[resValues.length - 1] + comValues[comValues.length - 1] : 0,
    });
  };

  const handleDataTableChange = (
    newData: any[],
    meta?: { year?: number; key?: string; action?: 'edit' | 'add' | 'delete' }
  ) => {
    const nextResMods = new Set(modifiedResYears);
    const nextComMods = new Set(modifiedComYears);

    if (meta?.action === 'delete' && meta.year != null && Number.isFinite(meta.year)) {
      nextResMods.delete(meta.year);
      nextComMods.delete(meta.year);
    } else if (meta?.year != null && Number.isFinite(meta.year)) {
      const y = Number(meta.year);
      if (meta.key === 'residential' || meta.action === 'add') nextResMods.add(y);
      if (meta.key === 'commercial' || meta.action === 'add') nextComMods.add(y);
      // If key is year or unknown edit, treat both sectors as edited for that year
      if (meta.action === 'edit' && meta.key !== 'residential' && meta.key !== 'commercial') {
        nextResMods.add(y);
        nextComMods.add(y);
      }
    } else {
      // Fallback: only mark years the user changed in this payload vs current table
      newData.forEach((newItem) => {
        const cur = tableData.find((d) => Number(d.year) === Number(newItem.year));
        if (!cur) return;
        if (Math.abs(Number(newItem.residential) - Number(cur.residential)) > 1) {
          nextResMods.add(Number(newItem.year));
        }
        if (Math.abs(Number(newItem.commercial) - Number(cur.commercial)) > 1) {
          nextComMods.add(Number(newItem.year));
        }
      });
    }

    const years = (newData.length ? newData : originalData)
      .map((d) => Number(d.year))
      .filter((y) => Number.isFinite(y))
      .sort((a, b) => a - b);
    const editedRes = new Map(newData.map((d) => [Number(d.year), Number(d.residential)]));
    const editedCom = new Map(newData.map((d) => [Number(d.year), Number(d.commercial)]));
    const origRes = new Map(originalData.map((d) => [Number(d.year), Number(d.residential)]));
    const origCom = new Map(originalData.map((d) => [Number(d.year), Number(d.commercial)]));
    const origResProv = new Map(
      originalData.map((d) => [
        Number(d.year),
        (d.residentialProvenance || 'extrapolated') as EnergySeriesProvenance,
      ])
    );
    const origComProv = new Map(
      originalData.map((d) => [
        Number(d.year),
        (d.commercialProvenance || 'extrapolated') as EnergySeriesProvenance,
      ])
    );

    const resRe = applyUserObservedAnchors({
      years,
      editedValuesByYear: editedRes,
      originalValuesByYear: origRes,
      originalProvenanceByYear: origResProv,
      modifiedYears: nextResMods,
    });
    const comRe = applyUserObservedAnchors({
      years,
      editedValuesByYear: editedCom,
      originalValuesByYear: origCom,
      originalProvenanceByYear: origComProv,
      modifiedYears: nextComMods,
    });

    const location = originalData[0]?.location || currentLocation || '';
    const finalData = years.map((year, i) => ({
      year,
      residential: resRe.values[i],
      commercial: comRe.values[i],
      total: resRe.values[i] + comRe.values[i],
      location,
      residentialProvenance: resRe.sources[i],
      commercialProvenance: comRe.sources[i],
    }));

    setModifiedResYears(nextResMods);
    setModifiedComYears(nextComMods);
    setTableData(finalData);
    setBuildingsEnergyOverrideFromEnergyPage(currentLocation, energyType, finalData, {
      residential: resRe.sources,
      commercial: comRe.sources,
    });
    updateCharts(finalData, resRe.sources, comRe.sources, nextResMods, nextComMods);
  };

  const restoreDefaults = () => {
    if (originalData.length > 0) {
      setTableData(originalData.map((d) => ({ ...d })));
      setModifiedResYears(new Set());
      setModifiedComYears(new Set());
      if (pristineRef.current) {
        setResData(pristineRef.current.res);
        setComData(pristineRef.current.com);
        setStackedData(pristineRef.current.stacked);
      }
    }
  };

  return {
    showDataTable,
    tableData,
    setTableData,
    originalData,
    setOriginalData,
    modifiedYears,
    toggleDataTable,
    handleDataTableChange,
    restoreDefaults
  };
}

// ─── End extracted helpers ───────────────────────────────────────────

interface EnergyProps {
  location?: string;
}

interface HeatPumpRecord {
  year: number;
  system: string;
  units_installed: number;
  project_cost_per_unit_usd: number;
  annual_ghg_savings_mtco2e: number;
}

// Function to calculate stacked data as sum of residential + commercial
const calculateStackedData = (
  resData: any,
  comData: any,
  type: string,
  locationName?: string,
  nonresStackLabel = 'Commercial'
) => {
  if (!resData?.chartData || !comData?.chartData) return null;

  const resLabels = (resData.chartData.labels || []) as number[];
  const comLabels = (comData.chartData.labels || []) as number[];
  
  // Get years that exist in both residential and commercial data
  const resYears = new Set(resLabels);
  const comYears = new Set(comLabels);
  const allYears: number[] = Array.from(resYears).filter((year: number) => comYears.has(year)).sort();
  
  // Extract data from all datasets (observed, interpolated, extrapolated) for each sector
  const resDatasets = resData.chartData.datasets || [];
  const comDatasets = comData.chartData.datasets || [];
  
  const resMap = sumDatasetsByYear(resLabels, resDatasets);
  const comMap = sumDatasetsByYear(comLabels, comDatasets);

  // Calculate stacked values
  const stackedValues = allYears.map(year => 
    (resMap.get(year) as number || 0) + (comMap.get(year) as number || 0)
  );

  // Create stacked chart data
  const stackedChartData = {
    labels: allYears,
    datasets: [
      {
        label: 'Residential',
        data: allYears.map(year => resMap.get(year) || 0),
        backgroundColor: type === 'Electricity' ? '#1e40af' : '#166534',
        borderColor: type === 'Electricity' ? '#1e3a5f' : '#166534',
        borderWidth: 1,
        stack: 'total'
      },
      {
        label: nonresStackLabel,
        data: allYears.map(year => comMap.get(year) || 0),
        backgroundColor: type === 'Electricity' ? '#0d9488' : '#22c55e',
        borderColor: type === 'Electricity' ? '#115e59' : '#22c55e',
        borderWidth: 1,
        stack: 'total'
      }
    ]
  };

  const electricityGwh = type === 'Electricity';
  const stackedChartOptions = buildChartOptions({
    type: 'bar',
    title: locationName
      ? electricityGwh
        ? `Total electricity consumption`
        : `Total ${type} Consumption (Therms) - ${locationName}`
      : `Total ${type} (Stacked)`,
    xStacked: true,
    yStacked: true,
    yTitle: electricityGwh ? 'GWh' : 'Therms',
    yTickFormat: electricityGwh
      ? (v: any) => formatGwhAxisTickFromKwh(Number(v))
      : (v: any) => Number(v).toLocaleString(),
    tooltipFormat: electricityGwh ? (v: number) => formatElectricityTooltipPrimary(v) : undefined,
  });

  return {
    chartData: stackedChartData,
    chartOptions: stackedChartOptions,
    latestYear: Math.max(...allYears),
    latestValue: stackedValues[stackedValues.length - 1] || 0
  };
};

export const Energy: React.FC<EnergyProps> = ({ location: propLocation }) => {
  const { selectedLocation, selectedGeofips, coreDataLoading } = useLocation();
  const [marketRentChartView, setMarketRentChartView] = useState<'line' | 'stacked'>('stacked');
  const [marketRentNarrative, setMarketRentNarrative] = useState<string | null>(null);
  const [marketRentNarrativeStacked, setMarketRentNarrativeStacked] = useState<string | null>(null);
  const [marketRentTrend, setMarketRentTrend] = useState<{ higherRentIncreased: boolean; lowerRentDeclined: boolean } | null>(null);
  const [capacityData, setCapacityData] = useState<any>(null);
  const [costData, setCostData] = useState<any>(null);
  const [projectCountData, setProjectCountData] = useState<any>(null);
  const [elecRes, setElecRes] = useState<any>(null);
  const [elecCom, setElecCom] = useState<any>(null);
  const [elecStacked, setElecStacked] = useState<any>(null);
  const [gasRes, setGasRes] = useState<any>(null);
  const [gasCom, setGasCom] = useState<any>(null);
  const [gasStacked, setGasStacked] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<string>('overview');
  const [activeHeatingFuelView, setActiveHeatingFuelView] = useState<string>('overview');
  const [heatingFuelChartMode, setHeatingFuelChartMode] = useState<'main' | 'other' | 'fossil' | 'compare'>('main');
  const [heatingFuelMetrics, setHeatingFuelMetrics] = useState<HeatingFuelMetrics | null>(null);

  // Scroll-driven fade-in for each section
  const { visible: sectionVisible, refs: sectionObserverRefs } = useSectionReveal(
    BUILDINGS_SECTION_IDS,
    !loading && !coreDataLoading
  );
  const [elecView, setElecView] = useState<BurbankBuildingsElecTab>('total');
  const [commercialExtrapModel, setCommercialExtrapModel] = useState<CommercialExtrapolationModel>('average');
  const [commercialGasExtrapModel, setCommercialGasExtrapModel] = useState<CommercialExtrapolationModel>('average');
  const [residentialExtrapModel, setResidentialExtrapModel] = useState<ResidentialExtrapolationModel>('average');
  const [gasView, setGasView] = useState<'res' | 'com' | 'total' | 'perCapita'>('total');
  const [elecMethodology, setElecMethodology] = useState<{dataSources: string[], methods: string[]} | null>(null);
  const [gasMethodology, setGasMethodology] = useState<{dataSources: string[], methods: string[]} | null>(null);
  
  // Chart refs for download functionality
  const elecChartRef = useRef<HTMLCanvasElement>(null);
  const gasChartRef = useRef<HTMLCanvasElement>(null);
  const [pvView, setPvView] = useState<'capacity' | 'cost' | 'cumulative' | 'perCapita' | 'batteryCost' | 'installers' | 'installersCost'>('capacity');
  const [batteryCostData, setBatteryCostData] = useState<any>(null);
  const [installersData, setInstallersData] = useState<any>(null);
  const [installersCostData, setInstallersCostData] = useState<any>(null);
  const [solarPerCapitaTrend, setSolarPerCapitaTrend] = useState<'increasing' | 'decreasing' | 'stable' | null>(null);
  const [solarPerCapitaSnapshot, setSolarPerCapitaSnapshot] = useState<SolarPerCapitaSnapshot | null>(null);
  const [solarCumulativeSnapshot, setSolarCumulativeSnapshot] = useState<SolarCumulativeSnapshot | null>(null);
  
  // Data table state + handlers via shared hook (electricity & gas)
  // NOTE: hooks called here; destructured names match the original variable names used throughout JSX below.
  const elecDataTable = useEditableDataTable('electricity', elecRes, elecCom, elecStacked, setElecRes, setElecCom, setElecStacked, propLocation || selectedLocation || '');
  const gasDataTable = useEditableDataTable('naturalGas', gasRes, gasCom, gasStacked, setGasRes, setGasCom, setGasStacked, propLocation || selectedLocation || '');
  
  // Per capita data state
  const [elecPerCapitaData, setElecPerCapitaData] = useState<any>(null);
  const [gasPerCapitaData, setGasPerCapitaData] = useState<any>(null);
  const [californiaElecPerCapita, setCaliforniaElecPerCapita] = useState<any>(null);
  const [californiaGasPerCapita, setCaliforniaGasPerCapita] = useState<any>(null);
  const [heatPumpMetrics, setHeatPumpMetrics] = useState<any>(null);
  const [heatPumpPerCapitaFacts, setHeatPumpPerCapitaFacts] = useState<HeatPumpPerCapitaFacts | null>(null);
  const [heatPumpCostFacts, setHeatPumpCostFacts] = useState<HeatPumpCostFacts | null>(null);
  const [solarDgStatsCoverage, setSolarDgStatsCoverage] = useState<DgStatsCoverageStatus>('unknown');
  const [solarCoverageFacts, setSolarCoverageFacts] = useState<DgStatsCoverageFacts | null>(null);
  const [populationGeographyType, setPopulationGeographyType] = useState<string | null>(null);
  const [gasProvenanceRow, setGasProvenanceRow] = useState<GasFormatProvenanceRow | null>(null);
  const [heatPumpView, setHeatPumpView] = useState<'cost' | 'technology' | 'units' | 'perCapita'>('units');
  const [activeCarbonIntensityView, setActiveCarbonIntensityView] = useState<string>('overview');
  const [ownerOccupiedHousingMetrics, setOwnerOccupiedHousingMetrics] = useState<{
    currentValue: number;
    californiaAverage: number;
    year: number;
    variable: string;
  } | null>(null);
  const handleOwnerOccupiedHousingDataLoaded = useCallback((metrics: MapMetrics) => {
    if (metrics.californiaAverage == null) return;
    setOwnerOccupiedHousingMetrics({
      currentValue: metrics.currentValue,
      californiaAverage: metrics.californiaAverage,
      year: metrics.year,
      variable: metrics.variable,
    });
  }, []);
  const [activeOwnerOccupiedView, setActiveOwnerOccupiedView] = useState<string>('overview');
  const [activeUrbanHeatIslandView, setActiveUrbanHeatIslandView] = useState<string>('overview');
  const [uhiTractRows, setUhiTractRows] = useState<
    { uhii_percentile: number | null; deg_hour_day: number | null }[]
  >([]);
  const handleUhiTractRowsLoaded = useCallback(
    (rows: { geofips: string | number; uhii_percentile?: string | number | null; deg_hour_day?: string | number | null }[]) => {
      const next = rows.map((row) => ({
        uhii_percentile:
          row.uhii_percentile != null && row.uhii_percentile !== ''
            ? Number(row.uhii_percentile)
            : null,
        deg_hour_day:
          row.deg_hour_day != null && row.deg_hour_day !== ''
            ? Number(row.deg_hour_day)
            : null,
      }));
      setUhiTractRows((prev) => {
        if (
          prev.length === next.length &&
          prev.every(
            (p, i) =>
              p.uhii_percentile === next[i].uhii_percentile && p.deg_hour_day === next[i].deg_hour_day
          )
        ) {
          return prev;
        }
        return next;
      });
    },
    []
  );
  const [carbonIntensityData, setCarbonIntensityData] = useState<any>(null);
  const [electricityPricesData, setElectricityPricesData] = useState<ElectricityPricesByGeographyResult | null>(null);
  const [electricityPricesView, setElectricityPricesView] = useState<string>('overview');
  const [electricityPricesChartView, setElectricityPricesChartView] = useState<'byUtility' | 'locationVsCA'>('locationVsCA');
  const [electricityPricesSector, setElectricityPricesSector] = useState<string>('Residential');
  const [marketRentData, setMarketRentData] = useState<{ year: number; rent_category: string; estimate: number | null; percent: number | null }[]>([]);
  const [heatPumpChartData, setHeatPumpChartData] = useState<Record<string, string | number>[]>([]);

  const currentLocation = propLocation || selectedLocation;

  useEffect(() => {
    setElectricityPricesData(null);
  }, [currentLocation, selectedGeofips]);

  const marketRentKeyInsights = React.useMemo(() => {
    if (!marketRentData.length || !currentLocation) return null;

    const years = Array.from(new Set(marketRentData.map((r) => r.year))).filter((y) => Number.isFinite(y)).sort((a, b) => a - b);
    if (years.length < 2) return null;

    const earliestYear = years[0];
    const latestYear = years[years.length - 1];

    const formatCategoryLabel = (category: string): string => {
      const labelMap: Record<string, string> = {
        no_rent_paid: 'No Rent Paid',
        under_500: 'Under $500',
        '500_to_999': '$500-$999',
        '1000_to_1499': '$1,000-$1,499',
        '1500_and_over': '$1,500 and Over',
        '1500_to_1999': '$1,500-$1,999',
        '2000_to_2499': '$2,000-$2,499',
        '2500_to_2999': '$2,500-$2,999',
        '3000_and_over': '$3,000 and Over',
      };
      return labelMap[category] || category.replace(/_/g, ' ');
    };

    const getRowsForYear = (year: number) => marketRentData.filter((r) => r.year === year && r.rent_category && r.rent_category !== 'other');

    const latestRows = getRowsForYear(latestYear);
    const earliestRows = getRowsForYear(earliestYear);
    if (!latestRows.length || !earliestRows.length) return null;

    const hasPercent = latestRows.some((r) => r.percent != null && Number.isFinite(Number(r.percent)));
    const latestTotalEst = latestRows.reduce((s, r) => s + (Number(r.estimate) || 0), 0);
    const earliestTotalEst = earliestRows.reduce((s, r) => s + (Number(r.estimate) || 0), 0);

    const latestShares = latestRows.map((r) => {
      const pct =
        hasPercent && r.percent != null && Number.isFinite(Number(r.percent))
          ? Number(r.percent)
          : latestTotalEst > 0
          ? ((Number(r.estimate) || 0) / latestTotalEst) * 100
          : 0;
      return { category: r.rent_category, pct };
    });

    const largest = latestShares.sort((a, b) => b.pct - a.pct)[0];
    const largestCategoryLabel = largest ? formatCategoryLabel(largest.category) : '—';

    const isHigherRentCategory = (category: string) => {
      // Treat categories with lower bound >= 1500 as "higher-rent".
      const m = category.match(/^(\d+)_/);
      if (!m) return category.includes('1500');
      return Number(m[1]) >= 1500;
    };

    const shareForYear = (rows: typeof marketRentData, yearTotalEst: number) =>
      rows.reduce((sum, r) => {
        if (!isHigherRentCategory(r.rent_category)) return sum;
        if (hasPercent && r.percent != null && Number.isFinite(Number(r.percent))) return sum + Number(r.percent);
        return yearTotalEst > 0 ? sum + ((Number(r.estimate) || 0) / yearTotalEst) * 100 : sum;
      }, 0);

    const higherLatest = shareForYear(latestRows, latestTotalEst);
    const higherEarliest = shareForYear(earliestRows, earliestTotalEst);
    const delta = higherLatest - higherEarliest;
    const overallChange =
      Math.abs(delta) < 1
        ? 'no significant change'
        : delta > 0
        ? 'an overall increase'
        : 'an overall decrease';

    return {
      earliestYear,
      latestYear,
      largestCategoryLabel,
      overallChange,
    };
  }, [marketRentData, currentLocation]);
  const {
    isSectionEnabled,
    isChartEnabled,
    getSectionOrder,
    getChartOrder
  } = useAdminPageSettings('buildings', currentLocation);

  // Scroll to chart section when opening page with a hash (e.g. from livability index).
  useEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash.slice(1);
      if (!hash) return;
      const el =
        document.getElementById(hash) ||
        document.querySelector(`[data-section-id="${hash}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    const t1 = setTimeout(scrollToHash, 150);
    const t2 = setTimeout(scrollToHash, 500);
    const onHashChange = () => {
      setTimeout(scrollToHash, 80);
      setTimeout(scrollToHash, 400);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('hashchange', onHashChange);
    };
  }, []);

  const electricityNonresSectorLabel = electricitySectorLabelForGeofips(selectedGeofips);

  const allowedElectricityBase = useFilteredChartOptions(
    'electricity_consumption',
    [
      { id: 'electricity_consumption', value: 'total', label: 'Total (Stacked)' },
      { id: 'elec_res', value: 'res', label: 'Residential' },
      { id: 'elec_com', value: 'com', label: electricityNonresSectorLabel },
      { id: 'elec_percapita', value: 'perCapita', label: 'Per Capita' },
    ],
    elecView,
    setElecView,
    isChartEnabled,
    getChartOrder
  );

  const allowedNaturalGasOptions = useFilteredChartOptions('natural_gas_consumption', [
    { id: 'natural_gas_consumption', value: 'total', label: 'Total (Stacked)' },
    { id: 'gas_res', value: 'res', label: 'Residential' },
    { id: 'gas_com', value: 'com', label: 'Commercial' },
    { id: 'gas_percapita', value: 'perCapita', label: 'Per Capita' }
  ], gasView, setGasView, isChartEnabled, getChartOrder);

  const allowedSolarOptions = useFilteredChartOptions('solar_pv', [
    { id: 'solar_capacity', value: 'capacity', label: 'By Approval Cohort' },
    { id: 'cumulative_solar_capacity', value: 'cumulative', label: 'Cumulative Cohort' },
    { id: 'solar_per_capita', value: 'perCapita', label: 'Per Capita' },
    { id: 'solar_cost', value: 'cost', label: 'Cost' },
    { id: 'solar_battery_cost', value: 'batteryCost', label: 'Solar + Storage Cost' },
    { id: 'solar_installers_capacity', value: 'installers', label: 'Installers (Capacity)' },
    { id: 'solar_installers_cost', value: 'installersCost', label: 'Installers (Cost)' }
  ], pvView, setPvView, isChartEnabled, getChartOrder);

  const publicSolarOptions = React.useMemo(
    () =>
      allowedSolarOptions.filter((opt) => {
        if (isSolarCostViewHeld() && opt.value === 'cost') return false;
        if (isSolarBatteryCostViewHeld() && opt.value === 'batteryCost') return false;
        if (isSolarInstallerCostViewHeld() && opt.value === 'installersCost') return false;
        return true;
      }),
    [allowedSolarOptions]
  );

  const solarPublicChartTitle = React.useMemo(() => {
    switch (pvView) {
      case 'capacity':
        return 'Current Interconnected Solar Capacity by Approval Cohort';
      case 'cumulative':
        return 'Cumulative Current Capacity by Approval Cohort';
      case 'perCapita':
        return 'Solar Capacity Intensity (Current Snapshot)';
      case 'installers':
        return 'Installer Capacity — Top-10 Rollup (kW DC)';
      default:
        return 'Solar Installations';
    }
  }, [pvView]);

  const allowedHeatPumpOptions = useFilteredChartOptions('heat_pump', [
    { id: 'heat_pump_units', value: 'units', label: 'Units Installed' },
    { id: 'heat_pump_per_capita', value: 'perCapita', label: 'Per Capita' },
    { id: 'heat_pump_cost', value: 'cost', label: 'Installation Costs' },
    { id: 'heat_pump_technology', value: 'technology', label: 'Technology Mix' }
  ], heatPumpView, setHeatPumpView, isChartEnabled, getChartOrder);

  const allowedMarketRentOptions = useFilteredChartOptions('housing_rent_category', [
    { id: 'market_rent_line', value: 'line', label: 'Line' },
    { id: 'market_rent_stacked', value: 'stacked', label: 'Stacked' }
  ], marketRentChartView, setMarketRentChartView, isChartEnabled, getChartOrder);

  const availableCommercialModelOptions = useCommercialModelOptions(elecCom, 'electricity', commercialExtrapModel, setCommercialExtrapModel);
  const availableCommercialGasModelOptions = useCommercialModelOptions(gasCom, 'naturalGas', commercialGasExtrapModel, setCommercialGasExtrapModel);

  React.useEffect(() => {
    setHeatPumpChartData([]);
  }, [heatPumpView]);

  const getSectionStyle = (sectionId: string) => ({
    order: getSectionOrder(sectionId),
    display: isSectionEnabled(sectionId) ? 'block' : 'none'
  });

  const solarGroups = [
    {
      category: 'capacity',
      label: 'Capacity',
      options: publicSolarOptions.filter((opt) => ['capacity', 'cumulative', 'perCapita'].includes(opt.value))
    },
    {
      category: 'cost',
      label: 'Cost',
      options: publicSolarOptions.filter((opt) => ['batteryCost', 'cost'].includes(opt.value))
    },
    {
      category: 'installers',
      label: 'Installers',
      options: publicSolarOptions.filter((opt) => ['installers', 'installersCost'].includes(opt.value))
    }
  ].filter((group) => group.options.length > 0);

  // Function to calculate heat pump metrics
  const calculateHeatPumpMetrics = async (location: string, geofips: number) => {
    try {
      if (!geofips) return null;

      // Fetch heat pump data for the location
      const { data: locationData, error: locationError } = await fetchHeatPumps<HeatPumpRecord>({
        select: 'year, system, units_installed, project_cost_per_unit_usd, annual_ghg_savings_mtco2e',
        geofips
      });

      if (locationError) {
        console.error('Error fetching heat pump metrics:', locationError);
        return null;
      }

      if (!locationData || locationData.length === 0) {
        return null;
      }

      // Calculate metrics
      const unitByYearForTotals = unitsByYearFromRecords(locationData, (r) => r.units_installed ?? null);
      const totalUnits = Array.from(unitByYearForTotals.values()).reduce((sum, u) => sum + u, 0);
      const totalGHGSavings = locationData.reduce((sum, record) => sum + (record.annual_ghg_savings_mtco2e || 0), 0);
      
      // Get latest year data
      const latestYear = Math.max(...locationData.map(d => d.year));
      const latestYearData = locationData.filter(d => d.year === latestYear);
      
      const latestYearUnits = unitByYearForTotals.get(latestYear) ?? null;
      const latestYearGHGSavings = latestYearData.reduce((sum, record) => sum + (record.annual_ghg_savings_mtco2e || 0), 0);
      
      // Calculate average cost per unit
      const totalCost = locationData.reduce((sum, record) => {
        return sum + ((record.units_installed || 0) * (record.project_cost_per_unit_usd || 0));
      }, 0);
      const averageCostPerUnit = totalUnits > 0 ? totalCost / totalUnits : 0;
      
      // Get system breakdown
      const hvacData = locationData.filter(d => d.system === 'HVAC');
      const hpwhData = locationData.filter(d => d.system === 'HPWH');
      
      const hvacUnits = hvacData.reduce((sum, record) => sum + (record.units_installed || 0), 0);
      const hpwhUnits = hpwhData.reduce((sum, record) => sum + (record.units_installed || 0), 0);
      
      // Calculate year-over-year growth
      const years = Array.from(new Set(locationData.map(d => d.year))).sort();
      const firstYear = Math.min(...years);
      const lastYear = Math.max(...years);
      
      const firstYearData = locationData.filter(d => d.year === firstYear);
      const lastYearData = locationData.filter(d => d.year === lastYear);
      
      const firstYearUnits = firstYearData.reduce((sum, record) => sum + (record.units_installed || 0), 0);
      const lastYearUnits = lastYearData.reduce((sum, record) => sum + (record.units_installed || 0), 0);
      
      const unitByYear = unitsByYearFromRecords(locationData, (r) => r.units_installed ?? null);
      const latestCompleteYear = latestCompletePositiveYear(unitByYear);
      const priorCompleteYear =
        latestCompleteYear != null ? priorCompletePositiveYear(unitByYear, latestCompleteYear) : null;
      const latestCompleteUnits =
        latestCompleteYear != null ? unitByYear.get(latestCompleteYear) ?? null : null;
      const priorCompleteUnits =
        priorCompleteYear != null ? unitByYear.get(priorCompleteYear) ?? null : null;
      const yoyGrowth = yoyPercentChange(latestCompleteUnits, priorCompleteUnits);
      const cumulativeUnits = Array.from(unitByYear.values()).reduce((s, u) => s + u, 0);
      const firstYearWithData = years.length ? Math.min(...years) : null;

      return {
        totalUnits: cumulativeUnits,
        totalGHGSavings,
        latestYearUnits: latestCompleteUnits,
        latestCompleteYear,
        priorCompleteYear,
        latestYearGHGSavings,
        averageCostPerUnit,
        hvacUnits,
        hpwhUnits,
        yoyGrowth,
        firstYear: firstYearWithData ?? firstYear,
        lastYear: latestCompleteYear ?? lastYear,
        yearsObserved: years.length,
      };
    } catch (error) {
      console.error('Error calculating heat pump metrics:', error);
      return null;
    }
  };

  const formatRentCategoryLabel = (category: string): string => {
    const labelMap: Record<string, string> = {
      no_rent_paid: 'No Rent Paid',
      under_500: 'Under $500',
      '500_to_999': '$500-$999',
      '1000_to_1499': '$1,000-$1,499',
      '1500_and_over': '$1,500 and Over',
      '1500_to_1999': '$1,500-$1,999',
      '2000_to_2499': '$2,000-$2,499',
      '2500_to_2999': '$2,500-$2,999',
      '3000_and_over': '$3,000 and Over',
    };
    return labelMap[category] || category.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const calculateMarketRentNarrative = useCallback(async (location: string, geofips: number): Promise<{ narrativeLine: string; narrativeStacked: string; higherRentIncreased: boolean; lowerRentDeclined: boolean } | null> => {
    try {
      if (!geofips) return null;

      const { data, error: fetchError } = await supabaseClient
        .from('market_rent_percent')
        .select('year, rent_category, estimate')
        .eq('geofips', geofips)
        .order('year', { ascending: true });

      if (fetchError) {
        console.error('Error fetching market rent data for narrative:', fetchError);
        return null;
      }
      if (!data || data.length === 0) return null;

      const yearToCategoryTotals = new Map<number, Map<string, number>>();
      for (const row of data as Array<{ year: number; rent_category: string; estimate: number | null }>) {
        if (row?.estimate == null) continue;
        const raw = row?.rent_category;
        if (!raw || raw === 'other') continue;
        const category = String(raw).trim().toLowerCase().replace(/\s+/g, '_');
        const year = row.year;
        if (!yearToCategoryTotals.has(year)) yearToCategoryTotals.set(year, new Map());
        const catMap = yearToCategoryTotals.get(year)!;
        catMap.set(category, (catMap.get(category) || 0) + row.estimate);
      }

      const years = Array.from(yearToCategoryTotals.keys()).sort((a, b) => a - b);
      if (years.length === 0) return null;
      const baselineYear = years[0];
      const latestYear = years[years.length - 1];

      const latestTotals = yearToCategoryTotals.get(latestYear) || new Map<string, number>();
      let topCategory: string | null = null;
      let topValue = -Infinity;
      latestTotals.forEach((value, category) => {
        if (value > topValue) {
          topValue = value;
          topCategory = category;
        }
      });
      const topLabel = topCategory ? formatRentCategoryLabel(topCategory) : 'N/A';
      const topPhrase = topLabel.includes('$')
        ? `fell within the ${topLabel} range`
        : `fell in the ${topLabel} category`;

      const higherCategories = new Set([
        '1500_and_over',
        '1500_to_1999',
        '2000_to_2499',
        '2500_to_2999',
        '3000_and_over',
      ]);
      const lowerCategories = new Set(['under_500', '500_to_999']);

      const sumForYear = (year: number, cats: Set<string>) => {
        const m = yearToCategoryTotals.get(year);
        if (!m) return 0;
        let sum = 0;
        cats.forEach((c) => {
          sum += m.get(c) || 0;
        });
        return sum;
      };

      const higherBaseline = sumForYear(baselineYear, higherCategories);
      const higherLatest = sumForYear(latestYear, higherCategories);
      const lowerBaseline = sumForYear(baselineYear, lowerCategories);
      const lowerLatest = sumForYear(latestYear, lowerCategories);

      const higherDelta = higherLatest - higherBaseline;
      const lowerDelta = lowerLatest - lowerBaseline;

      const higherWord = higherDelta > 0 ? 'increase' : higherDelta < 0 ? 'decrease' : 'little change';
      const lowerWord = lowerDelta < 0 ? 'decline' : lowerDelta > 0 ? 'growth' : 'little change';

      const trendSentences: string[] = [];
      if (higherDelta > 0) {
        trendSentences.push(
          'This may reflect rising housing costs, stronger housing demand, new housing development, or inflation.'
        );
      }
      if (lowerDelta < 0) {
        trendSentences.push(
          'This may suggest that affordable rental units are becoming less available in the local housing market.'
        );
      }
      const trendsText = trendSentences.length ? ` ${trendSentences.join(' ')}` : '';
      const unitsPhrase = Number.isFinite(topValue) ? `, with approximately ${Math.round(topValue).toLocaleString()} housing units` : '';

      // Line: full key insights (unit count, most common price level, comparison, direct trend sentences)
      const narrativeLine = `In ${latestYear}, the largest share of rental units in ${location} ${topPhrase}${unitsPhrase}. This indicates the most common rental price level in the local housing market. Compared to ${baselineYear}, there has been an overall ${higherWord} in higher-rent categories and a ${lowerWord} in lower-rent categories.${trendsText}`;
      // Stacked: shorter key insights (range only, comparison; trend bullets shown separately in UI)
      const narrativeStacked = `In ${latestYear}, the largest share of rental units in ${location} ${topPhrase}. Compared to ${baselineYear}, there has been an overall ${higherWord} in higher-rent categories and a ${lowerWord} in lower-rent categories.`;

      const higherRentIncreased = higherDelta > 0;
      const lowerRentDeclined = lowerDelta < 0;

      return { narrativeLine, narrativeStacked, higherRentIncreased, lowerRentDeclined };
    } catch (err) {
      console.error('Error calculating market rent narrative:', err);
      return null;
    }
  }, []);

  const eia861PilotConfig =
    selectedGeofips != null ? resolveEia861PilotForGeofips(selectedGeofips) : null;
  const electricityUsesEia861Pilot = Boolean(elecRes?.eia861PilotActive && eia861PilotConfig);

  const allowedElectricityOptions = allowedElectricityBase;

  const burbankReconciledCanonical = useBurbankReconciledCanonical(
    currentLocation,
    electricityUsesEia861Pilot
  );
  const elecReconciledChartRef = useRef<ChartJS<'bar' | 'line'>>(null);
  const showBurbankReconciledElecChart =
    electricityUsesEia861Pilot && burbankElecTabUsesReconciledChart(elecView);
  const burbankReconciledElecView = burbankElecTabToRev2View(elecView);

  const formatElectricityDisplay = (kwh: number): string => formatGwhHeadline(kwh);

  const getEiaElectricitySectorMetrics = (
    series: any,
    sector: 'residential' | 'commercial'
  ) => {
    if (!series?.chartData || !series?.eia861PilotActive || !eia861PilotConfig) {
      return getSectorMetrics(series, 2005);
    }
    const years = (series.chartData.labels || []) as number[];
    const canonical = buildEia861CanonicalElectricitySeries(eia861PilotConfig, years);
    const baseline = eia861FirstTrendBaselineYear(canonical, sector);
    const baseYear = baseline?.year ?? 2005;
    const metrics = getSectorMetrics(series, baseYear);
    if (!baseline) {
      return {
        ...metrics,
        changePercentSince2005: null,
        changeAbsSince2005: null,
        baselineYear: null as number | null,
        baselineInvalid: true,
      };
    }
    const latest = metrics.latestValue;
    const valueBase = Math.round(baseline.kwh);
    const changeAbs = latest - valueBase;
    const changePct =
      valueBase > 0 ? Math.round(((latest - valueBase) / valueBase) * 1000) / 10 : null;
    return {
      ...metrics,
      value2005: valueBase,
      changeAbsSince2005: Math.round(changeAbs),
      changePercentSince2005: changePct,
      baselineYear: baseYear,
      baselineInvalid: false,
    };
  };

  const getEiaElectricityTotalMetrics = (resData: any, comData: any) => {
    if (!resData?.eia861PilotActive || !eia861PilotConfig) {
      return calculateTotalMetrics(resData, comData);
    }
    const years = (resData.chartData.labels || []) as number[];
    const canonical = buildEia861CanonicalElectricitySeries(eia861PilotConfig, years);
    const baseline = eia861FirstTrendBaselineYear(canonical, 'total');
    const metrics = calculateTotalMetrics(resData, comData);
    if (!baseline) {
      return { ...metrics, baselineYear: null, baselineInvalid: true };
    }
    const valueBase = Math.round(baseline.kwh);
    const changeAbs = metrics.latestValue - valueBase;
    const changePct =
      valueBase > 0 ? Math.round(((metrics.latestValue - valueBase) / valueBase) * 1000) / 10 : null;
    return {
      ...metrics,
      value2005: valueBase,
      changeAbsSince2005: Math.round(changeAbs),
      changePercentSince2005: changePct,
      baselineYear: baseline.year,
      baselineInvalid: false,
    };
  };

  const eia861SourceNote = (
    <p style={{ fontSize: 13, color: '#374151', margin: '0 0 12px 0' }} role="note">
      <strong>Source:</strong> EIA-861, BWP utility ID 2507 — Burbank Water and Power retail sales (blue/teal bars).
      <strong> Orange bars</strong> are published CAP values (Rincon 2024) at 2010, 2019, 2022, and 2024 for comparison
      only — CAP commercial is not the same as EIA nonresidential.
    </p>
  );

  // Convert therms to gigajoules (GJ) and terajoules (TJ) and format with thousands separators
  const formatThermsAsGJ = (therms: number): string => {
    if (!Number.isFinite(therms) || therms < 0) return '—';
    const gj = therms * 0.1055; // 1 therm ≈ 0.1055 GJ
    return gj.toLocaleString(undefined, { maximumFractionDigits: 1 });
  };

  const formatThermsAsTJ = (therms: number): string => {
    if (!Number.isFinite(therms) || therms < 0) return '—';
    const tj = therms * 0.1055 / 1000; // 1 TJ = 1000 GJ
    return tj.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  // Helper function to calculate metrics for Total (Stacked) - latest year and since 2005
  const calculateTotalMetrics = (resData: any, comData: any) => {
    if (!resData?.chartData || !comData?.chartData) return { latestValue: 0, latestYear: 0, yearsAvailable: 0, value2005: null as number | null, changePercentSince2005: null as number | null, changeAbsSince2005: null as number | null };
    
    const resLabels = (resData.chartData.labels || []) as number[];
    const comLabels = (comData.chartData.labels || []) as number[];
    const resDatasets = resData.chartData.datasets || [];
    const comDatasets = comData.chartData.datasets || [];
    
    const resYears = new Set(resLabels);
    const comYears = new Set(comLabels);
    const completeYears = Array.from(resYears).filter(year => comYears.has(year)).sort((a, b) => a - b);
    
    if (completeYears.length === 0) return { latestValue: 0, latestYear: 0, yearsAvailable: 0, value2005: null, changePercentSince2005: null, changeAbsSince2005: null };
    
    const getTotalForYear = (year: number): number => {
      const resIndex = resLabels.indexOf(year);
      const comIndex = comLabels.indexOf(year);
      if (resIndex === -1 || comIndex === -1) return 0;
      const resValue = resDatasets.reduce((sum: number, dataset: any) => sum + (dataset.data?.[resIndex] || 0), 0);
      const comValue = comDatasets.reduce((sum: number, dataset: any) => sum + (dataset.data?.[comIndex] || 0), 0);
      return resValue > 0 && comValue > 0 ? resValue + comValue : 0;
    };
    
    let latestCompleteYear = 0;
    let latestValue = 0;
    for (let i = completeYears.length - 1; i >= 0; i--) {
      const year = completeYears[i];
      const total = getTotalForYear(year);
      if (total > 0) {
        latestCompleteYear = year;
        latestValue = Math.round(total);
        break;
      }
    }
    
    const baselineYear = 2005;
    const value2005 = completeYears.includes(baselineYear) ? getTotalForYear(baselineYear) : null;
    const changePercentSince2005 = value2005 != null && value2005 > 0
      ? Math.round(((latestValue - value2005) / value2005) * 1000) / 10
      : null;
    const changeAbsSince2005 = value2005 != null ? latestValue - Math.round(value2005) : null;
    
    return {
      latestValue,
      latestYear: latestCompleteYear,
      yearsAvailable: completeYears.length,
      value2005: value2005 != null ? Math.round(value2005) : null,
      changePercentSince2005,
      changeAbsSince2005: changeAbsSince2005 != null ? Math.round(changeAbsSince2005) : null
    };
  };

  // Sector-level metrics (res or com) with baseline year for narrative
  const getSectorMetrics = (chartData: any, baselineYear: number = 2005) => {
    if (!chartData?.chartData) return { latestValue: 0, latestYear: 0, value2005: null as number | null, changePercentSince2005: null as number | null, changeAbsSince2005: null as number | null, yearMin: 0, yearMax: 0 };
    const labels = (chartData.chartData.labels || []) as number[];
    const datasets = chartData.chartData.datasets || [];
    const getValueForYear = (year: number): number => {
      const i = labels.indexOf(year);
      if (i === -1) return 0;
      return datasets.reduce((sum: number, d: any) => sum + (d.data?.[i] || 0), 0);
    };
    const yearsWithData = labels.filter((_, i) => getValueForYear(labels[i]) > 0);
    if (yearsWithData.length === 0) return { latestValue: 0, latestYear: 0, value2005: null, changePercentSince2005: null, changeAbsSince2005: null, yearMin: 0, yearMax: 0 };
    const yearMin = Math.min(...yearsWithData);
    const yearMax = Math.max(...yearsWithData);
    let latestYear = 0;
    let latestValue = 0;
    for (let i = labels.length - 1; i >= 0; i--) {
      const v = getValueForYear(labels[i]);
      if (v > 0) {
        latestYear = labels[i];
        latestValue = Math.round(v);
        break;
      }
    }
    const value2005 = labels.includes(baselineYear) ? Math.round(getValueForYear(baselineYear)) : null;
    const changePercentSince2005 = value2005 != null && value2005 > 0 ? Math.round(((latestValue - value2005) / value2005) * 1000) / 10 : null;
    const changeAbsSince2005 = value2005 != null ? latestValue - value2005 : null;
    return {
      latestValue,
      latestYear,
      value2005,
      changePercentSince2005,
      changeAbsSince2005: changeAbsSince2005 != null ? Math.round(changeAbsSince2005) : null,
      yearMin,
      yearMax
    };
  };

  // Electricity narrative variables for Overview body (total, residential, or commercial view)
  const getElectricityNarrativeVars = (): {
    location: string;
    totalGwh: string;
    latestYear: number;
    increaseDecrease: string;
    pctChange: string;
    baselineYear: number;
    changeGwh: string;
    sectorLabel: string;
    trend: string;
    trendPeriodPhrase: string;
    period: string;
    trendDirectionSentence: string;
    dataPeriod: string;
    utilityName: string;
    value2005: number | null;
    changePercentSince2005: number | null;
    residentialShare?: number | null;
    commercialShare?: number | null;
  } | null => {
    const baselineYear = 2005;
    const location = currentLocation;
    const utilityName = (elecMethodology?.dataSources && elecMethodology.dataSources.length > 0)
      ? elecMethodology.dataSources[0]
      : 'PG&E (e.g.)';

    if (elecView === 'total' && elecRes?.chartData && elecCom?.chartData) {
      const m = getEiaElectricityTotalMetrics(elecRes, elecCom);
      if (m.latestValue <= 0) return null;
      const displayYear = m.latestYear > 0 ? m.latestYear : new Date().getFullYear();
      const narrativeBaseline = (m as { baselineYear?: number | null }).baselineYear ?? baselineYear;
      const pair = latestPositiveYearPair(elecRes.chartData, elecCom.chartData);
      const residentialShare = pair ? sectorSharePercent(pair.residential, pair.total) : null;
      const commercialShare = pair ? sectorSharePercent(pair.commercial, pair.total) : null;
      const pct = m.changePercentSince2005 ?? 0;
      const absChange = m.changeAbsSince2005 ?? 0;
      const trend = pct > 5 ? 'generally upward' : pct < -5 ? 'generally downward' : 'relatively stable';
      const trendPeriodPhrase = pct > 5 ? 'periods of increase' : pct < -5 ? 'periods of decline' : 'periods of leveling';
      const yearRange = m.yearsAvailable ? `${narrativeBaseline}–${displayYear}` : `${displayYear}`;
      return {
        location,
        totalGwh: formatElectricityDisplay(m.latestValue),
        latestYear: displayYear,
        increaseDecrease: pct >= 0 ? 'increase' : 'decrease',
        pctChange: Math.abs(pct).toFixed(1),
        baselineYear: narrativeBaseline,
        changeGwh: formatElectricityDisplay(Math.abs(absChange)),
        sectorLabel: 'total',
        trend,
        trendPeriodPhrase,
        period: yearRange,
        trendDirectionSentence: pct < 0
          ? 'Where consumption is declining, this may be consistent with factors such as improved building efficiency, reduced electricity intensity, or changes in occupancy/activity patterns.'
          : 'Where consumption is rising, this may be consistent with increased demands, expanded building stock, greater cooling loads, or electrification of end uses.',
        dataPeriod: `${narrativeBaseline}–${displayYear}`,
        utilityName: electricityUsesEia861Pilot ? 'Burbank Water and Power (EIA-861)' : utilityName,
        value2005: m.value2005,
        changePercentSince2005: m.changePercentSince2005,
        residentialShare,
        commercialShare,
      };
    }

    if (elecView === 'res' && elecRes?.chartData) {
      const m = getEiaElectricitySectorMetrics(elecRes, 'residential');
      if (m.latestValue <= 0) return null;
      const pct = m.changePercentSince2005 ?? 0;
      const absChange = m.changeAbsSince2005 ?? 0;
      const trend = pct > 5 ? 'generally upward' : pct < -5 ? 'generally downward' : 'relatively stable';
      const trendPeriodPhrase = pct > 5 ? 'periods of increase' : pct < -5 ? 'periods of decline' : 'periods of leveling';
      const period = m.yearMin && m.yearMax ? `${m.yearMin}–${m.yearMax}` : `${m.latestYear}`;
      const narrativeBaseline = (m as { baselineYear?: number | null }).baselineYear ?? baselineYear;
      return {
        location,
        totalGwh: formatElectricityDisplay(m.latestValue),
        latestYear: m.latestYear,
        increaseDecrease: pct >= 0 ? 'increase' : 'decrease',
        pctChange: Math.abs(pct).toFixed(1),
        baselineYear: narrativeBaseline,
        changeGwh: formatElectricityDisplay(Math.abs(absChange)),
        sectorLabel: 'residential',
        trend,
        trendPeriodPhrase,
        period,
        trendDirectionSentence: pct < 0
          ? 'Where consumption is declining, this may be consistent with factors such as improved building efficiency, reduced electricity intensity, or changes in occupancy/activity patterns.'
          : 'Where consumption is rising, this may be consistent with increased demands, expanded building stock, greater cooling loads, or electrification of end uses.',
        dataPeriod: m.yearMin && m.yearMax ? `${m.yearMin}–${m.yearMax}` : `${m.latestYear}`,
        utilityName: electricityUsesEia861Pilot ? 'Burbank Water and Power (EIA-861)' : utilityName,
        value2005: m.value2005,
        changePercentSince2005: m.changePercentSince2005
      };
    }

    if (elecView === 'com' && elecCom?.chartData) {
      const m = getEiaElectricitySectorMetrics(elecCom, 'commercial');
      if (m.latestValue <= 0) return null;
      const pct = m.changePercentSince2005 ?? 0;
      const absChange = m.changeAbsSince2005 ?? 0;
      const trend = pct > 5 ? 'generally upward' : pct < -5 ? 'generally downward' : 'relatively stable';
      const trendPeriodPhrase = pct > 5 ? 'periods of increase' : pct < -5 ? 'periods of decline' : 'periods of leveling';
      const period = m.yearMin && m.yearMax ? `${m.yearMin}–${m.yearMax}` : `${m.latestYear}`;
      const narrativeBaseline = (m as { baselineYear?: number | null }).baselineYear ?? baselineYear;
      return {
        location,
        totalGwh: formatElectricityDisplay(m.latestValue),
        latestYear: m.latestYear,
        increaseDecrease: pct >= 0 ? 'increase' : 'decrease',
        pctChange: Math.abs(pct).toFixed(1),
        baselineYear: narrativeBaseline,
        changeGwh: formatElectricityDisplay(Math.abs(absChange)),
        sectorLabel: 'commercial',
        trend,
        trendPeriodPhrase,
        period,
        trendDirectionSentence: pct < 0
          ? 'Where consumption is declining, this may be consistent with factors such as improved building efficiency, reduced electricity intensity, or changes in occupancy/activity patterns.'
          : 'Where consumption is rising, this may be consistent with increased demands, expanded building stock, greater cooling loads, or electrification of end uses.',
        dataPeriod: m.yearMin && m.yearMax ? `${m.yearMin}–${m.yearMax}` : `${m.latestYear}`,
        utilityName: electricityUsesEia861Pilot ? 'Burbank Water and Power (EIA-861)' : utilityName,
        value2005: m.value2005,
        changePercentSince2005: m.changePercentSince2005
      };
    }

    return null;
  };

  const buildElectricityNarrativeInput = (): import('../../domains/narrative/electricityConsumptionNarrative').ElectricityConsumptionNarrativeInput | null => {
    const viewId = elecView as ElectricityViewId;
    const commercialLabel = elecCom?.eia861NonresLabel ?? 'Reported commercial';
    const resModelLabel =
      RESIDENTIAL_MODEL_OPTIONS.find((o) => o.value === residentialExtrapModel)?.label ?? null;
    const comModelLabel =
      COMMERCIAL_MODEL_OPTIONS.find((o) => o.value === commercialExtrapModel)?.label ??
      formatCommercialModelLabelWithSmape(
        commercialExtrapModel,
        'electricity',
        elecCom?.commercialModelSmape as number | undefined
      );

    const toSector = (m: ReturnType<typeof getEiaElectricitySectorMetrics>) => ({
      latestValueKwh: m.latestValue,
      latestYear: m.latestYear,
      baselineYear: (m as { baselineYear?: number | null }).baselineYear ?? 2005,
      valueBaselineKwh: m.value2005,
      changePercentSinceBaseline: m.changePercentSince2005,
    });

    let perCapita: import('../../domains/narrative/electricityConsumptionNarrative').ElectricityPerCapitaPair | null =
      null;
    let perCapitaBaseline: { year: number; local: number } | null = null;
    if (elecPerCapitaData?.chartData) {
      const locLabels = (elecPerCapitaData.chartData.labels || []) as number[];
      const locData = (elecPerCapitaData.chartData.datasets?.[0]?.data || []) as number[];
      const latestLocal = latestPositiveYearFromSeries(locLabels, locData);
      if (latestLocal) {
        let californiaKwhPerPerson: number | null = null;
        if (californiaElecPerCapita?.chartData) {
          const caLabels = (californiaElecPerCapita.chartData.labels || []) as number[];
          const caData = (californiaElecPerCapita.chartData.datasets?.[0]?.data || []) as number[];
          const pair = latestCommonPositiveYear(locLabels, locData, caLabels, caData);
          if (pair) {
            perCapita = {
              year: pair.year,
              localKwhPerPerson: pair.local,
              californiaKwhPerPerson: pair.compare,
            };
          } else {
            const caIdx = caLabels.findIndex((y) => Number(y) === latestLocal.year);
            const caVal = caIdx >= 0 ? caData[caIdx] : undefined;
            californiaKwhPerPerson =
              caVal != null && Number.isFinite(caVal) && caVal > 0 ? caVal : null;
          }
        }
        if (!perCapita) {
          perCapita = {
            year: latestLocal.year,
            localKwhPerPerson: latestLocal.value,
            californiaKwhPerPerson,
          };
        }
      }
      const bIdx = locLabels.findIndex((y) => Number(y) === 2005);
      if (bIdx >= 0 && Number.isFinite(locData[bIdx]) && locData[bIdx] > 0) {
        perCapitaBaseline = { year: 2005, local: locData[bIdx] };
      }
    }

    const pair =
      elecRes?.chartData && elecCom?.chartData
        ? latestPositiveYearPair(elecRes.chartData, elecCom.chartData)
        : null;

    const v = getElectricityNarrativeVars();
    return {
      locationName: currentLocation,
      viewId,
      total: elecRes && elecCom
        ? (() => {
            const m = getEiaElectricityTotalMetrics(elecRes, elecCom);
            return {
              latestValueKwh: m.latestValue,
              latestYear: m.latestYear,
              baselineYear: (m as { baselineYear?: number | null }).baselineYear ?? 2005,
              valueBaselineKwh: m.value2005,
              changePercentSinceBaseline: m.changePercentSince2005,
            };
          })()
        : undefined,
      residential: elecRes ? toSector(getEiaElectricitySectorMetrics(elecRes, 'residential')) : undefined,
      commercial: elecCom ? toSector(getEiaElectricitySectorMetrics(elecCom, 'commercial')) : undefined,
      compositionYear: pair?.year,
      residentialKwh: pair?.residential ?? null,
      commercialKwh: pair?.commercial ?? null,
      perCapita,
      perCapitaBaseline,
      formatGwh: formatElectricityDisplay,
      commercialLabel,
      residentialExtrapModelLabel: electricityUsesEia861Pilot ? null : resModelLabel,
      commercialExtrapModelLabel: electricityUsesEia861Pilot ? null : comModelLabel,
      usesEia861Pilot: electricityUsesEia861Pilot,
      utilityName:
        (elecMethodology?.dataSources && elecMethodology.dataSources.length > 0)
          ? elecMethodology.dataSources[0]
          : undefined,
      dataPeriod: v?.dataPeriod,
      hasEstimatedYears: chartSeriesHasEstimatedYears(elecRes, elecCom),
    };
  };

  const renderPerCapitaVsCaliforniaMetric = (
    localChart: { chartData?: { labels?: number[]; datasets?: { data?: number[] }[] }; latestValue?: number | null; latestYear?: number },
    caChart: { chartData?: { labels?: number[]; datasets?: { data?: number[] }[] }; latestValue?: number | null; latestYear?: number },
    unitSuffix: string
  ) => {
    const locLabels = (localChart.chartData?.labels || []) as number[];
    const locData = (localChart.chartData?.datasets?.[0]?.data || []) as number[];
    const caLabels = (caChart.chartData?.labels || []) as number[];
    const caData = (caChart.chartData?.datasets?.[0]?.data || []) as number[];
    const pair = latestCommonPositiveYear(locLabels, locData, caLabels, caData);
    if (!pair) {
      return <KeyMetric value="—" label="Versus California (common year)" />;
    }
    const { differencePercent, comparison } = compareLocalToCalifornia(pair.local, pair.compare);
    const value =
      differencePercent != null
        ? `${formatSignedPercentChange(differencePercent)} ${comparison} CA`
        : '—';
    return (
      <KeyMetric
        value={value}
        label={`Versus California (${pair.year}), ${unitSuffix}`}
      />
    );
  };

  // Natural gas narrative variables for Overview and key metrics (total view)
  const getNaturalGasNarrativeVars = () => {
    if (!gasRes?.chartData || !gasCom?.chartData) return null;

    const baselineYear = 2005;
    const metrics = calculateTotalMetrics(gasRes, gasCom);
    if (metrics.latestValue <= 0) return null;

    const latestYear = metrics.latestYear > 0 ? metrics.latestYear : new Date().getFullYear();
    const baselineValue = metrics.value2005;
    const changePercent =
      metrics.changePercentSince2005 != null ? metrics.changePercentSince2005 : null;
    const changeAbs =
      metrics.changeAbsSince2005 != null ? metrics.changeAbsSince2005 : null;

    let trend: 'upward' | 'downward' | 'stable' = 'stable';
    if (changePercent != null) {
      if (changePercent > 5) trend = 'upward';
      else if (changePercent < -5) trend = 'downward';
      else trend = 'stable';
    }

    return {
      latestYear,
      latestValue: metrics.latestValue,
      baselineYear,
      baselineValue,
      changePercentSinceBaseline: changePercent,
      changeAbsSinceBaseline: changeAbs,
      trend,
    };
  };

  const buildNaturalGasNarrativeInput = () =>
    buildNaturalGasNarrativeInputFromCharts({
      locationName: currentLocation,
      viewId: gasView,
      baselineYear: 2005,
      gasRes,
      gasCom,
      gasPerCapitaData,
      californiaGasPerCapita,
      hasEstimatedYears: chartSeriesHasEstimatedYears(gasRes, gasCom),
      activeResidentialModel: residentialExtrapModel,
      activeCommercialModel: commercialGasExtrapModel,
      gasSourceRow: gasProvenanceRow,
    });

  // Carbon intensity narrative variables for Overview and key metrics
  const getCarbonIntensityNarrativeVars = () => {
    if (!carbonIntensityData?.chartData?.labels?.length) return null;

    const labels = carbonIntensityData.chartData.labels as number[];
    const datasets = carbonIntensityData.chartData.datasets || [];
    const data = (datasets[0]?.data as number[]) || [];

    if (!labels.length || !data.length) return null;

    const latestYear = carbonIntensityData.latestYear || Math.max(...labels);
    const latestIndex = labels.indexOf(latestYear);
    const latestValue =
      latestIndex >= 0 && data[latestIndex] != null
        ? Number(data[latestIndex])
        : carbonIntensityData.latestValue ?? null;

    if (latestValue == null) return null;

    const preferredBaselineYear = 2005;
    const baselineYear = labels.includes(preferredBaselineYear)
      ? preferredBaselineYear
      : Math.min(...labels);
    const baselineIndex = labels.indexOf(baselineYear);
    const baselineValue =
      baselineIndex >= 0 && data[baselineIndex] != null
        ? Number(data[baselineIndex])
        : null;

    const changePercentSinceBaseline =
      baselineValue != null && baselineValue > 0
        ? ((latestValue - baselineValue) / baselineValue) * 100
        : null;

    const changeAbsSinceBaseline =
      baselineValue != null ? latestValue - baselineValue : null;

    let trend: 'downward' | 'stable' | 'mixed' = 'mixed';
    if (changePercentSinceBaseline != null) {
      if (changePercentSinceBaseline <= -5) {
        trend = 'downward';
      } else if (Math.abs(changePercentSinceBaseline) < 5) {
        trend = 'stable';
      } else {
        trend = 'mixed';
      }
    }

    return {
      latestYear,
      latestValue,
      baselineYear,
      baselineValue,
      changePercentSinceBaseline,
      changeAbsSinceBaseline,
      trend,
    };
  };

  // Function to fetch methodology data from database
  const fetchMethodologyData = async (location: string, energyType: 'electricity' | 'naturalGas') => {
    try {
      // Get geofips for the location
      const { data: locationData, error: locationError } = await supabaseClient
        .from('population')
        .select('geofips, location_name, geography_type')
        .eq('location_name', location)
        .limit(1)
        .single();

      if (locationError || !locationData) {
        console.error('Error fetching location geofips for methodology:', locationError);
        return null;
      }

      const geofipsVariants = geofipsVariantsForEnergyFormat(locationData.geofips);
      
      // Determine table names
      const resTable = energyType === 'electricity' ? 'elec_res_format' : 'ng_res_format';
      const comTable = energyType === 'electricity' ? 'elec_com_format' : 'ng_com_format';
      
      // Fetch data from both tables
      const [resResponse, comResponse] = await Promise.all([
        supabaseClient
          .from(resTable)
          .select('data_source, method_note, measurement')
          .in('geofips', geofipsVariants)
          .eq('sector', 'Residential'),
        supabaseClient
          .from(comTable)
          .select('data_source, method_note, measurement')
          .in('geofips', geofipsVariants)
          .eq('sector', 'Commercial')
      ]);

      if (resResponse.error || comResponse.error) {
        console.error('Error fetching methodology data:', resResponse.error || comResponse.error);
        return null;
      }

      const resData = resResponse.data || [];
      const comData = comResponse.data || [];
      const allData = [...resData, ...comData];

      // Extract unique data sources
      const dataSources = new Set<string>();
      allData.forEach((row: any) => {
        if (row.data_source) {
          const src = String(row.data_source).trim();
          if (src) {
            // Format utility names
            const srcUpper = src.toUpperCase();
            if (srcUpper.includes('PGE') || srcUpper.includes('PG&E')) {
              dataSources.add('PG&E (Pacific Gas & Electric)');
            } else if (srcUpper.includes('SOCALGAS') || srcUpper.includes('SO CAL GAS')) {
              dataSources.add('SoCalGas (Southern California Gas Company)');
            } else if (srcUpper.includes('SCE')) {
              dataSources.add('SCE (Southern California Edison)');
            } else if (srcUpper.includes('SDGE') || srcUpper.includes('SDG&E')) {
              dataSources.add('SDGE (San Diego Gas & Electric)');
            } else if (srcUpper.includes('EGRID') || srcUpper.includes('E-GRID')) {
              dataSources.add('eGRID (EPA Emissions & Generation Resource Integrated Database)');
            } else if (srcUpper.includes('NREL')) {
              dataSources.add('NREL (National Renewable Energy Laboratory)');
            } else if (srcUpper.includes('CEC')) {
              dataSources.add('CEC (California Energy Commission)');
            } else {
              dataSources.add(src);
            }
          }
        }
      });

      // Analyze method notes to determine methods used
      const methods: string[] = [];
      const hasInterpolation = allData.some((row: any) => 
        row.method_note && String(row.method_note).toLowerCase().includes('interpol')
      );
      const hasCountyRatioExtrapolation = allData.some((row: any) => 
        row.method_note && String(row.method_note).toLowerCase().includes('county ratio')
      );
      const hasFixedExtrapolation = allData.some((row: any) => 
        row.method_note && (
          String(row.method_note).toLowerCase().includes('3% efficiency') ||
          String(row.method_note).toLowerCase().includes('elasticity')
        )
      );
      const hasBackwardExtrapolation = allData.some((row: any) => 
        row.method_note && String(row.method_note).toLowerCase().includes('backward')
      );
      const hasObserved = allData.some((row: any) => 
        row.measurement && String(row.measurement).toLowerCase() === 'observed'
      );

      // Build methodology description
      methods.push("Data provenance indicated by measurement column (observed, interpolated, extrapolated)");
      
      if (hasObserved) {
        methods.push("Observed data comes directly from utility providers");
      }
      
      if (hasInterpolation) {
        methods.push("Linear interpolation used for missing values between observed data points");
      }
      
      if (hasCountyRatioExtrapolation) {
        methods.push("Extrapolation uses county-level user_average ratios to scale values forward or backward in time");
        if (hasBackwardExtrapolation) {
          methods.push("Backward extrapolation applied to historical years using county-level trends");
        }
      }
      
      if (hasFixedExtrapolation) {
        methods.push("Extrapolation uses fixed methodology: 3% annual efficiency improvement and 0.2 worker/population elasticity");
      }
      
      methods.push("Stacked bars sum residential and commercial by year");
      methods.push("Colors indicate data source: observed (dark), interpolated (medium), extrapolated (light)");

      return {
        dataSources: Array.from(dataSources).sort(),
        methods
      };
    } catch (error) {
      console.error('Error in fetchMethodologyData:', error);
      return null;
    }
  };

  // Total/res/com per capita from displayed consumption ÷ annual population.
  // Residential-only is flat when extrapolation scales kWh by population (kWh/pop ≈ constant).
  const calculatePerCapitaData = useCallback(async (resData: any, comData: any, type: string) => {
    if (!resData?.chartData) return null;

    try {
      const { data: locationRows, error: locationError } = await supabaseClient
        .from('population')
        .select('geofips, location_name, geography_type')
        .eq('location_name', currentLocation)
        .order('geography_type', { ascending: true });

      if (locationError || !locationRows?.length) {
        console.error('Error fetching location geofips:', locationError);
        return null;
      }

      const cityRow = locationRows.find((row: { geography_type?: string }) => row.geography_type === 'City');
      const countyRow = locationRows.find((row: { geography_type?: string }) => row.geography_type === 'County');
      const locationData = cityRow || countyRow || locationRows[0];

      const rawGeofips = String(locationData.geofips).replace(/^0+/, '');
      const geographyType = locationData.geography_type;
      const allYears = resData.chartData.labels || [];

      const { data: popData, error: popError } = await supabaseClient
        .from('population')
        .select('year, population')
        .eq('geofips', rawGeofips)
        .eq('geography_type', geographyType)
        .in('year', allYears)
        .order('year', { ascending: true });

      if (popError) {
        console.error('Error fetching population data:', popError);
        return null;
      }

      const popMap = new Map<number, number>();
      (popData || []).forEach((row: any) => {
        const pop = parseFloat(row.population);
        if (pop && pop > 0) {
          popMap.set(Number(row.year), pop);
        }
      });

      const resLabels = (resData.chartData.labels || []) as number[];
      const resMap = sumDatasetsByYear(resLabels, resData.chartData.datasets || []);
      const comLabels = (comData?.chartData?.labels || []) as number[];
      const comMap = sumDatasetsByYear(comLabels, comData?.chartData?.datasets || []);

      const unitScale = type === 'Electricity' ? 1 : THERMS_TO_GJ;
      const unit = type === 'Electricity' ? 'kWh/person' : 'GJ/person';
      const yTitle = type === 'Electricity' ? 'kWh per person' : 'GJ per person';

      const locationPerCapita = resLabels
        .map((year) => {
          const y = Number(year);
          const population = popMap.get(y) || 0;
          const consumption = (resMap.get(y) || 0) + (comMap.get(y) || 0);
          return {
            year: y,
            value: population > 0 ? (consumption / population) * unitScale : 0,
          };
        })
        .filter((item) => item.value > 0)
        .sort((a, b) => a.year - b.year);

      if (locationPerCapita.length === 0) return null;

      return {
        chartData: {
          labels: locationPerCapita.map((item) => item.year),
          datasets: [{
            label: `${currentLocation} (Total)`,
            data: locationPerCapita.map((item) => item.value),
            backgroundColor: type === 'Electricity' ? '#1e40af' : '#166534',
            borderColor: type === 'Electricity' ? '#1e40af' : '#166534',
            borderWidth: 2,
            type: 'line',
          }],
        },
        chartOptions: buildChartOptions({
          type: 'line',
          title: `Total ${type} Consumption Per Capita - ${currentLocation}`,
          tooltipFormat: (v) => `${v.toFixed(2)} ${unit}`,
          yTitle,
          yTickFormat: (v: any) => Number(v).toFixed(1),
        }),
        latestYear: locationPerCapita[locationPerCapita.length - 1]?.year,
        latestValue: locationPerCapita[locationPerCapita.length - 1]?.value || 0,
      };
    } catch (error) {
      console.error(`Error calculating ${type} per capita data:`, error);
      return null;
    }
  }, [currentLocation]);

  // Function to fetch stacked data from new format tables with extrapolation
  const _fetchStackedDataFromNewTables = async (location: string, type: 'electricity' | 'naturalGas') => {
    try {
      // Get geofips for the location - must match the selected location's geography type
      // Use the location context to get the correct geography type
      const { data: locationData, error: locationError } = await supabaseClient
        .from('population')
        .select('geofips, location_name, geography_type')
        .eq('location_name', location)
        .limit(1)
        .single();

      if (locationError || !locationData) {
        console.error('Error fetching location geofips:', locationError);
        return null;
      }

      // Get raw geofips (new format: state="6", county="6001", city="600296" - no leading zeros)
      const rawGeofips = String(locationData.geofips).replace(/^0+/, ''); // Remove leading zeros
      const geographyType = locationData.geography_type; // Store the geography type
      
      // All subsequent queries must use this same geography_type

      // Determine table names and value column
      const resTable = type === 'electricity' ? 'elec_res_format' : 'ng_res_format';
      const comTable = type === 'electricity' ? 'elec_com_format' : 'ng_com_format';
      const valueColumn = type === 'electricity' ? 'total_kwh' : 'total_therms';
      const yLabel = type === 'electricity' ? 'kWh' : 'GJ';

      // Define all years we want to display (2005-2023)
      const allYears = Array.from({ length: 2023 - 2005 + 1 }, (_, i) => 2005 + i);

      // Use ONLY the correct geofips format (no leading zeros)
      // State: "6", County: "6001", City: "600296"
      // Old duplicate rows with 7-digit geofips have been deleted
      const geofipsFilter = `geofips.eq.${rawGeofips}`;
      
      // Fetch residential and commercial data with user_average, denominator columns, and measurement type
      // Try all geofips variants - include method_note to help identify extrapolated values
      const [resResponse, comResponse] = await Promise.all([
        supabaseClient
          .from(resTable)
          .select(`year, ${valueColumn}, user_average, population, measurement, method_note`)
          .or(geofipsFilter)
          .eq('sector', 'Residential')
          .order('year', { ascending: true }),
        supabaseClient
          .from(comTable)
          .select(`year, ${valueColumn}, user_average, total_worker, measurement, method_note`)
          .or(geofipsFilter)
          .eq('sector', 'Commercial')
          .order('year', { ascending: true })
      ]);

      if (resResponse.error || comResponse.error) {
        console.error('Error fetching data from new tables:', resResponse.error || comResponse.error);
        return null;
      }

      const resData = resResponse.data || [];
      const comData = comResponse.data || [];
      
      // Fetch population data for all years (for residential extrapolation)
      // Use the same geofips and geography_type to ensure we get the correct geography type
      // Population table uses string geofips (no leading zeros)
      const { data: popData, error: popError } = await supabaseClient
        .from('population')
        .select('year, population')
        .eq('geofips', rawGeofips) // Use raw geofips (no leading zeros)
        .eq('geography_type', geographyType) // Must match the selected location's geography type
        .in('year', allYears)
        .order('year', { ascending: true });

      if (popError) {
        // Population data fetch failed - non-critical, continue with available data
      }

      // Fetch worker data for all years (for commercial extrapolation)
      // LODES uses 6-digit geofips for cities, 4-digit for counties, "6" for state
      const workerGeofips = geographyType === 'State' ? '6' : 
                            geographyType === 'County' ? rawGeofips.slice(-4) : 
                            rawGeofips.slice(-6); // City: last 6 digits
      const { data: workerData, error: workerError } = await supabaseClient
        .from('lodes_employees_workplace')
        .select('year, total_worker')
        .eq('geofips', workerGeofips)
        .eq('geography_type', geographyType === 'State' ? 'state' : geographyType === 'County' ? 'county' : 'place')
        .in('year', allYears)
        .order('year', { ascending: true });

      if (workerError) {
        // Worker data fetch failed - non-critical, continue with available data
      }

      // Create population map
      const popMap = new Map<number, number>();
      (popData || []).forEach((row: any) => {
        const pop = parseFloat(row.population);
        if (pop && pop > 0) {
          popMap.set(Number(row.year), pop);
        }
      });

      // Create worker map - try both 6-digit and 7-digit geofips
      const workerMap = new Map<number, number>();
      (workerData || []).forEach((row: any) => {
        const workers = parseFloat(row.total_worker);
        if (workers && workers > 0) {
          workerMap.set(row.year, workers);
        }
      });
      
      // If no worker data found, try with alternative geofips format
      if (workerMap.size === 0 && geographyType === 'City') {
        // Try 6-digit geofips for cities
        const altWorkerGeofips = rawGeofips.slice(-6);
        const { data: workerData6, error: workerError6 } = await supabaseClient
          .from('lodes_employees_workplace')
          .select('year, total_worker')
          .eq('geofips', altWorkerGeofips)
          .eq('geography_type', 'place')
          .in('year', allYears)
          .order('year', { ascending: true });
        
        if (!workerError6 && workerData6) {
          workerData6.forEach((row: any) => {
            const workers = parseFloat(row.total_worker);
            if (workers && workers > 0) {
              workerMap.set(row.year, workers);
            }
          });
        }
      }

      // Color scheme matching the original charts
      const resColors = type === 'electricity' 
        ? { observed: '#1e40af', interpolated: '#3b82f6', extrapolated: '#60a5fa' }
        : { observed: '#166534', interpolated: '#22c55e', extrapolated: '#4ade80' };
      const comColors = type === 'electricity'
        ? { observed: '#166534', interpolated: '#22c55e', extrapolated: '#4ade80' }
        : { observed: '#1e40af', interpolated: '#3b82f6', extrapolated: '#60a5fa' };

      // Process residential data - use ONLY database values, no filtering or preference logic
      // Database should have all years (2005-2023) with proper measurement types
      const resDataMap = new Map<number, { value: number; measurement: string }>();
      resData.forEach((row: any) => {
        const year = row.year;
        const value = parseFloat(row[valueColumn]);
        // Use any valid value from database (database has all years populated)
        if (!isNaN(value) && value >= 0) {
          const measurement = classifyEnergyRowProvenance(
            row.measurement,
            row.method_note,
            row.extrapolation_model
          );
          // If year already exists, keep first one (database should have one row per year)
          if (!resDataMap.has(year)) {
            resDataMap.set(year, { value, measurement });
          }
        }
      });

      // Process commercial data - use ONLY database values, no filtering or preference logic
      // Database should have all years (2005-2023) with proper measurement types
      const comDataMap = new Map<number, { value: number; measurement: string }>();
      comData.forEach((row: any) => {
        const year = row.year;
        const value = parseFloat(row[valueColumn]);
        // Use any valid value from database (database has all years populated)
        if (!isNaN(value) && value >= 0) {
          const measurement = classifyEnergyRowProvenance(
            row.measurement,
            row.method_note,
            row.extrapolation_model
          );
          // If year already exists, keep first one (database should have one row per year)
          if (!comDataMap.has(year)) {
            comDataMap.set(year, { value, measurement });
          }
        }
      });

      // Build arrays for all years, separated by measurement type
      const resObserved: number[] = [];
      const resInterpolated: number[] = [];
      const resExtrapolated: number[] = [];
      const comObserved: number[] = [];
      const comInterpolated: number[] = [];
      const comExtrapolated: number[] = [];

      allYears.forEach(year => {
        // Residential - use ONLY database values (no client-side recalculation)
        const resData = resDataMap.get(year);
        if (resData) {
          // Use database value directly, categorize by measurement type from database
          const measurement = resData.measurement;
          if (measurement === 'observed') {
            resObserved.push(resData.value);
            resInterpolated.push(0);
            resExtrapolated.push(0);
          } else if (measurement === 'interpolated') {
            resObserved.push(0);
            resInterpolated.push(resData.value);
            resExtrapolated.push(0);
          } else if (measurement === 'extrapolated') {
            resObserved.push(0);
            resInterpolated.push(0);
            resExtrapolated.push(resData.value);
          } else {
            resObserved.push(resData.value);
            resInterpolated.push(0);
            resExtrapolated.push(0);
          }
        } else {
          // No data in database for this year - show as zero
          resObserved.push(0);
          resInterpolated.push(0);
          resExtrapolated.push(0);
        }

        // Commercial - use ONLY database values (no client-side recalculation)
        const comData = comDataMap.get(year);
        if (comData) {
          // Use database value directly, categorize by measurement type from database
          const measurement = comData.measurement;
          if (measurement === 'observed') {
            comObserved.push(comData.value);
            comInterpolated.push(0);
            comExtrapolated.push(0);
          } else if (measurement === 'interpolated') {
            comObserved.push(0);
            comInterpolated.push(comData.value);
            comExtrapolated.push(0);
          } else if (measurement === 'extrapolated') {
            comObserved.push(0);
            comInterpolated.push(0);
            comExtrapolated.push(comData.value);
          } else {
            comObserved.push(comData.value);
            comInterpolated.push(0);
            comExtrapolated.push(0);
          }
        } else {
          // No data in database for this year - show as zero
          comObserved.push(0);
          comInterpolated.push(0);
          comExtrapolated.push(0);
        }
      });

      // Create stacked chart data with separate datasets for each measurement type
      // All datasets use the same stack ID so residential and commercial stack together
      const stackedChartData = {
        labels: allYears,
        datasets: [
          {
            label: 'Residential - Observed',
            data: resObserved,
            backgroundColor: resColors.observed,
            borderColor: '#374151',
            borderWidth: 1,
            stack: 'total'
          },
          {
            label: 'Residential - Interpolated',
            data: resInterpolated,
            backgroundColor: resColors.interpolated,
            borderColor: '#374151',
            borderWidth: 1,
            stack: 'total'
          },
          {
            label: 'Residential - Extrapolated',
            data: resExtrapolated,
            backgroundColor: resColors.extrapolated,
            borderColor: '#374151',
            borderWidth: 1,
            stack: 'total'
          },
          {
            label: 'Commercial - Observed',
            data: comObserved,
            backgroundColor: comColors.observed,
            borderColor: '#374151',
            borderWidth: 1,
            stack: 'total'
          },
          {
            label: 'Commercial - Interpolated',
            data: comInterpolated,
            backgroundColor: comColors.interpolated,
            borderColor: '#374151',
            borderWidth: 1,
            stack: 'total'
          },
          {
            label: 'Commercial - Extrapolated',
            data: comExtrapolated,
            backgroundColor: comColors.extrapolated,
            borderColor: '#374151',
            borderWidth: 1,
            stack: 'total'
          }
        ]
      };

      const stackedChartOptions = buildChartOptions({
        type: 'bar',
        title: `Total ${type === 'electricity' ? 'Electricity' : 'Natural Gas'} (Stacked) - New Data`,
        xStacked: true,
        yStacked: true,
        yTitle: yLabel,
        yTickFormat: (v: any) => Number(v).toLocaleString()
      });

      const latestYear = allYears.length > 0 ? allYears[allYears.length - 1] : undefined;
      const latestResValue = allYears.length > 0 
        ? (resObserved[resObserved.length - 1] || 0) + (resInterpolated[resInterpolated.length - 1] || 0) + (resExtrapolated[resExtrapolated.length - 1] || 0)
        : 0;
      const latestComValue = allYears.length > 0
        ? (comObserved[comObserved.length - 1] || 0) + (comInterpolated[comInterpolated.length - 1] || 0) + (comExtrapolated[comExtrapolated.length - 1] || 0)
        : 0;
      const latestValue = latestResValue + latestComValue;

      return {
        chartData: stackedChartData,
        chartOptions: stackedChartOptions,
        latestYear,
        latestValue
      };
    } catch (error) {
      console.error(`Error fetching stacked data from new tables for ${type}:`, error);
      return null;
    }
  };

  void _fetchStackedDataFromNewTables;

  // California benchmark: (res + com activity) ÷ state population from ghg_inventory_precalculated.
  const calculateCaliforniaPerCapita = async (type: string) => {
    try {
      const years = Array.from({ length: 2024 - 2005 + 1 }, (_, i) => 2005 + i);
      let activityRows = await fetchCaliforniaStateEnergyActivityFromGhgInventory(supabaseClient);
      const hasElecActivity = activityRows.some(
        (r) => r.elec_residential_kwh + r.elec_commercial_kwh > 0
      );
      const hasGasActivity = activityRows.some(
        (r) => r.ng_residential_therms + r.ng_commercial_therms > 0
      );
      if (!hasElecActivity && !hasGasActivity) {
        const { data: precalcRows } = await supabaseClient
          .from('ghg_inventory_precalculated')
          .select(
            'year,elec_residential_kwh,elec_commercial_kwh,ng_residential_therms,ng_commercial_therms'
          )
          .eq('geography_type', 'State')
          .eq('geofips', CALIFORNIA_STATE_GEOFIPS)
          .gte('year', 2005)
          .lte('year', 2024)
          .order('year', { ascending: true });
        activityRows = (precalcRows || []).map((row: any) => ({
          year: Number(row.year),
          elec_residential_kwh: Number(row.elec_residential_kwh) || 0,
          elec_commercial_kwh: Number(row.elec_commercial_kwh) || 0,
          elec_residential_emissions: null,
          elec_commercial_emissions: null,
          ng_residential_therms: Number(row.ng_residential_therms) || 0,
          ng_commercial_therms: Number(row.ng_commercial_therms) || 0,
        }));
      }
      const [populationResult] = await Promise.all([
        supabaseClient
          .from('population')
          .select('year, population')
          .eq('location_name', 'California')
          .eq('geography_type', 'State')
          .in('year', years)
          .order('year', { ascending: true }),
      ]);

      if (populationResult.error) throw populationResult.error;

      const populationByYear = new Map<number, number>();
      (populationResult.data || []).forEach((row: any) => {
        const population = Number(row.population);
        if (Number.isFinite(population) && population > 0) {
          populationByYear.set(Number(row.year), population);
        }
      });

      const isElectricity = type === 'Electricity';
      const unit = isElectricity ? 'kWh/person' : 'GJ/person';
      const yTitle = isElectricity ? 'kWh per person' : 'GJ per person';

      const californiaPerCapita = activityRows
        .map((row) => {
          const population = populationByYear.get(row.year) || 0;
          const residential = isElectricity
            ? row.elec_residential_kwh
            : row.ng_residential_therms * THERMS_TO_GJ;
          const commercial = isElectricity
            ? row.elec_commercial_kwh
            : row.ng_commercial_therms * THERMS_TO_GJ;
          const consumption = residential + commercial;
          return {
            year: row.year,
            value: population > 0 && consumption > 0 ? consumption / population : 0,
          };
        })
        .filter((item) => item.value > 0);

      if (californiaPerCapita.length === 0) return null;

      return {
        chartData: {
          labels: californiaPerCapita.map((item) => item.year),
          datasets: [{
            label: 'California Average (Total)',
            data: californiaPerCapita.map((item) => item.value),
            backgroundColor: '#ef4444',
            borderColor: '#ef4444',
            borderWidth: 2,
            type: 'line',
          }],
        },
        chartOptions: buildChartOptions({
          type: 'line',
          title: `Total ${type} Per Capita - California Average`,
          tooltipFormat: (v) => `${v.toFixed(2)} ${unit}`,
          yTitle,
          yTickFormat: (v: any) => Number(v).toFixed(1),
        }),
        latestYear: californiaPerCapita[californiaPerCapita.length - 1]?.year,
        latestValue: californiaPerCapita[californiaPerCapita.length - 1]?.value || 0,
      };
    } catch (error) {
      console.error(`Error calculating California ${type} per capita:`, error);
      return null;
    }
  };

  // Download functions for Electricity chart
  const elecViewDownloadLabel =
    elecView === 'res'
      ? 'Residential'
      : elecView === 'com'
        ? 'Commercial'
        : elecView === 'total'
          ? 'Total'
          : 'Per_Capita';

  const handleElecDownloadPNG = () => {
    const filename = formatFilename(currentLocation, `Electricity_${elecViewDownloadLabel}_Chart`);
    if (showBurbankReconciledElecChart) {
      downloadChartJsInstanceAsPNG(elecReconciledChartRef.current, filename);
      return;
    }
    downloadChartAsPNG(elecChartRef, filename);
  };

  const handleElecDownloadCSV = () => {
    const filename = formatFilename(currentLocation, `Electricity_${elecViewDownloadLabel}_Data`);
    // Get the current chart data based on view
    let chartData = null;
    if (elecView === 'res' && elecRes) chartData = elecRes.chartData;
    else if (elecView === 'com' && elecCom) chartData = elecCom.chartData;
    else if (elecView === 'total' && elecStacked) chartData = elecStacked.chartData;
    else if (elecView === 'perCapita' && elecPerCapitaData) chartData = elecPerCapitaData.chartData;
    
    if (chartData) {
      downloadDataAsCSV(chartData, filename);
    }
  };

  // Download functions for Natural Gas chart
  const handleGasDownloadPNG = () => {
    const viewLabel = gasView === 'res' ? 'Residential' : gasView === 'com' ? 'Commercial' : gasView === 'total' ? 'Total' : 'Per_Capita';
    const filename = formatFilename(currentLocation, `Natural_Gas_${viewLabel}_Chart`);
    downloadChartAsPNG(gasChartRef, filename);
  };

  const handleGasDownloadCSV = () => {
    const viewLabel = gasView === 'res' ? 'Residential' : gasView === 'com' ? 'Commercial' : gasView === 'total' ? 'Total' : 'Per_Capita';
    const filename = formatFilename(currentLocation, `Natural_Gas_${viewLabel}_Data`);
    // Get the current chart data based on view
    let chartData = null;
    if (gasView === 'res' && gasRes) chartData = gasRes.chartData;
    else if (gasView === 'com' && gasCom) chartData = gasCom.chartData;
    else if (gasView === 'total' && gasStacked) chartData = gasStacked.chartData;
    else if (gasView === 'perCapita' && gasPerCapitaData) chartData = gasPerCapitaData.chartData;
    
    if (chartData) {
      downloadDataAsCSV(chartData, filename);
    }
  };

  useEffect(() => {
    if (!currentLocation) return;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load Solar PV datasets and energy series
        const [capacityResult, costResult, projectCountResult, batteryCostResult, installersResult, installersCostResult, er, ec, gr, gc] = await Promise.all([
          fetchSolarPVCapacitySupabase(currentLocation),
          fetchSolarPVCostSupabase(currentLocation),
          fetchSolarPVProjectCountSupabase(currentLocation),
          fetchSolarPVBatteryCostSupabase(currentLocation),
          fetchSolarPVInstallersSupabase(currentLocation),
          fetchSolarPVInstallersCostSupabase(currentLocation),
          fetchElectricitySeries(currentLocation, 'residential', 'average', residentialExtrapModel),
          fetchElectricitySeries(currentLocation, 'commercial', commercialExtrapModel),
          fetchNaturalGasSeries(currentLocation, 'residential', 'average', residentialExtrapModel),
          fetchNaturalGasSeries(currentLocation, 'commercial', commercialGasExtrapModel)
        ]);

        setCapacityData(capacityResult);
        setCostData(costResult);
        setProjectCountData(projectCountResult);
        setBatteryCostData(batteryCostResult);
        setInstallersData(installersResult);
        setInstallersCostData(installersCostResult);
        setElecRes(er);
        setElecCom(ec);
        setGasRes(gr);
        setGasCom(gc);

        // Calculate stacked data locally as sum of residential + commercial
        const eiaPilotCfg =
          selectedGeofips != null ? resolveEia861PilotForGeofips(selectedGeofips) : null;
        const elecStacked =
          eiaPilotCfg && er?.eia861PilotActive && er?.chartData?.labels
            ? buildElectricityStackedTotalFromEia861Pilot(er.chartData.labels as number[], eiaPilotCfg)
            : calculateStackedData(
                er,
                ec,
                'Electricity',
                currentLocation,
                electricitySectorLabelForGeofips(selectedGeofips)
              );
        const gasStacked = calculateStackedData(gr, gc, 'Natural Gas');
        setElecStacked(elecStacked);
        setGasStacked(gasStacked);


        // Calculate per capita data
        const elecPerCapita = await calculatePerCapitaData(er, ec, 'Electricity');
        const gasPerCapita = await calculatePerCapitaData(gr, gc, 'Natural Gas');
        setElecPerCapitaData(elecPerCapita);
        setGasPerCapitaData(gasPerCapita);

        // Calculate California averages
        const californiaElec = await calculateCaliforniaPerCapita('Electricity');
        const californiaGas = await calculateCaliforniaPerCapita('Natural Gas');
        setCaliforniaElecPerCapita(californiaElec);
        setCaliforniaGasPerCapita(californiaGas);

        const { data: popMeta } = await supabaseClient
          .from('population')
          .select('geography_type')
          .eq('location_name', currentLocation)
          .limit(1)
          .maybeSingle();
        setPopulationGeographyType(popMeta?.geography_type ?? null);

        // Calculate heat pump metrics
        if (selectedGeofips) {
          const [heatPumpData, perCapitaFacts, costFacts, dgCoverage, dgCoverageFacts] = await Promise.all([
            calculateHeatPumpMetrics(currentLocation, selectedGeofips),
            fetchHeatPumpPerCapitaFacts(selectedGeofips),
            fetchHeatPumpCostFacts(selectedGeofips),
            resolveDgStatsCoverageForGeofips(selectedGeofips),
            resolveSolarDgStatsCoverageFacts(selectedGeofips),
          ]);
          setHeatPumpMetrics(heatPumpData);
          setHeatPumpPerCapitaFacts(perCapitaFacts);
          setHeatPumpCostFacts(costFacts);
          setSolarDgStatsCoverage(dgCoverage);
          setSolarCoverageFacts(dgCoverageFacts);
          const rentNarratives = await calculateMarketRentNarrative(currentLocation, selectedGeofips);
          if (rentNarratives) {
            setMarketRentNarrative(rentNarratives.narrativeLine);
            setMarketRentNarrativeStacked(rentNarratives.narrativeStacked);
            setMarketRentTrend({ higherRentIncreased: rentNarratives.higherRentIncreased, lowerRentDeclined: rentNarratives.lowerRentDeclined });
          } else {
            setMarketRentNarrative(null);
            setMarketRentNarrativeStacked(null);
            setMarketRentTrend(null);
          }
        } else {
          setHeatPumpPerCapitaFacts(null);
          setHeatPumpCostFacts(null);
          setSolarDgStatsCoverage('unknown');
          setSolarCoverageFacts(null);
          setMarketRentNarrative(null);
          setMarketRentNarrativeStacked(null);
          setMarketRentTrend(null);
        }

        // Fetch methodology data
        const [elecMethod, gasMethod] = await Promise.all([
          fetchMethodologyData(currentLocation, 'electricity'),
          fetchMethodologyData(currentLocation, 'naturalGas')
        ]);
        setElecMethodology(elecMethod);
        setGasMethodology(gasMethod);


        if (er?.chartData && ec?.chartData) {
          const etd = buildTableData(er.chartData, ec.chartData, currentLocation);
          elecDataTable.setTableData(etd);
          elecDataTable.setOriginalData([...etd]);
        }

        if (gr?.chartData && gc?.chartData) {
          const gtd = buildTableData(gr.chartData, gc.chartData, currentLocation);
          gasDataTable.setTableData(gtd);
          gasDataTable.setOriginalData([...gtd]);
        }
      } catch (err) {
        console.error('Error loading Solar PV data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load Solar PV data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentLocation, calculateMarketRentNarrative, calculatePerCapitaData, selectedGeofips, commercialExtrapModel, commercialGasExtrapModel, residentialExtrapModel]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!selectedGeofips || !gasRes?.chartData || !gasCom?.chartData) {
        if (!cancelled) setGasProvenanceRow(null);
        return;
      }
      const draft = buildNaturalGasNarrativeInputFromCharts({
        locationName: currentLocation,
        viewId: gasView,
        baselineYear: 2005,
        gasRes,
        gasCom,
        gasPerCapitaData,
        californiaGasPerCapita,
        hasEstimatedYears: chartSeriesHasEstimatedYears(gasRes, gasCom),
        activeResidentialModel: residentialExtrapModel,
        activeCommercialModel: commercialGasExtrapModel,
      });
      const prov = await fetchGasProvenanceForView({
        geofips: selectedGeofips,
        geographyType: populationGeographyType,
        viewId: gasView,
        latestYear: draft?.latestYear ?? null,
      });
      if (!cancelled) setGasProvenanceRow(prov);
    })();
    return () => {
      cancelled = true;
    };
  }, [
    currentLocation,
    gasView,
    selectedGeofips,
    populationGeographyType,
    gasRes,
    gasCom,
    gasPerCapitaData,
    californiaGasPerCapita,
    residentialExtrapModel,
    commercialGasExtrapModel,
  ]);

  useEffect(() => {
    if (!selectedGeofips || isSolarCoverageBlocked(solarCoverageFacts)) {
      setSolarPerCapitaSnapshot(null);
      setSolarCumulativeSnapshot(null);
      setSolarPerCapitaTrend(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [pc, cum] = await Promise.all([
          computeSolarPerCapitaSnapshot(selectedGeofips),
          computeSolarCumulativeSnapshot(selectedGeofips),
        ]);
        if (cancelled) return;
        setSolarPerCapitaSnapshot(pc);
        setSolarCumulativeSnapshot(cum);
        if (pc.localSolarKwPerResident != null) {
          setSolarPerCapitaTrend(null);
        }
      } catch {
        if (!cancelled) {
          setSolarPerCapitaSnapshot(null);
          setSolarCumulativeSnapshot(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedGeofips, solarCoverageFacts]);

  const solarCapacityLargestSectorShare = useMemo(() => {
    const latestYear = capacityData?.latestYear;
    const chartData = capacityData?.chartData;
    if (latestYear == null || !chartData?.labels?.length || !chartData?.datasets?.length) {
      return null;
    }
    const labels = chartData.labels as (number | string)[];
    const idx = labels.findIndex((y) => Number(y) === Number(latestYear));
    if (idx < 0) return null;
    let total = 0;
    let maxVal = 0;
    let maxLabel = '';
    for (const ds of chartData.datasets) {
      const v = Number(ds.data?.[idx]);
      if (!Number.isFinite(v) || v <= 0) continue;
      total += v;
      if (v > maxVal) {
        maxVal = v;
        maxLabel = String(ds.label ?? '');
      }
    }
    if (total <= 0 || maxVal <= 0) return null;
    return { sharePct: (maxVal / total) * 100, sector: maxLabel, year: Number(latestYear) };
  }, [capacityData]);

  const solarDgStatsBlocked = isSolarCoverageBlocked(solarCoverageFacts);

  const solarNarrativeFacts = useMemo(() => {
    const coverage: DgStatsCoverageFacts =
      solarCoverageFacts ?? {
        coverageStatus: 'UNAVAILABLE',
        coverageReason: DGSTATS_COVERAGE_UNAVAILABLE_LABEL,
        coveredUtility: null,
        sourceDataset: 'California DGStats (Interconnected Project Sites → aggregated tables)',
        sourceLatestDate: null,
      };
    const latestYear =
      pvView === 'perCapita'
        ? solarPerCapitaSnapshot?.latestYear ??
          costData?.latestYear ??
          (capacityData?.latestYear != null ? Number(capacityData.latestYear) : null)
        : pvView === 'cumulative'
          ? solarCumulativeSnapshot?.latestYear ??
            costData?.latestYear ??
            (capacityData?.latestYear != null ? Number(capacityData.latestYear) : null)
          : costData?.latestYear ??
            (capacityData?.latestYear != null ? Number(capacityData.latestYear) : null);
    return buildSolarInstallationNarrativeFacts({
      locationName: currentLocation || '',
      viewId: pvView as SolarPvViewId,
      coverage,
      latestYear,
      capacityMw:
        pvView === 'perCapita' && solarPerCapitaSnapshot?.latestCapacityKw != null
          ? solarPerCapitaSnapshot.latestCapacityKw / 1000
          : capacityData?.latestYearCapacity ?? null,
      latestAnnualCapacityMw:
        solarCumulativeSnapshot?.latestAnnualAdditionMw ?? capacityData?.latestYearCapacity ?? null,
      cumulativeSolarMw: solarCumulativeSnapshot?.cumulativeSolarMw ?? capacityData?.totalCapacity ?? null,
      localSolarKwPerResident: solarPerCapitaSnapshot?.localSolarKwPerResident ?? null,
      sameYearPopulation: solarPerCapitaSnapshot?.sameYearPopulation ?? null,
      aggregateSolarCostPerW: costData?.latestResidentialAggregateCostPerW ?? null,
      representedProjectCount: costData?.representedApplicationCount ?? null,
      projectUnitLabel: 'applications represented in aggregate rows',
      validApplicationCount: costData?.representedApplicationCount ?? null,
      selectedSolarSectorLabel: 'residential',
      topInstallerLabel: installersData?.topPairLabel ?? installersData?.topInstaller ?? '—',
      topInstallerShare: installersData?.topPairShare ?? null,
      installerShareBasis:
        installersData?.installerShareBasis ??
        'capacity represented by the displayed top 10 installer-manufacturer pairs',
      secondInstallerSentence: '',
      installerCountSentence: installersData?.totalInstallers
        ? ` ${installersData.totalInstallers} installer–manufacturer pairs shown in the top 10 list.`
        : '',
      lowestCostInstaller: installersCostData?.lowestCostInstaller ?? '—',
      highestCostInstaller:
        (installersCostData?.qualifyingPairCount ?? 0) >= 2
          ? installersCostData?.highestCostInstaller ?? '—'
          : '—',
      lowestInstallerCostPerW: installersCostData?.lowestInstallerCostPerW ?? null,
      highestInstallerCostPerW:
        (installersCostData?.qualifyingPairCount ?? 0) >= 2
          ? installersCostData?.highestInstallerCostPerW ?? null
          : null,
      installerCostSampleSentence:
        installersCostData?.comparisonUniverseLabel != null
          ? ` Comparison uses ${installersCostData.comparisonUniverseLabel} (${installersCostData.qualifyingPairCount ?? 0} qualifying categories).`
          : '',
      comparisonUniverseLabel: installersCostData?.comparisonUniverseLabel ?? '',
      lowestInstallerN: installersCostData?.lowestInstallerN ?? null,
      highestInstallerN: installersCostData?.highestInstallerN ?? null,
      cumulativeGrowthSentence: '',
      latestAdditionSentence:
        solarCumulativeSnapshot?.post2015CohortSharePct != null
          ? ` ${solarCumulativeSnapshot.post2015CohortSharePct.toFixed(1)}% of currently represented DC capacity belongs to approval cohorts after 2015.`
          : solarCumulativeSnapshot?.latestAnnualAdditionMw != null && solarCumulativeSnapshot.latestYear
            ? ` The ${solarCumulativeSnapshot.latestYear} approval cohort accounts for ${solarCumulativeSnapshot.latestAnnualAdditionMw.toFixed(2)} MW DC of currently represented capacity.`
            : '',
      solarPerCapitaComparisonSentence: '',
      solarCutoffYear: SOLAR_ANALYSIS_CUTOFF_YEAR,
      populationYear: solarPerCapitaSnapshot?.latestYear ?? null,
      comparisonYear: batteryCostData?.comparisonYear ?? costData?.latestYear ?? null,
      solarOnlyCostPerKw: batteryCostData?.latestSolarOnlyCostPerKw ?? null,
      solarStorageCostPerKw: batteryCostData?.latestSolarStorageCostPerKw ?? null,
      solarStorageCostUnitLabel: 'nominal $/kW DC (mean, residential tech_type_cost)',
      solarPerCapitaTrendSentence:
        solarPerCapitaTrend === 'increasing'
          ? ' Trend: increasing over the displayed period.'
          : solarPerCapitaTrend === 'decreasing'
            ? ' Trend: decreasing over the displayed period.'
            : '',
      ownershipMixSentence: '',
      solarSampleSentence: '',
      solarCostTrendSentence:
        costData?.costTrend === 'downward'
          ? ' Trend: downward over the displayed period (aggregate $/W DC).'
          : '',
      trendSentence: '',
      sectorShareSentence: '',
    });
  }, [
    solarCoverageFacts,
    currentLocation,
    pvView,
    capacityData,
    costData,
    installersData,
    installersCostData,
    batteryCostData,
    solarPerCapitaTrend,
    solarPerCapitaSnapshot,
    solarCumulativeSnapshot,
  ]);

  const solarAuthoredBundle = useMemo(
    () => buildSolarInstallationsAuthoredBundle(pvView as SolarPvViewId, solarNarrativeFacts),
    [pvView, solarNarrativeFacts]
  );

  const toggleView = (view: string) => {
    setActiveView(view);
  };

  const toggleCarbonIntensityView = (view: string) => {
    setActiveCarbonIntensityView(view);
  };

  if (!currentLocation) {
    return (
      <div>
        <PageHeader 
          title="Energy & Buildings" 
          subtitle="Please select a location to view Solar PV installation data."
          variant="buildings"
        />
      </div>
    );
  }

  if (coreDataLoading) {
    return (
      <div>
        <PageHeader 
          title="Loading core data..." 
          subtitle="Preloading VMT, electricity, and natural gas data for this location."
          variant="buildings"
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        <PageHeader 
          title="Loading..." 
          subtitle="Please wait while we load the Buildings and Energy data."
          variant="buildings"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageHeader 
          title="Error" 
          subtitle={error}
          variant="buildings"
        />
      </div>
    );
  }

  return (
    <div data-report-root="buildings" data-outreach-snapshot="buildings">
      <PageHeader 
        title={`Energy & Buildings - ${currentLocation}`}
        subtitle="Track energy use, emissions, costs, clean-energy adoption, and the housing conditions that shape the transition."
        variant="buildings"
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
      {/* Electricity (Residential / Commercial) with toggle-style layout similar to GHG */}
      <SectionWrapper sectionId="electricity_consumption" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <ChartLayout
        scrollAnchorId="chart-electricity-consumption"
        title="Electricity Consumption"
        narrativeViewId={elecView}
        description="Residential and reported commercial electricity use over time, with observed and estimated years distinguished."
        chartType="bar"
        chartSelector={{
          options: allowedElectricityOptions.map((option) => ({ value: option.value, label: option.label })),
          value: elecView,
          onChange: (value) => setElecView(value as BurbankBuildingsElecTab),
        }}
        keyMetrics={
          <KeyMetrics>
            {showBurbankReconciledElecChart && burbankReconciledElecView && (() => {
              if (burbankReconciledCanonical.loading) {
                return <KeyMetric value="…" label="Loading reconciled series" size="small" />;
              }
              const rows = burbankReconciledCanonical.electricity?.years ?? [];
              const latest = rows[rows.length - 1];
              if (!latest) return null;
              const est =
                burbankReconciledElecView === 'elec_residential'
                  ? latest.residential
                  : burbankReconciledElecView === 'elec_nonresidential'
                    ? latest.nonresidential
                    : latest.total;
              const activity = est?.value != null ? formatActivityUnits(est.value) : '—';
              return (
                <>
                  <KeyMetric
                    value={`${activity} ${est?.unit ?? 'kWh'}`}
                    label={`Reconciled (${latest.year})`}
                    size="medium"
                  />
                  <KeyMetric
                    value="EIA-861 + CAP"
                    label="Pilot data sources"
                    size="small"
                  />
                </>
              );
            })()}
            {!showBurbankReconciledElecChart && elecView === 'res' && elecRes && elecRes.chartData && elecCom?.chartData && (() => {
              const metrics = getEiaElectricitySectorMetrics(elecRes, 'residential');
              const displayYear = metrics.latestYear > 0 ? metrics.latestYear : 2023;
              const baselineYear = (metrics as { baselineYear?: number | null }).baselineYear;
              const pair = latestPositiveYearPair(elecRes.chartData, elecCom.chartData);
              const share = pair ? sectorSharePercent(pair.residential, pair.total) : null;
              return (
                <>
                  <KeyMetric
                    value={metrics.latestValue > 0 ? formatElectricityDisplay(metrics.latestValue) : '—'}
                    label={`Residential consumption (${displayYear})`}
                  />
                  <KeyMetric
                    value={formatSignedPercentChange(metrics.changePercentSince2005)}
                    label={percentChangeLabel(baselineYear ?? 2005)}
                  />
                  <KeyMetric
                    value={share != null ? `${share}%` : '—'}
                    label={`Residential share of res+com (${pair?.year ?? displayYear})`}
                  />
                </>
              );
            })()}
            {!showBurbankReconciledElecChart && elecView === 'com' && elecCom && elecCom.chartData && elecRes?.chartData && (() => {
              const metrics = getEiaElectricitySectorMetrics(elecCom, 'commercial');
              const displayYear = metrics.latestYear > 0 ? metrics.latestYear : 2023;
              const comLabel = elecCom.eia861NonresLabel ?? 'Reported commercial';
              const baselineYear = (metrics as { baselineYear?: number | null }).baselineYear;
              const pair = latestPositiveYearPair(elecRes.chartData, elecCom.chartData);
              const share = pair ? sectorSharePercent(pair.commercial, pair.total) : null;
              return (
                <>
                  <KeyMetric
                    value={metrics.latestValue > 0 ? formatElectricityDisplay(metrics.latestValue) : '—'}
                    label={`${comLabel} consumption (${displayYear})`}
                  />
                  <KeyMetric
                    value={formatSignedPercentChange(metrics.changePercentSince2005)}
                    label={percentChangeLabel(baselineYear ?? 2005)}
                  />
                  <KeyMetric
                    value={share != null ? `${share}%` : '—'}
                    label={`Commercial share of res+com (${pair?.year ?? displayYear})`}
                  />
                </>
              );
            })()}
            {!showBurbankReconciledElecChart && elecView === 'total' && elecRes && elecRes.chartData && elecCom && elecCom.chartData && (() => {
              const metrics = getEiaElectricityTotalMetrics(elecRes, elecCom);
              const displayYear = metrics.latestYear > 0 ? metrics.latestYear : 2023;
              const baselineYear = (metrics as { baselineYear?: number | null }).baselineYear;
              const pair = latestPositiveYearPair(elecRes.chartData, elecCom.chartData);
              const resShare = pair ? sectorSharePercent(pair.residential, pair.total) : null;
              const comShare = pair ? sectorSharePercent(pair.commercial, pair.total) : null;
              return (
                <>
                  <KeyMetric value={metrics.latestValue > 0 ? formatElectricityDisplay(metrics.latestValue) : '—'} label={`Residential + commercial (${displayYear})`} />
                  <KeyMetric value={formatSignedPercentChange(metrics.changePercentSince2005)} label={percentChangeLabel(baselineYear ?? 2005)} />
                  <KeyMetric
                    value={resShare != null && comShare != null ? `${resShare.toFixed(1)}% / ${comShare.toFixed(1)}%` : '—'}
                    label={`Res / reported-commercial share (${pair?.year ?? displayYear})`}
                  />
                </>
              );
            })()}
            {elecView === 'perCapita' && elecPerCapitaData?.chartData && (() => {
              const locLabels = (elecPerCapitaData.chartData.labels || []) as number[];
              const locData = (elecPerCapitaData.chartData.datasets?.[0]?.data || []) as number[];
              const latestLocal = latestPositiveYearFromSeries(locLabels, locData);
              const caChart = californiaElecPerCapita?.chartData;
              const pair =
                caChart
                  ? latestCommonPositiveYear(
                      locLabels,
                      locData,
                      (caChart.labels || []) as number[],
                      (caChart.datasets?.[0]?.data || []) as number[]
                    )
                  : null;
              const localYear = pair?.year ?? latestLocal?.year;
              const localVal = pair?.local ?? latestLocal?.value;
              const localStr =
                localVal != null ? `${Math.round(localVal)} kWh/person` : '—';
              const caStr = pair != null ? `${Math.round(pair.compare)} kWh/person` : '—';
              const { differencePercent, comparison } = pair
                ? compareLocalToCalifornia(pair.local, pair.compare)
                : { differencePercent: null, comparison: 'about the same as' as const };
              const vsCa =
                differencePercent != null
                  ? `${formatSignedPercentChange(differencePercent)} ${comparison} CA`
                  : '—';
              const bIdx = locLabels.findIndex((y) => Number(y) === 2005);
              const base2005 = bIdx >= 0 ? locData[bIdx] : null;
              const pctSince2005 =
                localVal != null && base2005 != null && base2005 > 0
                  ? ((localVal - base2005) / base2005) * 100
                  : null;
              return (
                <>
                  <KeyMetric value={localStr} label={localYear ? `Local kWh per person (${localYear})` : 'Local kWh per person'} />
                  <KeyMetric value={formatSignedPercentChange(pctSince2005)} label="Change since 2005 (kWh/person)" />
                  <KeyMetric value={caStr} label={pair ? `California kWh per person (${pair.year})` : 'California kWh per person'} />
                  <KeyMetric value={vsCa} label={pair ? `Versus California (${pair.year})` : 'Versus California'} />
                </>
              );
            })()}
          </KeyMetrics>
        }
        chartContent={
          <div>
            {(isElectricityDataUnderReview(selectedGeofips) || isElectricityDataUnderReviewByName(currentLocation)) && (
              <DataReviewBanner locationName={getLocationNameUnderReview(selectedGeofips) || getLocationNameUnderReview(getGeofipsFromLocationName(currentLocation)) || currentLocation} />
            )}
            {showBurbankReconciledElecChart && burbankReconciledElecView ? (
              burbankReconciledCanonical.error ? (
                <div style={{ color: '#b91c1c' }} role="alert">{burbankReconciledCanonical.error}</div>
              ) : burbankReconciledCanonical.loading ? (
                <div style={{ minHeight: 200 }}>Loading reconciled electricity chart…</div>
              ) : (
                <BurbankBuildingsReconciledChart
                  location={currentLocation}
                  view={burbankReconciledElecView}
                  electricityRows={burbankReconciledCanonical.electricity?.years ?? []}
                  chartRef={elecReconciledChartRef}
                />
              )
            ) : null}
            {!showBurbankReconciledElecChart && elecView === 'res' && elecRes && (
              <>
                {electricityUsesEia861Pilot ? (
                  eia861SourceNote
                ) : (
                  <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    {elecRes.allDataObserved && <span>100% observed data</span>}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>Residential extrapolation:</span>
                      <select
                        value={residentialExtrapModel}
                        onChange={(e) => setResidentialExtrapModel(e.target.value as ResidentialExtrapolationModel)}
                        style={{
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          padding: '6px 8px',
                          fontSize: '13px',
                          background: '#fff'
                        }}
                      >
                        {RESIDENTIAL_MODEL_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                )}
                {elecRes.eia861ChartSubtitle && (
                  <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 8px 0' }}>{elecRes.eia861ChartSubtitle}</p>
                )}
                <ChartRendererSupabase
                  chartData={elecRes.chartData}
                  chartOptions={elecRes.chartOptions}
                  title="Residential Electricity"
                  location={currentLocation}
                  chartRef={elecChartRef}
                  remountKey={`elec-res-${currentLocation}`}
                />
              </>
            )}
            {!showBurbankReconciledElecChart && elecView === 'com' && elecCom && (
              <>
                {electricityUsesEia861Pilot ? (
                  eia861SourceNote
                ) : (
                  <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    {elecCom.allDataObserved && <span>100% observed data</span>}
                    {availableCommercialModelOptions.length === 0 && !elecCom.allDataObserved && (
                      <span style={{ fontSize: 13, color: '#6b7280' }}>
                        No guardrail-approved extrapolation models for this city; chart uses observed, interpolated, and legacy gap-fill values.
                      </span>
                    )}
                    {availableCommercialModelOptions.length > 0 && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>Extrapolation method:</span>
                        <select
                          value={commercialExtrapModel}
                          onChange={(e) => setCommercialExtrapModel(e.target.value as CommercialExtrapolationModel)}
                          style={{
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            fontSize: '13px',
                            background: '#fff'
                          }}
                        >
                          {availableCommercialModelOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                  </div>
                )}
                {elecCom.eia861ChartSubtitle && (
                  <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 8px 0' }}>{elecCom.eia861ChartSubtitle}</p>
                )}
                <ChartRendererSupabase
                  chartData={elecCom.chartData}
                  chartOptions={elecCom.chartOptions}
                  title="Commercial Electricity"
                  location={currentLocation}
                  chartRef={elecChartRef}
                  remountKey={`elec-com-${currentLocation}`}
                />
              </>
            )}
            {!showBurbankReconciledElecChart && elecView === 'total' && elecStacked && (
              <>
                {electricityUsesEia861Pilot && eia861SourceNote}
                <ChartRendererSupabase
                  chartData={elecStacked.chartData}
                  chartOptions={elecStacked.chartOptions}
                  title="Total Electricity (Stacked)"
                  location={currentLocation}
                  chartRef={elecChartRef}
                  remountKey={`elec-total-${currentLocation}`}
                />
              </>
            )}
            {elecView === 'perCapita' && elecPerCapitaData?.chartData && (
              <ChartRendererSupabase 
                chartData={{
                  labels: elecPerCapitaData.chartData.labels,
                  datasets: mergePerCapitaCaliforniaDatasets(
                    elecPerCapitaData.chartData,
                    californiaElecPerCapita?.chartData
                  ),
                }}
                chartOptions={elecPerCapitaData.chartOptions}
                height="450px"
                title="Electricity Total Per Capita"
                location={currentLocation}
                chartRef={elecChartRef}
                remountKey={`elec-percapita-${currentLocation}`}
              />
            )}
            {!elecRes && !elecCom && !elecStacked && (
              <div className="placeholder-chart"><p>Loading electricity data…</p></div>
            )}
          </div>
        }
        overviewContent={
          (() => {
            const narrativeInput = buildElectricityNarrativeInput();
            const commercialLabel = elecCom?.eia861NonresLabel ?? 'Reported commercial';
            const authored =
              narrativeInput && (elecRes || elecCom || elecView === 'perCapita')
                ? buildElectricityAuthoredBundle(narrativeInput, {
                    dataSourcesFromDb: elecMethodology?.dataSources ?? [],
                    methodsFromDb: elecMethodology?.methods ?? [],
                  })
                : null;
            const overviewDescription = authored?.overview
              ? authored.overview
              : electricityOverviewDescription(elecView as ElectricityViewId, commercialLabel);
            const keyInsights =
              authored?.keyInsights ||
              (narrativeInput ? buildElectricityKeyInsights(narrativeInput) : `Loading electricity data for ${currentLocation}…`);

            return (
              <OverviewContent title="Overview" description={overviewDescription}>
                <p>{keyInsights}</p>
              </OverviewContent>
            );
          })()
        }
        relevanceContent={
          (() => {
            const narrativeInput = buildElectricityNarrativeInput();
            const authored = narrativeInput
              ? buildElectricityAuthoredBundle(narrativeInput, {
                  dataSourcesFromDb: elecMethodology?.dataSources ?? [],
                  methodsFromDb: elecMethodology?.methods ?? [],
                })
              : null;
            if (authored) return <AuthoredRelevancePanel bundle={authored} />;
            return (
              <AuthoredRelevancePanel
                chartId="electricity_consumption"
                viewId={elecView as string}
                body="Electricity demand matters because its climate impact depends on both how much electricity is used and how carbon-intensive the supply is."
              />
            );
          })()
        }
        equityContent={
          (() => {
            const narrativeInput = buildElectricityNarrativeInput();
            const authored = narrativeInput
              ? buildElectricityAuthoredBundle(narrativeInput, {
                  dataSourcesFromDb: elecMethodology?.dataSources ?? [],
                  methodsFromDb: elecMethodology?.methods ?? [],
                })
              : null;
            if (authored?.equityV6 || authored?.equity) {
              return <AuthoredEquityPanel bundle={authored} />;
            }
            return (
              <AuthoredEquityPanel
                body="Community totals can conceal large differences in bills, housing quality, technology access, and control over building improvements."
              />
            );
          })()
        }
        methodologyContent={
          (() => {
            const narrativeInput = buildElectricityNarrativeInput();
            const authored = narrativeInput
              ? buildElectricityAuthoredBundle(narrativeInput, {
                  dataSourcesFromDb: elecMethodology?.dataSources ?? [],
                  methodsFromDb: elecMethodology?.methods ?? [],
                })
              : null;
            const structured =
              authored?.methodology ??
              (narrativeInput
                ? buildElectricityMethodologySteps(narrativeInput, {
                    dataSourcesFromDb: elecMethodology?.dataSources ?? [],
                    methodsFromDb: elecMethodology?.methods ?? [],
                  })
                : null);
            const v = getElectricityNarrativeVars();
            const dataPeriod = v?.dataPeriod ?? 'the available period';
            const location = currentLocation;
            const utilityName = (elecMethodology?.dataSources && elecMethodology.dataSources.length > 0)
              ? elecMethodology.dataSources[0]
              : 'utility provider records';
            const hasEstimatedElecYears = narrativeInput
              ? Boolean(narrativeInput.hasEstimatedYears)
              : chartSeriesHasEstimatedYears(elecRes, elecCom);
            const legacy = {
              title: 'Methodology',
              description: hasEstimatedElecYears
                ? 'Residential and reported commercial electricity by utility customer class (kWh), allocated to the selected place geography. The chart legend separates Observed (utility-reported), Interpolated, and Extrapolated years when gap-fill models are active.'
                : `Residential and reported commercial electricity by utility customer class (kWh) for ${location}. All years in the active charts are utility-reported (Observed); no interpolated or extrapolated gap-fill years are shown.`,
              dataSources: elecMethodology?.dataSources?.length ? elecMethodology.dataSources : [utilityName],
              methodology: [
                `Annual electricity consumption is compiled for the active chart view in ${location} for ${dataPeriod} (kWh).`,
                'Customize table edits are treated as observed anchors; intermediate years may be linearly interpolated.',
                'Industrial sales, when reported separately, are not included in residential plus reported commercial totals.',
              ],
            };
            const props = resolveMethodologyContentProps(structured ?? undefined, legacy);
            return <MethodologyContent {...props} />;
          })()
        }
        activeView={activeView}
        onViewChange={toggleView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        appendLocationToTitle={true}
        overviewSectionId="buildings"
        overviewChartId="electricity_consumption"
        dataTableContent={elecDataTable.showDataTable ? (
          <div>
            {elecDataTable.modifiedYears.size > 0 && (
              <div className="modified-years-info" style={{ marginBottom: '16px' }}>
                <small>
                  <strong>Modified years:</strong> {Array.from(elecDataTable.modifiedYears).sort((a, b) => a - b).join(', ')}
                  {' '}(treated as observed; earlier years keep prior YoY % change; years through the next observed value are linearly interpolated)
                </small>
              </div>
            )}
            <DataTable
              data={elecDataTable.tableData}
              highlightedRows={elecDataTable.modifiedYears}
              highlightKey="year"
              columns={[
                { key: 'year', label: 'Year', type: 'year', editable: true, 
                  validate: (value) => {
                    const year = parseInt(value);
                    return (year >= 2005 && year <= 2023) ? null : 'Year must be between 2005-2023';
                  }
                },
                { key: 'residential', label: 'Residential (kWh)', type: 'number', editable: true,
                  validate: (value) => {
                    const num = parseFloat(value);
                    return (!isNaN(num) && num >= 0) ? null : 'Value must be a positive number';
                  }
                },
                { key: 'commercial', label: 'Commercial (kWh)', type: 'number', editable: true,
                  validate: (value) => {
                    const num = parseFloat(value);
                    return (!isNaN(num) && num >= 0) ? null : 'Value must be a positive number';
                  }
                },
                { key: 'total', label: 'Total (kWh)', type: 'number', editable: false },
                { key: 'location', label: 'Location', type: 'text', editable: false }
              ]}
              onDataChange={elecDataTable.handleDataTableChange}
              onRestoreDefaults={elecDataTable.restoreDefaults}
              title="Electricity Data"
              maxHeight="500px"
              pageSize={15}
            />
          </div>
        ) : null}
        showDataTable={elecDataTable.showDataTable}
        onToggleDataTable={elecDataTable.toggleDataTable}
        onDownloadPNG={handleElecDownloadPNG}
        onDownloadCSV={handleElecDownloadCSV}
        pngFilename={`Electricity_${elecViewDownloadLabel}_Chart`}
        csvFilename={`Electricity_${elecViewDownloadLabel}_Data`}
      />
            </SectionWrapper>

 
      {/* Carbon Intensity of Electricity */}
      <SectionWrapper sectionId="carbon_intensity" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <ChartLayout
        title="Carbon Intensity of Electricity"
        description="This chart shows greenhouse gas emissions per unit of electricity consumed (gCO2e/kWh) over time, indicating how clean or carbon-intensive the local electricity supply is."
        chartType="bar"
        keyMetrics={
          <KeyMetrics>
            {(() => {
              const m = getCarbonIntensityNarrativeVars();
              if (!m) {
                return (
                  <>
                    <KeyMetric value="—" label="Carbon intensity (latest year)" />
                    <KeyMetric value="—" label="% change since baseline" />
                    <KeyMetric value="—" label="Change since baseline" />
                  </>
                );
              }

              const latestStr = `${Math.round(m.latestValue).toLocaleString()} gCO₂e/kWh`;
              const pctStr =
                m.changePercentSinceBaseline != null
                  ? `${m.changePercentSinceBaseline >= 0 ? '+' : ''}${m.changePercentSinceBaseline.toFixed(1)}%`
                  : '—';
              const absStr =
                m.changeAbsSinceBaseline != null
                  ? `${m.changeAbsSinceBaseline >= 0 ? '+' : ''}${Math.round(
                      m.changeAbsSinceBaseline
                    ).toLocaleString()} gCO₂e/kWh`
                  : '—';
              const changeLabel =
                m.changeAbsSinceBaseline != null && m.changeAbsSinceBaseline < 0
                  ? `decrease since ${m.baselineYear}`
                  : `increase since ${m.baselineYear}`;

              return (
                <>
                  <KeyMetric
                    value={latestStr}
                    label={`Carbon intensity in ${m.latestYear}`}
                  />
                  <KeyMetric
                    value={pctStr}
                    label={`% change since ${m.baselineYear}`}
                  />
                  <KeyMetric value={absStr} label={changeLabel} />
                </>
              );
            })()}
          </KeyMetrics>
        }
        chartContent={
          <CarbonIntensityChart
            geofips={String(selectedGeofips || '')}
            onDataLoaded={setCarbonIntensityData}
          />
        }
        overviewContent={
          (() => {
            const m = getCarbonIntensityNarrativeVars();
            const fallback = `Carbon intensity for ${currentLocation}.`;
            if (!m) {
              return (
                <OverviewContent title="Electricity Carbon Intensity" description={fallback}>
                  <p>{`Loading electricity carbon intensity data for ${currentLocation}...`}</p>
                </OverviewContent>
              );
            }
            const input = {
              locationName: currentLocation,
              latestYear: m.latestYear,
              latestGco2ePerKwh: m.latestValue,
              baselineYear: m.baselineYear,
              baselineGco2ePerKwh: m.baselineValue,
              percentChange: m.changePercentSinceBaseline,
              absoluteChange: m.changeAbsSinceBaseline,
              sourceStatusSentence: buildCarbonSourceStatusSentence(
                carbonIntensityData?.seriesByYear,
                m.latestYear
              ),
            };
            const authored = buildCarbonIntensityAuthoredBundle(input);
            const description = authored.overview || carbonIntensityOverviewDescription(fallback);
            const keyInsights = authored.keyInsights || buildCarbonIntensityKeyInsights(input);
            return (
              <OverviewContent title="Electricity Carbon Intensity" description={description}>
                <p>{keyInsights}</p>
              </OverviewContent>
            );
          })()
        }
        relevanceContent={
          (() => {
            const m = getCarbonIntensityNarrativeVars();
            if (!m) return <AuthoredRelevancePanel body="" />;
            const authored = buildCarbonIntensityAuthoredBundle({
              locationName: currentLocation,
              latestYear: m.latestYear,
              latestGco2ePerKwh: m.latestValue,
              baselineYear: m.baselineYear,
              baselineGco2ePerKwh: m.baselineValue,
              percentChange: m.changePercentSinceBaseline,
              absoluteChange: m.changeAbsSinceBaseline,
              sourceStatusSentence: '',
            });
            return <AuthoredRelevancePanel bundle={authored} />;
          })()
        }
        equityContent={
          (() => {
            const m = getCarbonIntensityNarrativeVars();
            if (!m) return <AuthoredEquityPanel body="" />;
            const authored = buildCarbonIntensityAuthoredBundle({
              locationName: currentLocation,
              latestYear: m.latestYear,
              latestGco2ePerKwh: m.latestValue,
              baselineYear: m.baselineYear,
              baselineGco2ePerKwh: m.baselineValue,
              percentChange: m.changePercentSinceBaseline,
              absoluteChange: m.changeAbsSinceBaseline,
              sourceStatusSentence: '',
            });
            return <AuthoredEquityPanel bundle={authored} />;
          })()
        }
        methodologyContent={
          (() => {
            const m = getCarbonIntensityNarrativeVars();
            const authored = m
              ? buildCarbonIntensityAuthoredBundle({
                  locationName: currentLocation,
                  latestYear: m.latestYear,
                  latestGco2ePerKwh: m.latestValue,
                  baselineYear: m.baselineYear,
                  baselineGco2ePerKwh: m.baselineValue,
                  percentChange: m.changePercentSinceBaseline,
                  absoluteChange: m.changeAbsSinceBaseline,
                  sourceStatusSentence: '',
                })
              : null;
            const verified = buildCarbonIntensityVerifiedMethodology();
            const legacy = {
              title: 'Methodology',
              description: `Electricity carbon intensity for ${currentLocation} is reported in gCO₂e/kWh.`,
              dataSources: [],
              methodology: [],
            };
            const props = resolveMethodologyContentProps(verified, legacy);
            return <MethodologyContent {...props} />;
          })()
        }
        activeView={activeCarbonIntensityView}
        onViewChange={toggleCarbonIntensityView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        appendLocationToTitle={true}
        onDownloadPNG={() => makeDownloadPNG('#carbon-intensity-chart', 'Carbon_Intensity_Electricity', currentLocation)}
        onDownloadCSV={() => {
          if (!carbonIntensityData?.chartData?.labels?.length) return;
          const { labels, datasets } = carbonIntensityData.chartData;
          const rows = labels.map((year: number, i: number) => {
            const row: Record<string, number | string> = { year };
            datasets.forEach((ds: { label: string; data: number[] }) => {
              row[ds.label || 'value'] = ds.data?.[i] ?? '';
            });
            return row;
          });
          downloadDataAsCSV(rows, formatFilename(currentLocation, 'Carbon_Intensity_Data'));
        }}
        pngFilename={formatFilename(currentLocation, 'Carbon_Intensity_Electricity')}
        csvFilename={formatFilename(currentLocation, 'Carbon_Intensity_Data')}
      />
      </SectionWrapper>

      {/* Electricity Prices by Geography */}
      <SectionWrapper sectionId="electricity_prices" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <ChartLayout
        scrollAnchorId="chart-electricity-prices"
        title="Electricity Prices"
        description="This chart compares retail electricity prices in the selected location with California over time and by sector to show local affordability and cost trends."
        chartType="line"
        activeView={electricityPricesView}
        onViewChange={setElectricityPricesView}
        chartSelector={{
          options: [
            { value: 'locationVsCA', label: 'Location vs California' },
            { value: 'byUtility', label: 'Prices by year (utility)' }
          ],
          value: electricityPricesChartView,
          onChange: (v) => setElectricityPricesChartView(v as ElectricityPricesChartView)
        }}
        additionalButtonGroups={[
          {
            label: 'Sector',
            options: [
              { value: 'Residential', label: 'Residential' },
              { value: 'Commercial', label: 'Commercial' },
              { value: 'Industrial', label: 'Industrial' },
              { value: 'Transportation', label: 'Transportation' }
            ],
            value: electricityPricesSector,
            onChange: setElectricityPricesSector
          }
        ]}
        keyMetrics={
          <KeyMetrics>
            {electricityPricesData && (() => {
              const avgLoc = getLatestAveragePrice(electricityPricesData.location, electricityPricesSector);
              const avgCA = getLatestAveragePrice(electricityPricesData.california, electricityPricesSector);
              const locSectorRows = electricityPricesData.location.filter((r) => r.sector === electricityPricesSector);
              const caSectorRows = electricityPricesData.california.filter((r) => r.sector === electricityPricesSector);
              const latestLocYear = locSectorRows.length ? Math.max(...locSectorRows.map((r) => r.year)) : null;
              const latestCaYear = caSectorRows.length ? Math.max(...caSectorRows.map((r) => r.year)) : null;
              const locStr = avgLoc != null ? `$${avgLoc.toFixed(4)}/kWh` : '—';
              const caStr = avgCA != null ? `$${avgCA.toFixed(4)}/kWh` : '—';
              const pct = avgCA != null && avgCA > 0 && avgLoc != null ? ((avgLoc - avgCA) / avgCA) * 100 : null;
              const pctStr = pct != null ? `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%` : '—';
              return (
                <>
                  <KeyMetric value={locStr} label={latestLocYear != null ? `Location avg (${latestLocYear})` : 'Location avg'} />
                  <KeyMetric value={caStr} label={latestCaYear != null ? `California avg (${latestCaYear})` : 'California avg'} />
                  <KeyMetric value={pctStr} label="Compared to CA State Average" />
                </>
              );
            })()}
            {!electricityPricesData?.location?.length && !electricityPricesData?.california?.length && (
              <>
                <KeyMetric value="—" label="Location avg" />
                <KeyMetric value="—" label="California avg" />
                <KeyMetric value="—" label="Compared to CA State Average" />
              </>
            )}
          </KeyMetrics>
        }
        chartContent={
          <ElectricityPricesByGeographyChart
            geofips={selectedGeofips ?? ''}
            locationName={currentLocation}
            chartView={electricityPricesChartView}
            sector={electricityPricesSector}
            showComparisonToCA={true}
            onDataLoaded={setElectricityPricesData}
          />
        }
        overviewContent={
          (() => {
            const locRows =
              electricityPricesData?.location?.filter((r) => r.sector === electricityPricesSector) || [];
            const caRows =
              electricityPricesData?.california?.filter((r) => r.sector === electricityPricesSector) || [];
            const latestLocYear = locRows.length ? Math.max(...locRows.map((r) => r.year)) : null;
            const latestCaYear = caRows.length ? Math.max(...caRows.map((r) => r.year)) : null;
            const latestLocRow =
              latestLocYear != null ? locRows.find((r) => r.year === latestLocYear) : undefined;
            const latestCaRow =
              latestCaYear != null ? caRows.find((r) => r.year === latestCaYear) : undefined;
            const latestLoc = latestLocRow ? effectivePricePerKwh(latestLocRow) : null;
            const latestCa = latestCaRow ? effectivePricePerKwh(latestCaRow) : null;
            const baselineYear = 2005;
            const baselineLoc = locRows.find((r) => r.year === baselineYear);
            const baselinePrice = baselineLoc ? effectivePricePerKwh(baselineLoc) : null;
            const pctChange =
              baselinePrice != null && baselinePrice > 0 && latestLoc != null
                ? ((latestLoc - baselinePrice) / baselinePrice) * 100
                : null;
            const pct =
              latestCa != null && latestCa > 0 && latestLoc != null
                ? ((latestLoc - latestCa) / latestCa) * 100
                : null;
            const comparisonSentence =
              pct != null && latestCaYear != null
                ? ` Compared with California (${latestCaYear}), local average revenue was ${pct >= 0 ? 'above' : 'below'} the statewide average for this customer class.`
                : '';

            const fallback = electricityPricesOverviewDescription('Average revenue per kWh.');
            if (latestLoc == null || latestLocYear == null) {
              return (
                <OverviewContent title="Overview" description={fallback}>
                  <p>{`Loading average revenue per kWh for ${currentLocation}...`}</p>
                </OverviewContent>
              );
            }
            const input = {
              locationName: currentLocation,
              customerClassLabel: electricityPricesSector,
              latestYear: latestLocYear,
              avgRevenuePerKwh: latestLoc,
              baselineYear,
              percentChange: pctChange,
              comparisonSentence,
            };
            const authored = buildElectricityPricesAuthoredBundle(input, electricityPricesData);
            return (
              <OverviewContent
                title="Overview"
                description={authored.overview || fallback}
              >
                <p>{authored.keyInsights || buildElectricityPricesKeyInsights(input)}</p>
              </OverviewContent>
            );
          })()
        }
        relevanceContent={
          (() => {
            const locRows =
              electricityPricesData?.location?.filter((r) => r.sector === electricityPricesSector) || [];
            const latestLocYear = locRows.length ? Math.max(...locRows.map((r) => r.year)) : null;
            const latestLocRow =
              latestLocYear != null ? locRows.find((r) => r.year === latestLocYear) : undefined;
            const latestLoc = latestLocRow ? effectivePricePerKwh(latestLocRow) : null;
            if (latestLoc == null || latestLocYear == null) return <AuthoredRelevancePanel body="" />;
            const authored = buildElectricityPricesAuthoredBundle(
              {
                locationName: currentLocation,
                customerClassLabel: electricityPricesSector,
                latestYear: latestLocYear,
                avgRevenuePerKwh: latestLoc,
                baselineYear: 2005,
                percentChange: null,
                comparisonSentence: '',
              },
              electricityPricesData
            );
            return <AuthoredRelevancePanel bundle={authored} />;
          })()
        }
        equityContent={
          (() => {
            const locRows =
              electricityPricesData?.location?.filter((r) => r.sector === electricityPricesSector) || [];
            const latestLocYear = locRows.length ? Math.max(...locRows.map((r) => r.year)) : null;
            const latestLocRow =
              latestLocYear != null ? locRows.find((r) => r.year === latestLocYear) : undefined;
            const latestLoc = latestLocRow ? effectivePricePerKwh(latestLocRow) : null;
            if (latestLoc == null || latestLocYear == null) return <AuthoredEquityPanel body="" />;
            const authored = buildElectricityPricesAuthoredBundle(
              {
                locationName: currentLocation,
                customerClassLabel: electricityPricesSector,
                latestYear: latestLocYear,
                avgRevenuePerKwh: latestLoc,
                baselineYear: 2005,
                percentChange: null,
                comparisonSentence: '',
              },
              electricityPricesData
            );
            return <AuthoredEquityPanel bundle={authored} />;
          })()
        }
        methodologyContent={
          (() => {
            const locRows =
              electricityPricesData?.location?.filter((r) => r.sector === electricityPricesSector) || [];
            const latestLocYear = locRows.length ? Math.max(...locRows.map((r) => r.year)) : null;
            const latestLocRow =
              latestLocYear != null ? locRows.find((r) => r.year === latestLocYear) : undefined;
            const latestLoc = latestLocRow ? effectivePricePerKwh(latestLocRow) : null;
            const authored =
              latestLoc != null && latestLocYear != null
                ? buildElectricityPricesAuthoredBundle(
                    {
                      locationName: currentLocation,
                      customerClassLabel: electricityPricesSector,
                      latestYear: latestLocYear,
                      avgRevenuePerKwh: latestLoc,
                      baselineYear: 2005,
                      percentChange: null,
                      comparisonSentence: '',
                    },
                    electricityPricesData
                  )
                : null;
            const verified = buildElectricityPricesVerifiedMethodology();
            const legacy = {
              title: 'Methods',
              description: `Average revenue per kWh from EIA-861 Sales to Ultimate Customers (revenue ÷ retail sales) for matched utility/CCA series.`,
              dataSources: [],
              methodology: [],
            };
            const props = resolveMethodologyContentProps(verified, legacy);
            return <MethodologyContent {...props} />;
          })()
        }
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        appendLocationToTitle={true}
      />
      </SectionWrapper>

      {/* Energy Cost Burden (LEAD 2022) */}
      <SectionWrapper
        sectionId="energy_cost_burden"
        bgMap={BUILDINGS_SECTION_BG}
        refs={sectionObserverRefs}
        visible={sectionVisible}
        getSectionStyle={getSectionStyle}
      >
        <LeadEnergyCostBurdenSection location={currentLocation} geofips={selectedGeofips ?? ''} />
      </SectionWrapper>

      {/* Natural Gas */}
      <SectionWrapper sectionId="natural_gas_consumption" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <ChartLayout
        scrollAnchorId="chart-natural-gas-consumption"
        title="Natural Gas Consumption"
        description="Residential and reported commercial natural-gas use over time, with observed and estimated years distinguished."
        chartType="bar"
        chartSelector={{
          options: allowedNaturalGasOptions.map((option) => ({ value: option.value, label: option.label })),
          value: gasView,
          onChange: (value) => setGasView(value as 'res' | 'com' | 'total' | 'perCapita')
        }}
        keyMetrics={
          <KeyMetrics>
            {gasView === 'res' && gasRes && gasRes.chartData && gasCom?.chartData && (() => {
              const metrics = getSectorMetrics(gasRes, 2005);
              const displayYear = metrics.latestYear > 0 ? metrics.latestYear : 2023;
              const latestTJ =
                metrics.latestValue > 0
                  ? `${formatThermsAsTJ(metrics.latestValue)} TJ`
                  : '—';
              const pair = latestPositiveYearPair(gasRes.chartData, gasCom.chartData);
              const share = pair ? sectorSharePercent(pair.residential, pair.total) : null;
              return (
                <>
                  <KeyMetric value={latestTJ} label={`Residential consumption (${displayYear})`} />
                  <KeyMetric value={formatSignedPercentChange(metrics.changePercentSince2005)} label={percentChangeLabel(2005)} />
                  <KeyMetric value={share != null ? `${share}%` : '—'} label={`Residential share of res+com (${pair?.year ?? displayYear})`} />
                </>
              );
            })()}
            {gasView === 'com' && gasCom && gasCom.chartData && gasRes?.chartData && (() => {
              const metrics = getSectorMetrics(gasCom, 2005);
              const displayYear = metrics.latestYear > 0 ? metrics.latestYear : 2023;
              const latestTJ =
                metrics.latestValue > 0
                  ? `${formatThermsAsTJ(metrics.latestValue)} TJ`
                  : '—';
              const pair = latestPositiveYearPair(gasRes.chartData, gasCom.chartData);
              const share = pair ? sectorSharePercent(pair.commercial, pair.total) : null;
              return (
                <>
                  <KeyMetric value={latestTJ} label={`Reported commercial consumption (${displayYear})`} />
                  <KeyMetric value={formatSignedPercentChange(metrics.changePercentSince2005)} label={percentChangeLabel(2005)} />
                  <KeyMetric value={share != null ? `${share}%` : '—'} label={`Commercial share of res+com (${pair?.year ?? displayYear})`} />
                </>
              );
            })()}
            {gasView === 'total' && gasRes && gasRes.chartData && gasCom && gasCom.chartData && (() => {
              const metrics = calculateTotalMetrics(gasRes, gasCom);
              const displayYear = metrics.latestYear > 0 ? metrics.latestYear : 2023;
              const latestTJ =
                metrics.latestValue > 0
                  ? `${formatThermsAsTJ(metrics.latestValue)} TJ`
                  : '—';
              return (
                <>
                  <KeyMetric value={latestTJ} label={`Residential + commercial (${displayYear})`} />
                  <KeyMetric value={formatSignedPercentChange(metrics.changePercentSince2005)} label={percentChangeLabel(2005)} />
                  {gasPerCapitaData && californiaGasPerCapita
                    ? renderPerCapitaVsCaliforniaMetric(gasPerCapitaData, californiaGasPerCapita, 'GJ/person')
                    : <KeyMetric value="—" label="Versus California per capita" />}
                </>
              );
            })()}
            {gasView === 'perCapita' &&
              gasPerCapitaData &&
              gasPerCapitaData.chartData &&
              californiaGasPerCapita &&
              californiaGasPerCapita.chartData &&
              (() => {
                const locLabels = (gasPerCapitaData.chartData.labels || []) as number[];
                const locData = (gasPerCapitaData.chartData.datasets?.[0]?.data || []) as number[];
                const caLabels = (californiaGasPerCapita.chartData.labels || []) as number[];
                const caData = (californiaGasPerCapita.chartData.datasets?.[0]?.data || []) as number[];
                const pair = latestCommonPositiveYear(locLabels, locData, caLabels, caData);
                const localStr = pair != null ? `${pair.local.toFixed(1)} GJ/person` : '—';
                const caStr = pair != null ? `${pair.compare.toFixed(1)} GJ/person` : '—';
                const { differencePercent, comparison } = pair
                  ? compareLocalToCalifornia(pair.local, pair.compare)
                  : { differencePercent: null, comparison: 'about the same as' as const };
                const vsCa =
                  differencePercent != null
                    ? `${formatSignedPercentChange(differencePercent)} ${comparison} CA`
                    : '—';
                return (
                  <>
                    <KeyMetric value={localStr} label={pair ? `Local GJ per person (${pair.year})` : 'Local GJ per person'} />
                    <KeyMetric value={caStr} label={pair ? `California GJ per person (${pair.year})` : 'California GJ per person'} />
                    <KeyMetric value={vsCa} label={pair ? `Versus California (${pair.year})` : 'Versus California'} />
                  </>
                );
              })()}
          </KeyMetrics>
        }
        chartContent={
          <div>
            {gasView === 'res' && gasRes && (
              <>
                <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  {gasRes.allDataObserved && <span>100% observed data</span>}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>Residential extrapolation:</span>
                    <select
                      value={residentialExtrapModel}
                      onChange={(e) => setResidentialExtrapModel(e.target.value as ResidentialExtrapolationModel)}
                      style={{
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        padding: '6px 8px',
                        fontSize: '13px',
                        background: '#fff'
                      }}
                    >
                      {RESIDENTIAL_MODEL_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <ChartRendererSupabase chartData={gasRes.chartData} chartOptions={gasRes.chartOptions} height="450px" title="Residential Natural Gas" location={currentLocation} chartRef={gasChartRef} />
              </>
            )}
            {gasView === 'com' && gasCom && (
              <>
                <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  {gasCom.allDataObserved && <span>100% observed data</span>}
                  {availableCommercialGasModelOptions.length === 0 && !gasCom.allDataObserved && (
                    <span style={{ fontSize: 13, color: '#6b7280' }}>
                      No guardrail-approved extrapolation models for this city; chart uses observed, interpolated, and legacy gap-fill values.
                    </span>
                  )}
                  {availableCommercialGasModelOptions.length > 0 && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>Extrapolation method:</span>
                      <select
                        value={commercialGasExtrapModel}
                        onChange={(e) => setCommercialGasExtrapModel(e.target.value as CommercialExtrapolationModel)}
                        style={{
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          padding: '6px 8px',
                          fontSize: '13px',
                          background: '#fff'
                        }}
                      >
                        {availableCommercialGasModelOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                </div>
                <ChartRendererSupabase chartData={gasCom.chartData} chartOptions={gasCom.chartOptions} height="450px" title="Commercial Natural Gas" location={currentLocation} chartRef={gasChartRef} />
              </>
            )}
            {gasView === 'total' && gasStacked && (
              <ChartRendererSupabase chartData={gasStacked.chartData} chartOptions={gasStacked.chartOptions} height="470px" title="Total Natural Gas (Stacked)" location={currentLocation} chartRef={gasChartRef} />
            )}
            {gasView === 'perCapita' && gasPerCapitaData?.chartData && (
              <ChartRendererSupabase 
                chartData={{
                  labels: gasPerCapitaData.chartData.labels,
                  datasets: mergePerCapitaCaliforniaDatasets(
                    gasPerCapitaData.chartData,
                    californiaGasPerCapita?.chartData
                  ),
                }}
                chartOptions={gasPerCapitaData.chartOptions}
                height="450px"
                title="Natural Gas Total Per Capita"
                location={currentLocation}
                chartRef={gasChartRef}
              />
            )}
            {!gasRes && !gasCom && !gasStacked && (
              <div className="placeholder-chart"><p>Loading natural gas data…</p></div>
            )}
          </div>
        }
        overviewContent={
          (() => {
            const input = buildNaturalGasNarrativeInput();
            const fallbackDesc =
              'This stacked view shows annual natural-gas use reported for residential and commercial customer classes.';
            const authored = input ? buildNaturalGasAuthoredBundle(input) : null;
            const description =
              authored?.overview || buildNaturalGasOverviewDescription(gasView, fallbackDesc);
            const keyInsights =
              input
                ? authored?.keyInsights || buildNaturalGasKeyInsights(input)
                : `Loading natural gas data for ${currentLocation}...`;
            return (
              <OverviewContent title="Natural Gas Overview" description={description}>
                <p>{keyInsights}</p>
              </OverviewContent>
            );
          })()
        }
        relevanceContent={
          (() => {
            const input = buildNaturalGasNarrativeInput();
            const authored = input ? buildNaturalGasAuthoredBundle(input) : null;
            if (authored) return <AuthoredRelevancePanel bundle={authored} />;
            return (
              <AuthoredRelevancePanel
                chartId="natural_gas_consumption"
                viewId={gasView}
                body="Natural-gas demand helps planners track fossil-fuel use in buildings."
              />
            );
          })()
        }
        equityContent={
          (() => {
            const input = buildNaturalGasNarrativeInput();
            const authored = input ? buildNaturalGasAuthoredBundle(input) : null;
            if (authored?.equityV6 || authored?.equity) {
              return <AuthoredEquityPanel bundle={authored} />;
            }
            return (
              <AuthoredEquityPanel
                chartId="natural_gas_consumption"
                viewId={gasView}
                body="Community totals do not reveal household energy burden or access to electrification."
              />
            );
          })()
        }
        methodologyContent={
          (() => {
            const input = buildNaturalGasNarrativeInput();
            const authored = input ? buildNaturalGasAuthoredBundle(input) : null;
            const legacy = {
              title: 'Methodology',
              description:
                'Natural gas methodology source strings are held pending jurisdiction-specific utility product verification (READY_METHOD_HOLD). Calculation steps below describe the ClimatePlans pipeline only.',
              dataSources:
                gasMethodology?.dataSources && gasMethodology.dataSources.length > 0
                  ? gasMethodology.dataSources
                  : ['Serving utility customer-class sales (verify provider/product per jurisdiction)'],
              methodology: [
                `Annual natural gas consumption data are compiled for residential and commercial sectors in ${currentLocation}.`,
                'Observed utility values are used where available in ng_res_format / ng_com_format.',
                'Residential gap years use population scaling, county year-over-year scaling, or the average of those two from the latest observed/interpolated anchor.',
                'Commercial gap years average guardrail-passing M1–M7 predictions (or a selected model), matching the electricity commercial method.',
                'Residential and commercial values are combined to show total annual consumption.',
                'Customize table edits are treated as observed anchors; years before the edit keep the prior year-over-year percent change (ratio-scaled); years between the edit and later observed values are linearly interpolated.',
              ],
            };
            const props = resolveMethodologyContentProps(authored?.methodology, legacy);
            return <MethodologyContent {...props} />;
          })()
        }
        activeView={activeView}
        onViewChange={toggleView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        appendLocationToTitle={true}
        overviewSectionId="buildings"
        overviewChartId="natural_gas_consumption"
        className="alternating-bg"
        dataTableContent={gasDataTable.showDataTable ? (
          <div>
            {gasDataTable.modifiedYears.size > 0 && (
              <div className="modified-years-info" style={{ marginBottom: '16px' }}>
                <small>
                  <strong>Modified years:</strong> {Array.from(gasDataTable.modifiedYears).sort((a, b) => a - b).join(', ')}
                  {' '}(treated as observed; earlier years keep prior YoY % change; years through the next observed value are linearly interpolated; values shown in GJ)
                </small>
              </div>
            )}
            <DataTable
              data={gasDataTable.tableData}
              highlightedRows={gasDataTable.modifiedYears}
              highlightKey="year"
              columns={[
                { key: 'year', label: 'Year', type: 'year', editable: true, 
                  validate: (value) => {
                    const year = parseInt(value);
                    return (year >= 2005 && year <= 2023) ? null : 'Year must be between 2005-2023';
                  }
                },
                { key: 'residential', label: 'Residential (GJ)', type: 'number', editable: true,
                  validate: (value) => {
                    const num = parseFloat(value);
                    return (!isNaN(num) && num >= 0) ? null : 'Value must be a positive number';
                  }
                },
                { key: 'commercial', label: 'Commercial (GJ)', type: 'number', editable: true,
                  validate: (value) => {
                    const num = parseFloat(value);
                    return (!isNaN(num) && num >= 0) ? null : 'Value must be a positive number';
                  }
                },
                { key: 'total', label: 'Total (GJ)', type: 'number', editable: false },
                { key: 'location', label: 'Location', type: 'text', editable: false }
              ]}
              onDataChange={gasDataTable.handleDataTableChange}
              onRestoreDefaults={gasDataTable.restoreDefaults}
              title="Natural Gas Data"
              maxHeight="500px"
              pageSize={15}
            />
          </div>
        ) : null}
        showDataTable={gasDataTable.showDataTable}
        onToggleDataTable={gasDataTable.toggleDataTable}
        onDownloadPNG={handleGasDownloadPNG}
        onDownloadCSV={handleGasDownloadCSV}
        pngFilename={`Natural_Gas_${gasView === 'res' ? 'Residential' : gasView === 'com' ? 'Commercial' : gasView === 'total' ? 'Total' : 'Per_Capita'}_Chart`}
        csvFilename={`Natural_Gas_${gasView === 'res' ? 'Residential' : gasView === 'com' ? 'Commercial' : gasView === 'total' ? 'Total' : 'Per_Capita'}_Data`}
      />
      </SectionWrapper>

      {/* Primary Heating Fuel (Supabase-backed) */}
      <SectionWrapper sectionId="heating_fuel" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
        <ChartLayout
          title="Primary Heating Fuel by Year"
          description="This chart shows how household primary heating fuel choices change over time, including natural gas, electricity, and other fuels."
          chartType="line"
          chartSelector={{
            value: heatingFuelChartMode,
            onChange: (value: string) => setHeatingFuelChartMode(value as 'main' | 'other' | 'fossil' | 'compare'),
            options: [
              { value: 'main', label: 'Main fuels' },
              { value: 'other', label: 'Other fuels' },
              { value: 'fossil', label: 'Percent fossil' },
              { value: 'compare', label: 'vs California' }
            ]
          }}
          keyMetrics={
            <KeyMetrics>
              <KeyMetric
                value={heatingFuelMetrics ? `${heatingFuelMetrics.gasPercent.toFixed(1)}%` : '—'}
                label={heatingFuelMetrics ? `Households using gas (${heatingFuelMetrics.latestYear})` : 'Households using gas'}
                color="#f59e0b"
              />
              <KeyMetric
                value={heatingFuelMetrics ? `${heatingFuelMetrics.elecPercent.toFixed(1)}%` : '—'}
                label={heatingFuelMetrics ? `Households using electricity (${heatingFuelMetrics.latestYear})` : 'Households using electricity'}
                color="#3b82f6"
              />
              <KeyMetric
                value={
                  heatingFuelMetrics?.elecShareChangePp != null
                    ? formatPercentagePointChange(heatingFuelMetrics.elecShareChangePp)
                    : '—'
                }
                label={
                  heatingFuelMetrics
                    ? `Electric-heating share change since ${heatingFuelMetrics.baselineYear} (pp)`
                    : 'Electric-heating share change (pp)'
                }
                color="#6b7280"
              />
              <KeyMetric
                value={heatingFuelMetrics ? `${heatingFuelMetrics.fossilPercent.toFixed(1)}%` : '—'}
                label={heatingFuelMetrics ? `Fossil heating share (${heatingFuelMetrics.latestYear})` : 'Fossil heating share'}
                color="#7c2d12"
              />
            </KeyMetrics>
          }
          chartContent={
            <HouseHeatingFuelSupabase
              locationName={currentLocation}
              onMetricsLoaded={setHeatingFuelMetrics}
              chartMode={heatingFuelChartMode}
            />
          }
          narrativeViewId={heatingFuelChartMode}
          overviewContent={
            (() => {
              const authored = buildHeatingFuelAuthoredBundle(
                currentLocation,
                heatingFuelMetrics,
                heatingFuelChartMode as HeatingFuelViewId
              );
              return (
                <OverviewContent
                  title="Overview"
                  description={
                    authored.overview ||
                    heatingFuelOverviewDescription(heatingFuelChartMode as HeatingFuelViewId)
                  }
                >
                  <p>
                    {authored.keyInsights ||
                      (heatingFuelMetrics
                        ? `Loading heating fuel narrative for ${currentLocation}...`
                        : `Loading heating fuel data for ${currentLocation}...`)}
                  </p>
                </OverviewContent>
              );
            })()
          }
          relevanceContent={
            <AuthoredRelevancePanel
              chartId="heating_fuel"
              viewId={heatingFuelChartMode as string}
              bundle={buildHeatingFuelAuthoredBundle(
                currentLocation,
                heatingFuelMetrics,
                heatingFuelChartMode as HeatingFuelViewId
              )}
            />
          }
          equityContent={
            <AuthoredEquityPanel
              bundle={buildHeatingFuelAuthoredBundle(
                currentLocation,
                heatingFuelMetrics,
                heatingFuelChartMode as HeatingFuelViewId
              )}
            />
          }
          methodologyContent={
            (() => {
              const authoredM = buildHeatingFuelAuthoredBundle(
                currentLocation,
                heatingFuelMetrics,
                heatingFuelChartMode as HeatingFuelViewId
              ).methodology;
              const legacy = {
                title: 'Methodology',
                description:
                  'ACS 5-year occupied housing units by primary house-heating fuel (Table B25040) aggregated to the selected place.',
                dataSources: [
                  'U.S. Census Bureau American Community Survey 5-year estimates — Table B25040 (House Heating Fuel)',
                ],
                methodology: [
                  'Percent of households by fuel category uses the same ACS category definitions each year; share changes are expressed in percentage points.',
                ],
                technicalDetails: ['house_heating_fuel', 'HouseHeatingFuelSupabase.tsx'],
              };
              const props = resolveMethodologyContentProps(authoredM, legacy);
              return <MethodologyContent {...props} />;
            })()
          }
          activeView={activeHeatingFuelView}
          onViewChange={setActiveHeatingFuelView}
          showRelevanceEquity={true}
          enableComments={true}
          location={currentLocation}
          appendLocationToTitle={true}
          overviewSectionId="buildings"
          overviewChartId="house_heating_fuel"
        />
      </SectionWrapper>

      {/* Solar PV Capacity & Projects (combined) */}
      <SectionWrapper sectionId="solar_pv" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <div data-testid="solar-section" data-section="solar">
      <ChartLayout
        scrollAnchorId="chart-cumulative-solar"
        title={solarPublicChartTitle}
        description="These charts summarize currently interconnected distributed solar capacity and installer characteristics represented in California Distributed Generation Statistics. The ClimatePlans Solar pipeline uses the Interconnected Project Sites dataset and System Size DC. Capacity-by-cohort views describe project approval cohorts of currently interconnected sites rather than a historical reconstruction of solar capacity. Coverage primarily reflects PG&E, SCE, and SDG&E service territories."
        chartType="bar"
        chartSelector={{
          grouped: true,
          groups: solarGroups.map((group) => ({
            category: group.category,
            label: group.label,
            options: group.options.map((option) => ({ value: option.value, label: option.label }))
          })),
          value: pvView,
          onChange: (value) => setPvView(value as 'capacity' | 'cost' | 'cumulative' | 'perCapita' | 'batteryCost' | 'installers' | 'installersCost')
        }}
        keyMetrics={
          solarDgStatsBlocked ? (
            <KeyMetrics>
              <KeyMetric value="—" label="DGStats source coverage" color="#6b7280" />
              <KeyMetric value="—" label="Metric unavailable" color="#6b7280" />
              <KeyMetric
                value="—"
                label={solarCoverageFacts?.coverageReason ?? DGSTATS_COVERAGE_UNAVAILABLE_LABEL}
                color="#6b7280"
              />
            </KeyMetrics>
          ) : pvView === 'cost' && isSolarCostViewHeld() ? (
            <KeyMetrics>
              <KeyMetric value="—" label="Cost view held" color="#6b7280" />
              <KeyMetric value="—" label="Source review in progress" color="#6b7280" />
              <KeyMetric value="—" label="Project Sites cost basis" color="#6b7280" />
            </KeyMetrics>
          ) : pvView === 'cost' ? (
            <KeyMetrics>
              <KeyMetric
                value={
                  costData?.latestResidentialAggregateCostPerW != null
                    ? formatCurrencyUsd(costData.latestResidentialAggregateCostPerW, { decimals: 2 }) +
                      '/W AC'
                    : '—'
                }
                label={
                  costData?.latestYear
                    ? `Aggregate reported cost (${costData.latestYear}, residential, nominal)`
                    : 'Aggregate reported cost (nominal $/W DC)'
                }
                color="#1e3a8a"
              />
              <KeyMetric
                value={
                  costData?.representedApplicationCount != null
                    ? String(costData.representedApplicationCount)
                    : '—'
                }
                label="Applications represented (aggregate rows)"
                color="#10b981"
              />
              <KeyMetric
                value={costData?.nominalDollarsLabel ?? 'Nominal $/W DC'}
                label="Dollar basis"
                color="#f59e0b"
              />
            </KeyMetrics>
          ) : pvView === 'batteryCost' && isSolarBatteryCostViewHeld() ? (
            <KeyMetrics>
              <KeyMetric value="—" label="Solar + storage cost held" color="#6b7280" />
              <KeyMetric value="—" label="Cost-basis review in progress" color="#6b7280" />
              <KeyMetric value="—" label="Project Sites TSC/DC" color="#6b7280" />
            </KeyMetrics>
          ) : pvView === 'batteryCost' ? (
            <KeyMetrics>
              <KeyMetric 
                value={batteryCostData?.latestSolarOnlyCostPerKw != null ? formatCurrencyUsd(batteryCostData.latestSolarOnlyCostPerKw, { decimals: 0, perUnit: '/kW' }) : '—'} 
                label={
                  batteryCostData?.comparisonYear
                    ? `Solar-only mean (${batteryCostData.comparisonYear}, nominal)`
                    : 'Solar-only mean (nominal $/kW)'
                }
                color="#3b82f6"
              />
              <KeyMetric 
                value={batteryCostData?.latestSolarStorageCostPerKw != null ? formatCurrencyUsd(batteryCostData.latestSolarStorageCostPerKw, { decimals: 0, perUnit: '/kW' }) : '—'} 
                label={
                  batteryCostData?.comparisonYear
                    ? `Solar + storage mean (${batteryCostData.comparisonYear}, nominal)`
                    : 'Solar + storage mean (nominal $/kW)'
                }
                color="#10b981"
              />
              <KeyMetric 
                value={
                  batteryCostData?.latestSolarStorageCostPerKw != null &&
                  batteryCostData?.latestSolarOnlyCostPerKw != null
                    ? formatCurrencyUsd(
                        batteryCostData.latestSolarStorageCostPerKw -
                          batteryCostData.latestSolarOnlyCostPerKw,
                        { decimals: 0, perUnit: '/kW' }
                      )
                    : '—'
                }
                label="Descriptive difference (not battery cost)"
                color="#f59e0b"
              />
            </KeyMetrics>
          ) : pvView === 'installers' ? (
            <KeyMetrics>
              <KeyMetric 
                value={formatCount(installersData?.totalInstallers ?? null)} 
                label="Top pairs"
                color="#3b82f6"
              />
              <KeyMetric 
                value={installersData?.topInstaller ? installersData.topInstaller : 'N/A'} 
                label="Leading pair"
                color="#10b981"
              />
              <KeyMetric 
                value={installersData?.topInstallerKw ? `${installersData.topInstallerKw.toFixed(1)} kW DC` : '—'} 
                label="Top pair represented capacity"
                color="#f59e0b"
              />
            </KeyMetrics>
          ) : pvView === 'installersCost' && isSolarInstallerCostViewHeld() ? (
            <KeyMetrics>
              <KeyMetric value="—" label="Installer cost held" color="#6b7280" />
              <KeyMetric value="—" label="Tied to Cost view review" color="#6b7280" />
              <KeyMetric value="—" label="Project Sites basis" color="#6b7280" />
            </KeyMetrics>
          ) : pvView === 'installersCost' ? (
            <KeyMetrics>
              <KeyMetric
                value={
                  installersCostData?.lowestInstallerCostPerW != null
                    ? formatCurrencyUsd(installersCostData.lowestInstallerCostPerW, { decimals: 2 }) + '/W AC'
                    : '—'
                }
                label="Lowest aggregate cost (top-10 rollup)"
                color="#3b82f6"
              />
              <KeyMetric
                value={
                  installersCostData?.highestInstallerCostPerW != null
                    ? formatCurrencyUsd(installersCostData.highestInstallerCostPerW, { decimals: 2 }) + '/W AC'
                    : '—'
                }
                label="Highest aggregate cost (top-10 rollup)"
                color="#10b981"
              />
              <KeyMetric
                value={installersCostData?.qualifyingPairCount != null ? String(installersCostData.qualifyingPairCount) : '—'}
                label={`Qualifying pairs (n ≥ ${SOLAR_INSTALLER_MIN_SAMPLE})`}
                color="#f59e0b"
              />
            </KeyMetrics>
          ) : pvView === 'perCapita' ? (
            <KeyMetrics>
              <KeyMetric
                value={
                  solarPerCapitaSnapshot?.latestCapacityKw != null
                    ? `${formatMw(solarPerCapitaSnapshot.latestCapacityKw / 1000)} DC`
                    : '—'
                }
                label="Current represented solar capacity"
                color="#1e3a8a"
              />
              <KeyMetric
                value={
                  solarPerCapitaSnapshot?.localSolarKwPerResident != null
                    ? `${solarPerCapitaSnapshot.localSolarKwPerResident.toFixed(3)} kW DC/person`
                    : '—'
                }
                label="Capacity per resident (current snapshot)"
                color="#10b981"
              />
              <KeyMetric
                value={
                  solarPerCapitaSnapshot?.sameYearPopulation != null
                    ? solarPerCapitaSnapshot.sameYearPopulation.toLocaleString()
                    : '—'
                }
                label={
                  solarPerCapitaSnapshot?.latestYear
                    ? `Population denominator (year ${solarPerCapitaSnapshot.latestYear})`
                    : 'Population denominator'
                }
                color="#f59e0b"
              />
            </KeyMetrics>
          ) : pvView === 'cumulative' ? (
            <KeyMetrics>
              <KeyMetric
                value={formatMw(solarCumulativeSnapshot?.cumulativeSolarMw ?? null)}
                label={
                  solarCumulativeSnapshot?.latestYear
                    ? `Cumulative cohort capacity DC (${solarCumulativeSnapshot.latestYear})`
                    : 'Cumulative cohort capacity (DC)'
                }
                color="#1e3a8a"
              />
              <KeyMetric
                value={formatMw(solarCumulativeSnapshot?.latestAnnualAdditionMw ?? null)}
                label={
                  solarCumulativeSnapshot?.latestYear
                    ? `Cohort-year capacity DC (${solarCumulativeSnapshot.latestYear})`
                    : 'Cohort-year capacity (DC)'
                }
                color="#10b981"
              />
              {solarCumulativeSnapshot?.post2015CohortSharePct != null ? (
                <KeyMetric
                  value={`${solarCumulativeSnapshot.post2015CohortSharePct.toFixed(1)}%`}
                  label="Share of current capacity from post-2015 approval cohorts"
                  color="#f59e0b"
                />
              ) : null}
            </KeyMetrics>
          ) : pvView === 'capacity' ? (
            <KeyMetrics>
              <KeyMetric
                value={formatMw(capacityData?.latestYearCapacity ?? null)}
                label={`Cohort capacity DC (${capacityData?.latestYear ?? '—'})`}
              />
              <KeyMetric value={formatCount(projectCountData?.latestYearProjects ?? null)} label={`Projects in dataset (${capacityData?.latestYear ?? '—'})`} />
              {solarCapacityLargestSectorShare != null && (
                <KeyMetric
                  value={`${solarCapacityLargestSectorShare.sharePct.toFixed(1)}%`}
                  label={`Largest sector (${solarCapacityLargestSectorShare.sector}, ${solarCapacityLargestSectorShare.year})`}
                  color="#f59e0b"
                />
              )}
            </KeyMetrics>
          ) : (
            <KeyMetrics>
              <KeyMetric value="—" label="Metric unavailable for this view" color="#6b7280" />
            </KeyMetrics>
          )
        }
        chartContent={
          solarDgStatsBlocked ? (
            <div style={{ padding: '2rem', maxWidth: 720 }}>
              <p>{solarCoverageFacts?.coverageReason ?? DGSTATS_COVERAGE_UNAVAILABLE_LABEL}</p>
            </div>
          ) : (
          <div id={`solar-pv-${pvView}`} data-solar-view={pvView} data-solar-loaded={solarDgStatsBlocked ? 'blocked' : 'true'}>
          {pvView === 'cost' && isSolarCostViewHeld() ? (
            <div style={{ padding: '2rem', maxWidth: 720 }}>
              <p>{SOLAR_COST_HOLD_PUBLIC_MESSAGE}</p>
            </div>
          ) : pvView === 'cost' ? (
            costData ? (
              <ChartRendererSupabase
                chartData={costData.chartData}
                chartOptions={costData.chartOptions}
                height="380px"
                title={`Solar PV Cost per Watt - ${currentLocation}`}
                location={currentLocation}
              />
            ) : (
              <div className="placeholder-chart">
                <p>Loading Solar PV Cost data...</p>
              </div>
            )
          ) : pvView === 'cumulative' ? (
            selectedGeofips ? (
              <CumulativeSolarCapacityChart location={currentLocation} geofips={selectedGeofips} />
            ) : (
              <div className="placeholder-chart">
                <p>Loading location data...</p>
              </div>
            )
          ) : pvView === 'perCapita' ? (
            selectedGeofips ? (
              <SolarPerCapitaComparisonChart location={currentLocation} geofips={selectedGeofips} />
            ) : (
              <div className="placeholder-chart">
                <p>Loading location data...</p>
              </div>
            )
          ) : pvView === 'batteryCost' && isSolarBatteryCostViewHeld() ? (
            <div style={{ padding: '2rem', maxWidth: 720 }}>
              <p>{SOLAR_BATTERY_COST_HOLD_MESSAGE}</p>
            </div>
          ) : pvView === 'batteryCost' ? (
            batteryCostData?.coverageUnavailable ? (
              <div style={{ padding: '2rem', maxWidth: 720 }}>
                <p>{batteryCostData.narrative ?? 'Solar + storage comparison unavailable for this location.'}</p>
              </div>
            ) : batteryCostData ? (
              <ChartRendererSupabase
                chartData={batteryCostData.chartData}
                chartOptions={batteryCostData.chartOptions}
                height="380px"
                title={`Solar-only vs solar + storage (residential) - ${currentLocation}`}
                location={currentLocation}
              />
            ) : (
              <div className="placeholder-chart">
                <p>Loading Solar + Storage Cost data...</p>
              </div>
            )
          ) : pvView === 'installers' ? (
            installersData ? (
              <ChartRendererSupabase
                chartData={installersData.chartData}
                chartOptions={{
                  ...installersData.chartOptions,
                  type: 'bar'
                }}
                height="550px"
                title={`Top Solar PV Installer-Manufacturer Pairs (Capacity) - ${currentLocation}`}
                location={currentLocation}
              />
            ) : (
              <div className="placeholder-chart">
                <p>Loading Solar PV Installers data...</p>
              </div>
            )
          ) : pvView === 'installersCost' && isSolarInstallerCostViewHeld() ? (
            <div style={{ padding: '2rem', maxWidth: 720 }}>
              <p>{SOLAR_INSTALLER_COST_HOLD_MESSAGE}</p>
            </div>
          ) : pvView === 'installersCost' ? (
            installersCostData ? (
              <ChartRendererSupabase
                chartData={installersCostData.chartData}
                chartOptions={{
                  ...installersCostData.chartOptions,
                  type: 'bar'
                }}
                height="550px"
                title={`Top Solar PV Installer-Manufacturer Pairs (Cost) - ${currentLocation}`}
                location={currentLocation}
              />
            ) : (
              <div className="placeholder-chart">
                <p>Loading Solar PV Installers Cost data...</p>
              </div>
            )
          ) : capacityData ? (
            <div>
              {pvView === 'capacity' && (
            <ChartRendererSupabase
              chartData={capacityData.chartData}
                  chartOptions={{
                    ...capacityData.chartOptions,
                    type: 'bar',
                    scales: {
                      ...(capacityData.chartOptions?.scales || {}),
                      x: { stacked: true },
                      y: { stacked: true, title: { display: true, text: 'Capacity (MW DC)' } }
                    }
                  }}
                  height="380px"
                  title={`Solar PV Capacity (Stacked) - ${currentLocation}`}
                  location={currentLocation}
                />
              )}
            </div>
          ) : (
            <div className="placeholder-chart"><p>Loading Solar PV data…</p></div>
          )}
          </div>
          )
        }
        overviewContent={
          <OverviewContent
            title="Overview"
            description={solarAuthoredBundle.overview || 'Solar installations'}
          >
            {solarAuthoredBundle.keyInsights ? <p>{solarAuthoredBundle.keyInsights}</p> : null}
          </OverviewContent>
        }
        relevanceContent={
          solarAuthoredBundle.relevance ? (
            <AuthoredRelevancePanel bundle={solarAuthoredBundle} />
          ) : undefined
        }
        equityContent={
          solarAuthoredBundle.equity ? <AuthoredEquityPanel bundle={solarAuthoredBundle} /> : undefined
        }
                methodologyContent={(() => {
          const structured = structuredFromAuthoredBundle(solarAuthoredBundle);
          const legacy = {
            title: 'Methodology',
            description: solarAuthoredBundle.overview ?? 'Solar installations methodology.',
            dataSources: [] as string[],
            methodology: [] as string[],
          };
          return <MethodologyContent {...resolveMethodologyContentProps(structured, legacy)} />;
        })()}
        activeView={activeView}
        onViewChange={toggleView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        appendLocationToTitle={true}
        overviewSectionId="buildings"
        overviewChartId="cumulative_solar_capacity"
        onDownloadPNG={() => {
          const viewLabel = pvView === 'capacity' ? 'Capacity' : pvView === 'cost' ? 'Cost' : pvView === 'cumulative' ? 'Cumulative' : pvView === 'perCapita' ? 'Per_Capita' : pvView === 'batteryCost' ? 'Battery_Cost' : pvView === 'installers' ? 'Installers' : 'Installers_Cost';
          makeDownloadPNG(`#solar-pv-${pvView}`, `Solar_PV_${viewLabel}`, currentLocation);
        }}
        onDownloadCSV={
          isSolarPublicExportView(pvView) && !solarDgStatsBlocked
            ? () => {
                const viewLabel =
                  pvView === 'capacity'
                    ? 'Capacity'
                    : pvView === 'cumulative'
                      ? 'Cumulative'
                      : pvView === 'perCapita'
                        ? 'Per_Capita'
                        : 'Installers_Capacity';
                const filename =
                  pvView === 'cumulative'
                    ? formatFilename(currentLocation, 'Solar_PV_Cumulative_Sector_Full_Table')
                    : formatFilename(currentLocation, `Solar_PV_${viewLabel}_Data`);
                if (pvView === 'capacity') {
                  const rows = buildSolarCapacityExportRows(currentLocation, capacityData?.chartData);
                  if (rows.length) downloadDataAsCSV(rows, filename);
                  return;
                }
                if (pvView === 'perCapita') {
                  const rows = buildSolarPerCapitaExportRows(currentLocation, solarPerCapitaSnapshot);
                  if (rows.length) downloadDataAsCSV(rows, filename);
                  return;
                }
                if (pvView === 'installers') {
                  const rows = buildSolarInstallerCapacityExportRows(
                    currentLocation,
                    installersData?.installerExportRows ?? []
                  );
                  if (rows.length) downloadDataAsCSV(rows, filename);
                  return;
                }
                if (pvView === 'cumulative' && selectedGeofips) {
                  void buildSolarCumulativeExportRows(currentLocation, selectedGeofips).then((rows) => {
                    if (rows.length) downloadDataAsCSV(rows, filename);
                  });
                }
              }
            : undefined
        }
        pngFilename={formatFilename(currentLocation, `Solar_PV_${pvView === 'capacity' ? 'Capacity' : pvView === 'cost' ? 'Cost' : pvView === 'cumulative' ? 'Cumulative' : pvView === 'perCapita' ? 'Per_Capita' : pvView === 'batteryCost' ? 'Battery_Cost' : pvView === 'installers' ? 'Installers' : 'Installers_Cost'}`)}
        csvFilename={
          pvView === 'cumulative'
            ? formatFilename(currentLocation, 'Solar_PV_Cumulative_Sector_Full_Table')
            : formatFilename(
                currentLocation,
                `Solar_PV_${pvView === 'capacity' ? 'Capacity' : pvView === 'cost' ? 'Cost' : pvView === 'perCapita' ? 'Per_Capita' : pvView === 'batteryCost' ? 'Battery_Cost' : pvView === 'installers' ? 'Installers' : 'Installers_Cost'}_Data`
              )
        }
      />
      </div>
      </SectionWrapper>

      {/* Heat Pump Adoption Section */}
      <SectionWrapper sectionId="heat_pump" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <ChartLayout
        title="Heat Pump Adoption"
        description="TECH Clean California incentive records show participating heat-pump projects—not all installations in the community."
        chartType="line"
        chartSelector={{
          options: allowedHeatPumpOptions.map((option) => ({ value: option.value, label: option.label })),
          value: heatPumpView,
          onChange: (value) => {
            setHeatPumpView(value as 'cost' | 'technology' | 'units' | 'perCapita');
          }
        }}
        keyMetrics={
          <KeyMetrics>
            {heatPumpView === 'units' && (
              <>
                <KeyMetric
                  value={heatPumpMetrics?.latestYearUnits != null ? formatCount(heatPumpMetrics.latestYearUnits) : '—'}
                  label={heatPumpMetrics?.latestCompleteYear ? `TECH-incentivized units (${heatPumpMetrics.latestCompleteYear})` : 'TECH-incentivized units'}
                />
                <KeyMetric
                  value={heatPumpMetrics ? formatCount(heatPumpMetrics.totalUnits) : '—'}
                  label="Cumulative TECH-incentivized units"
                />
                <KeyMetric
                  value={formatSignedPercentChange(heatPumpMetrics?.yoyGrowth ?? null)}
                  label="Change from prior complete year"
                />
              </>
            )}
            {heatPumpView === 'perCapita' && (
              <>
                <KeyMetric
                  value={
                    heatPumpPerCapitaFacts?.localPer10k != null
                      ? heatPumpPerCapitaFacts.localPer10k.toFixed(2)
                      : '—'
                  }
                  label={
                    heatPumpPerCapitaFacts?.comparisonYear
                      ? `Local TECH units per 10,000 (${heatPumpPerCapitaFacts.comparisonYear})`
                      : 'Local TECH units per 10,000 residents'
                  }
                />
                <KeyMetric
                  value={
                    heatPumpPerCapitaFacts?.comparatorAvailable && heatPumpPerCapitaFacts.californiaPer10k != null
                      ? heatPumpPerCapitaFacts.californiaPer10k.toFixed(2)
                      : '—'
                  }
                  label={
                    heatPumpPerCapitaFacts?.comparisonYear
                      ? `California TECH units per 10,000 (${heatPumpPerCapitaFacts.comparisonYear})`
                      : 'California TECH units per 10,000 residents'
                  }
                />
                <KeyMetric
                  value={
                    heatPumpPerCapitaFacts?.percentDifferenceVsCalifornia != null
                      ? formatSignedPercentChange(heatPumpPerCapitaFacts.percentDifferenceVsCalifornia)
                      : '—'
                  }
                  label="Percent difference vs California (same year)"
                />
              </>
            )}
            {heatPumpView === 'cost' && (
              <>
                <KeyMetric
                  value={
                    heatPumpCostFacts?.localHvacProjectCost != null
                      ? formatCurrencyUsd(heatPumpCostFacts.localHvacProjectCost, { decimals: 0 })
                      : '—'
                  }
                  label={
                    heatPumpCostFacts?.comparisonYear
                      ? `Average reported HVAC project cost (${heatPumpCostFacts.comparisonYear})`
                      : 'Average reported HVAC project cost'
                  }
                />
                <KeyMetric
                  value={
                    heatPumpCostFacts?.localHpwhProjectCost != null
                      ? formatCurrencyUsd(heatPumpCostFacts.localHpwhProjectCost, { decimals: 0 })
                      : '—'
                  }
                  label={
                    heatPumpCostFacts?.comparisonYear
                      ? `Average reported HPWH project cost (${heatPumpCostFacts.comparisonYear})`
                      : 'Average reported HPWH project cost'
                  }
                />
                <KeyMetric
                  value={(() => {
                    if (
                      heatPumpCostFacts?.localSelectedAverage == null ||
                      heatPumpCostFacts.californiaSelectedAverage == null
                    ) {
                      return '—';
                    }
                    const { differencePercent } = compareLocalToCalifornia(
                      heatPumpCostFacts.localSelectedAverage,
                      heatPumpCostFacts.californiaSelectedAverage
                    );
                    return formatSignedPercentChange(differencePercent);
                  })()}
                  label={`Local vs California (${heatPumpCostFacts?.selectedTechnology ?? 'HVAC'} project cost)`}
                />
              </>
            )}
            {heatPumpView === 'technology' && (
              <>
                <KeyMetric
                  value={heatPumpMetrics ? formatCount(heatPumpMetrics.hvacUnits) : '—'}
                  label="HVAC units (recorded)"
                />
                <KeyMetric
                  value={heatPumpMetrics ? formatCount(heatPumpMetrics.hpwhUnits) : '—'}
                  label="HPWH units (recorded)"
                />
                <KeyMetric
                  value={
                    heatPumpMetrics &&
                    heatPumpTechnologyShareEligible(
                      heatPumpMetrics.hvacUnits,
                      heatPumpMetrics.hpwhUnits,
                      heatPumpMetrics.totalUnits
                    )
                      ? `${((heatPumpMetrics.hvacUnits / heatPumpMetrics.totalUnits) * 100).toFixed(1)}% / ${((heatPumpMetrics.hpwhUnits / heatPumpMetrics.totalUnits) * 100).toFixed(1)}%`
                      : '—'
                  }
                  label="HVAC / HPWH share of TECH-recorded units"
                />
              </>
            )}
          </KeyMetrics>
        }
        chartContent={
          <div id={`heat-pump-${heatPumpView}`}>
            {selectedGeofips ? (
              heatPumpView === 'units' ? (
                <HeatPumpUnitsChart location={currentLocation} geofips={selectedGeofips} onDataLoaded={(rows: Record<string, unknown>[]) => setHeatPumpChartData(rows as unknown as Record<string, string | number>[])} />
              ) : heatPumpView === 'perCapita' ? (
                <HeatPumpPerCapitaChart location={currentLocation} geofips={selectedGeofips} onDataLoaded={(rows: Record<string, unknown>[]) => setHeatPumpChartData(rows as unknown as Record<string, string | number>[])} />
              ) : heatPumpView === 'cost' ? (
                <HeatPumpCostChart location={currentLocation} geofips={selectedGeofips} onDataLoaded={(rows: Record<string, unknown>[]) => setHeatPumpChartData(rows as unknown as Record<string, string | number>[])} />
              ) : (
                <HeatPumpTechnologyChart location={currentLocation} geofips={selectedGeofips} onDataLoaded={(rows: Record<string, unknown>[]) => setHeatPumpChartData(rows as unknown as Record<string, string | number>[])} />
              )
            ) : (
              <div>Loading location data...</div>
            )}
          </div>
        }
        overviewContent={
          heatPumpView === 'perCapita' ? (
            <OverviewContent
              title="Per Capita"
              description="This chart shows the number of heat pumps installed per 10,000 residents in the selected location compared with the state average. Heat pumps are energy-efficient systems that can provide both heating and cooling using electricity. Tracking heat pump adoption per capita helps show how quickly communities are transitioning away from fossil fuel-based heating systems toward cleaner technologies."
            >
              <p>Per-capita TECH program records are shown compared with California using the latest common complete year in the chart. These are program-participation counts, not total market adoption.</p>
            </OverviewContent>
          ) : heatPumpView === 'cost' ? (
            <OverviewContent
              title="Installation Costs"
              description="This chart shows the average installation cost for HVAC heat pump systems and heat pump water heaters (HPWH) among TECH participating contractors. It compares installation costs in the selected location with the California average over time. Tracking installation costs helps show how prices change as clean heating technologies become more widely adopted."
            >
              <p>Installation costs for HVAC heat pump systems are generally higher than HPWH systems because HVAC installations are usually larger and more complex. In {currentLocation}, installation costs may vary from year to year due to changes in equipment prices, labor availability, contractor capacity, and incentive programs.</p>
              <p>Comparing {currentLocation} with the California average helps show whether local installation costs follow broader statewide trends or differ due to regional market conditions. Changes over time may also reflect improvements in technology, market growth, or shifts in demand for electrification.</p>
            </OverviewContent>
          ) : heatPumpView === 'technology' ? (
            <OverviewContent
              title="Technology Mix"
              description="This chart shows the average project cost per unit for different heat pump technologies in the selected location over time. The technologies shown include HVAC heat pump systems and heat pump water heaters (HPWH). Tracking installation costs by technology helps illustrate how prices change as these electrification technologies become more widely adopted."
            >
              <p>HVAC heat pump systems generally have higher installation costs per unit than HPWH systems because they involve larger equipment and more complex installation processes. Costs for both technologies may change over time depending on factors such as equipment prices, labor availability, contractor experience, and market demand.</p>
              <p>Comparing installation costs across technologies helps show which systems may be more affordable or easier to adopt for households. Changes in costs over time may also reflect improvements in technology, supply chains, or incentive programs that support electrification.</p>
            </OverviewContent>
          ) : (
            <OverviewContent
              title="Heat Pump Adoption Overview"
              description="This chart shows heat pump installations in the selected location over time. Heat pumps are electric systems used for heating, cooling, and water heating. They are generally more energy efficient than traditional gas systems and play an important role in building electrification."
            >
              <p>
                {heatPumpMetrics ? (() => {
                  const { totalUnits, yoyGrowth, firstYear, lastYear, latestCompleteYear, latestYearUnits } = heatPumpMetrics;
                  const trendText =
                    yoyGrowth != null && yoyGrowth > 5 ? 'increased' :
                    yoyGrowth != null && yoyGrowth < -5 ? 'decreased' :
                    'remained relatively stable';
                  const changePct =
                    yoyGrowth != null ? Math.abs(yoyGrowth).toFixed(1) : '—';
                  const direction = changeDirectionWord(yoyGrowth);
                  return (
                    <>
                      The TECH dataset records {latestYearUnits != null ? latestYearUnits.toLocaleString() : '—'} incentivized heat-pump units in {latestCompleteYear ?? lastYear} and {totalUnits.toLocaleString()} across {firstYear}–{lastYear}. Installations {direction} by {changePct}% from the prior complete year. These are program-participation records, not a census of heat pumps in {currentLocation}.
                    </>
                  );
                })() : `Loading heat pump data for ${currentLocation}...`}
              </p>
              {heatPumpMetrics && (
                <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#64748b' }}>
                  <strong>Note:</strong> These totals reflect installations in the TECH Clean California database only, which tracks installations that received program incentives. This does not represent all heat pump installations in {currentLocation}, as many installations may occur without program incentives.
                </p>
              )}
            </OverviewContent>
          )
        }
        relevanceContent={
          heatPumpView === 'perCapita' ? (
            <RelevanceContent
              title="Relevance"
              description="Heat pump adoption is an important indicator of building electrification and the transition to cleaner heating and cooling systems."
            >
              <div className="methodology-details-simple">
                <h5>Climate Impact</h5>
                <p>Heat pumps can significantly reduce greenhouse gas emissions when they replace natural gas or other fossil fuel heating systems, especially when electricity comes from cleaner energy sources.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Energy Planning</h5>
                <p>Tracking adoption levels helps communities understand how quickly electrification technologies are spreading and whether additional policies, incentives, or outreach programs may be needed to support adoption.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Infrastructure Planning</h5>
                <p>Higher adoption of heat pumps may also influence electricity demand and help inform long-term energy and grid planning.</p>
              </div>
            </RelevanceContent>
          ) : heatPumpView === 'cost' ? (
            <RelevanceContent
              title="Relevance"
              description="Installation costs are an important factor influencing how quickly households adopt heat pump technologies."
            >
              <div className="methodology-details-simple">
                <h5>Climate Impact</h5>
                <p>Heat pumps can significantly reduce greenhouse gas emissions when they replace natural gas or other fossil fuel heating systems. Lower installation costs can make these technologies more accessible and accelerate the transition to cleaner building energy systems.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Energy Planning</h5>
                <p>Understanding installation cost trends helps policymakers and communities evaluate whether financial incentives or rebate programs are effective in supporting heat pump adoption.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Market Development</h5>
                <p>Tracking installation costs can also provide insight into how the clean energy market is evolving as more contractors gain experience installing these systems.</p>
              </div>
            </RelevanceContent>
          ) : heatPumpView === 'technology' ? (
            <RelevanceContent
              title="Relevance"
              description="Understanding the cost of different heat pump technologies is important for evaluating the progress of building electrification."
            >
              <div className="methodology-details-simple">
                <h5>Climate Impact</h5>
                <p>Heat pump technologies can significantly reduce greenhouse gas emissions when they replace fossil fuel heating systems such as natural gas furnaces or water heaters. Lower technology costs can help accelerate the transition to cleaner building energy systems.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Market Trends</h5>
                <p>Tracking costs across technologies helps identify how the clean energy market is evolving and which technologies are becoming more accessible to households.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Policy Planning</h5>
                <p>Cost trends can also help policymakers evaluate the effectiveness of rebate programs and incentives designed to encourage heat pump adoption.</p>
              </div>
            </RelevanceContent>
          ) : (
            <RelevanceContent
              title="Relevance"
              description="Heat pump adoption is an important sign of progress toward building electrification and reduced fossil fuel use."
            >
              <div className="methodology-details-simple">
                <h5>Climate Impact</h5>
                <p>Heat pumps can reduce greenhouse gas emissions compared to gas-fired heating systems, especially as the electricity grid becomes cleaner over time.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Energy Efficiency</h5>
                <p>Because heat pumps are more efficient than traditional systems, they can lower overall energy demand while maintaining indoor comfort.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Policy Planning</h5>
                <p>Tracking installation trends helps cities evaluate the effectiveness of incentive programs and measure progress toward decarbonization goals.</p>
              </div>
            </RelevanceContent>
          )
        }
        equityContent={
          heatPumpView === 'perCapita' ? (
            <EquityContent
              title="Equity"
              description="Access to heat pump technology may vary across households depending on income levels, housing type, and building conditions."
            >
              <div className="methodology-details-simple">
                <h5>Financial Barriers</h5>
                <p>Although heat pumps can reduce energy costs over time, the upfront installation cost may be a barrier for some households without financial assistance.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Housing Constraints</h5>
                <p>Renters and residents of older buildings may have fewer opportunities to install heat pumps compared to homeowners.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Energy Equity</h5>
                <p>Programs that provide rebates, incentives, or targeted support for low-income households can help expand access to heat pump technology and ensure the benefits of electrification are shared more broadly.</p>
              </div>
            </EquityContent>
          ) : heatPumpView === 'cost' ? (
            <EquityContent
              title="Equity"
              description="Access to heat pump technologies may vary across households depending on income levels, housing conditions, and access to financial support programs."
            >
              <div className="methodology-details-simple">
                <h5>Financial Barriers</h5>
                <p>Although heat pumps can reduce energy costs over time, the upfront installation cost can be a major barrier for many households, especially without incentives or rebates.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Housing Constraints</h5>
                <p>Renters and residents of older buildings may face additional challenges installing HVAC heat pumps or HPWH systems due to building design, landlord approval, or infrastructure limitations.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Energy Equity</h5>
                <p>Programs that provide rebates, financing options, or targeted support for low-income households can help expand access to clean heating technologies and ensure that the benefits of electrification are shared more broadly.</p>
              </div>
            </EquityContent>
          ) : heatPumpView === 'technology' ? (
            <EquityContent
              title="Equity"
              description="The cost of different heat pump technologies may affect who is able to adopt them."
            >
              <div className="methodology-details-simple">
                <h5>Financial Barriers</h5>
                <p>Higher installation costs may limit adoption for households with fewer financial resources, especially without incentives or financing programs.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Housing Constraints</h5>
                <p>Some technologies may be easier to install in certain types of buildings, meaning renters or residents of older homes may face additional barriers.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Energy Equity</h5>
                <p>Policies that provide rebates, financing options, or targeted support for low-income households can help ensure that the benefits of heat pump technology are accessible to a wider range of communities.</p>
              </div>
            </EquityContent>
          ) : (
            <EquityContent
              title="Equity"
              description="Heat pump adoption may vary depending on income level, housing type, and access to financial support."
            >
              <div className="methodology-details-simple">
                <h5>Cost Barriers</h5>
                <p>Upfront installation costs can limit adoption without rebates or financing programs. Households with limited financial resources may face greater challenges.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Rental Housing</h5>
                <p>Renters depend on landlords to upgrade heating systems, which may slow adoption in multifamily housing.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Access to Incentives</h5>
                <p>Participation in incentive programs may vary by awareness, eligibility requirements, or administrative barriers.</p>
              </div>
            </EquityContent>
          )
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="The chart compiles heat pump installation records for the selected location using available program data. Heat pump installation data from the TECH Clean California database, which tracks installations receiving program incentives."
            dataSources={[
              '<strong>TECH Clean California Database</strong> - <a href="https://techcleanca.com/heat-pump-data/" target="_blank" rel="noopener noreferrer">https://techcleanca.com/heat-pump-data/</a>',
              'The TECH Clean California program gathers and publishes data on heat pump water heating (HPWH) and heat pump heating, ventilation, and air-conditioning (HP HVAC) markets in California',
              'All data is anonymized and aggregated per California Public Utility Commission (CPUC) guidelines in Decision 11-07-056, Decision 14-05-016, Decision 20-03-027, and Decision 21-11-002',
              'Heat pump installation records by location and year from participating contractors',
              'Cost data by system type (HVAC, HPWH)',
              'Building type and geography classifications'
            ]}
            methodology={[
              'Data aggregated from installations that received incentives through the TECH Clean California program',
              'Only installations participating in the incentive program are included in this database',
              'This represents a subset of all heat pump installations, as many installations occur without program incentives',
              'Costs calculated per unit for each system type',
              'Location data matched by geo_fips codes',
              'Annual trends calculated from installation records in the TECH database'
            ]}
          />
        }
        activeView={activeView}
        onViewChange={toggleView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        appendLocationToTitle={true}
        onDownloadPNG={() => {
          const viewLabel = heatPumpView === 'cost' ? 'Installation_Costs' : heatPumpView === 'technology' ? 'Technology_Costs' : heatPumpView === 'perCapita' ? 'Per_Capita' : 'Units_Installed';
          makeDownloadPNG(`#heat-pump-${heatPumpView}`, `Heat_Pump_${viewLabel}`, currentLocation);
        }}
        onDownloadCSV={() => {
          if (!heatPumpChartData?.length) return;
          const viewLabel = heatPumpView === 'cost' ? 'Installation_Costs' : heatPumpView === 'technology' ? 'Technology_Costs' : heatPumpView === 'perCapita' ? 'Per_Capita' : 'Units_Installed';
          downloadDataAsCSV(heatPumpChartData, formatFilename(currentLocation, `Heat_Pump_${viewLabel}_Data`));
        }}
        pngFilename={formatFilename(currentLocation, `Heat_Pump_${heatPumpView === 'cost' ? 'Installation_Costs' : heatPumpView === 'technology' ? 'Technology_Costs' : heatPumpView === 'perCapita' ? 'Per_Capita' : 'Units_Installed'}`)}
        csvFilename={formatFilename(currentLocation, `Heat_Pump_${heatPumpView === 'cost' ? 'Installation_Costs' : heatPumpView === 'technology' ? 'Technology_Costs' : heatPumpView === 'perCapita' ? 'Per_Capita' : 'Units_Installed'}_Data`)}
      />
      </SectionWrapper>

      {/* Business establishments (sector & size; total row feeds policy commercial intensity) */}
      <SectionWrapper sectionId="business_establishments" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
        <BusinessEstablishmentsSection
          location={currentLocation}
          geofips={selectedGeofips != null ? String(selectedGeofips) : null}
        />
      </SectionWrapper>

      {/* Housing to Employees Ratio */}
      <SectionWrapper sectionId="housing_to_employees" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <HousingToEmployees
        location={currentLocation}
        geofips={selectedGeofips}
        preferredState="California"
      />
      </SectionWrapper>

      {/* Single Family Detached Housing Chart */}
      <SectionWrapper sectionId="single_family_detached" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
        <SingleFamilyDetached 
          location={currentLocation}
          geofips={selectedGeofips}
        />
      </SectionWrapper>

      {/* Owner-Occupied Housing Map */}
      <SectionWrapper sectionId="owner_occupied_map" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <ChartLayout
        title="Owner-Occupied Housing Map"
        description="This map shows the share of owner-occupied housing in the selected location relative to California, highlighting tenure patterns relevant to retrofit potential."
        chartType="map"
        keyMetrics={
          ownerOccupiedHousingMetrics ? (
            <KeyMetrics>
              <KeyMetric
                value={`${ownerOccupiedHousingMetrics.currentValue.toFixed(1)}%`}
                label="% Owner-Occupied Housing"
              />
              <KeyMetric
                value={`${ownerOccupiedHousingMetrics.californiaAverage.toFixed(1)}%`}
                label="% Owner-Occupied Housing in California"
              />
            </KeyMetrics>
          ) : undefined
        }
        chartContent={
          <div id="owner-occupied-housing-map">
            <GenericMapTiered 
              location={currentLocation}
              lockedVariable="owner_occupied_housing"
              hideVariableSelector={true}
              onDataLoaded={handleOwnerOccupiedHousingDataLoaded}
            />
          </div>
        }
        overviewContent={
          ownerOccupiedHousingMetrics ? (
            <OverviewContent
              title="Overview"
              description={`This map compares the percent of houses occupied by their owners across different regions of ${currentLocation}.`}
            >
              <p>
                {currentLocation} has {ownerOccupiedHousingMetrics.currentValue.toFixed(1)}% owner-occupied housing, which is {ownerOccupiedHousingMetrics.currentValue > ownerOccupiedHousingMetrics.californiaAverage ? 'higher' : 'lower'} than the California average of {ownerOccupiedHousingMetrics.californiaAverage.toFixed(1)}%. Owner-occupied housing represents households where residents own their homes, which can influence the ability to make energy efficiency improvements, install solar PV, and electrify building systems. Higher owner-occupancy rates may indicate greater potential for building decarbonization investments.
              </p>
            </OverviewContent>
          ) : undefined
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance"
            description="Geographic tenure patterns are a primary factor in urban planning, especially for climate and energy policy."
          >
            <div className="methodology-details-simple">
              <h5>Energy Improvements</h5>
              <p>Homeowners generally have more agency to invest in long-term building decarbonization. The high-owner-occupancy zones on the map represent the easiest targets for residential electrification programs.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Infrastructure Planning</h5>
              <p>Areas with higher renter concentrations often see higher turnover and different parking/transit needs compared to more stable, owner-occupied neighborhoods.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity"
            description="Tenure is a proxy for wealth accumulation and long-term housing security."
          >
            <div className="methodology-details-simple">
              <h5>Wealth Disparity</h5>
              <p>The map highlights the geographic divide in wealth; owner-occupied areas typically correlate with higher household incomes. Meanwhile, renters are more vulnerable to rising market rates and displacement.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="Owner-Occupied Housing analysis based on American Community Survey data with geographic visualization."
            dataSources={[
              'American Community Survey 5-Year Estimates (ACS5)',
              'U.S. Census Bureau demographic and housing data',
              'California census tract boundaries',
              'Housing tenure data (owner-occupied vs. renter-occupied)'
            ]}
            methodology={[
              'Owner-occupied housing percentage calculated as owner-occupied units divided by total occupied housing units',
              'Data represents housing tenure patterns by census tract from American Community Survey',
              'Geographic visualization shows owner-occupancy rates across census tracts',
              'Comparison calculated relative to California state average',
              '5-year estimates provide more reliable data for smaller geographic areas'
            ]}
          />
        }
        activeView={activeOwnerOccupiedView}
        onViewChange={setActiveOwnerOccupiedView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        appendLocationToTitle={true}
        onDownloadPNG={() => makeDownloadPNG('#owner-occupied-housing-map', 'Owner_Occupied_Housing_Map', currentLocation)}
        onDownloadCSV={() => {
          if (ownerOccupiedHousingMetrics) {
            const rows = [{ location: currentLocation, year: ownerOccupiedHousingMetrics.year, variable: ownerOccupiedHousingMetrics.variable, value: ownerOccupiedHousingMetrics.currentValue, california_average: ownerOccupiedHousingMetrics.californiaAverage }];
            downloadDataAsCSV(rows, formatFilename(currentLocation, 'Owner_Occupied_Housing_Data'));
          }
        }}
        pngFilename={formatFilename(currentLocation, 'Owner_Occupied_Housing_Map')}
        csvFilename={formatFilename(currentLocation, 'Owner_Occupied_Housing_Data')}
      />
      </SectionWrapper>

      {/* Urban Heat Island Map */}
      <SectionWrapper sectionId="urban_heat_island_map" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <ChartLayout
        title="Urban Heat Island Map"
        description="This map shows tract-level urban heat island intensity percentile for the selected location."
        chartType="map"
        chartContent={
          <div id="urban-heat-island-map">
            <UrbanHeatIslandMap
              selectedLocation={currentLocation}
              onTractRowsLoaded={handleUhiTractRowsLoaded}
            />
          </div>
        }
        overviewContent={
          <OverviewContent
            title="Overview"
            description={urbanHeatIslandOverviewDescription(
              'Urban heat island intensity helps identify neighborhoods where built environment and land cover trap more heat, often increasing health and cooling burdens.'
            )}
          >
            <p>
              {(() => {
                const bundle = buildUrbanHeatIslandAuthoredBundle(
                  summarizeUrbanHeatIslandTracts(currentLocation, uhiTractRows)
                );
                return bundle.keyInsights || `Use the map to compare tract-level modeled heat-island patterns for ${currentLocation}. Percentile ranks describe relative intensity among tracts; they are not direct temperature observations.`;
              })()}
            </p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance"
            description="Heat intensity varies within and across communities, and those differences affect public health, energy demand, and climate adaptation planning."
          >
            <div className="methodology-details-simple">
              <h5>Cooling and Public Health</h5>
              <p>Hotter areas can experience greater heat stress and higher cooling demand, especially during prolonged warm periods.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Planning Priorities</h5>
              <p>Spatial heat patterns can inform urban forestry, reflective surfaces, cool roofs, and neighborhood-level resilience investments.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity"
            description="Heat exposure and adaptive capacity are not distributed evenly; mapping hot spots helps target equitable interventions."
          >
            <div className="methodology-details-simple">
              <h5>Disproportionate Exposure</h5>
              <p>Historically underinvested neighborhoods may have less canopy and more heat-retaining surfaces, increasing heat risk.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Targeted Investment</h5>
              <p>Place-based heat data supports prioritization of cooling infrastructure and protections where impacts are likely greatest.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="Urban heat island mapping uses Supabase tract and place tables with percentile-based choropleth styling."
            dataSources={[
              'CalEPA Urban Heat Island Index (processed into Supabase)',
              'Supabase tables: urban_heat_island_tract and urban_heat_island_place',
              'California tract, place, and county boundary GeoJSON layers'
            ]}
            methodology={[
              'Map loads tract-level records using uhii_percentile',
              'Choropleth colors use fixed percentile bins from 0-100',
              'Tooltip includes percentile, UHII value, and converted approximate Fahrenheit difference'
            ]}
          />
        }
        activeView={activeUrbanHeatIslandView}
        onViewChange={setActiveUrbanHeatIslandView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        onDownloadPNG={() => makeDownloadPNG('#urban-heat-island-map', 'Urban_Heat_Island_Map', currentLocation)}
        pngFilename={formatFilename(currentLocation, 'Urban_Heat_Island_Map')}
      />
      </SectionWrapper>

      {/* Market Rent Chart */}
      <SectionWrapper sectionId="housing_rent_category" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <ChartLayout
        title="Housing Units by Rent"
        description="This chart shows the number of housing units in the selected location grouped by monthly rent category over time. Tracking rent categories helps show how the rental market is changing and whether housing is becoming more or less affordable over time."
        chartType={marketRentChartView === 'stacked' ? 'line' : 'line'}
        chartContent={
          <div id="market-rent-chart">
            <MarketRentChart key={`${currentLocation}-${marketRentChartView}`} location={currentLocation} chartType={marketRentChartView} onDataLoaded={setMarketRentData} />
          </div>
        }
        chartSelector={{
          options: allowedMarketRentOptions.map((option) => ({ value: option.value, label: option.label })),
          value: marketRentChartView,
          onChange: (value) => setMarketRentChartView(value as 'line' | 'stacked')
        }}
        overviewContent={
          marketRentChartView === 'line' ? (
            <OverviewContent
              title="Overview"
              description="This chart shows the number of housing units in this location grouped by rent price over time. Tracking rent categories helps show how the rental market is changing, and whether housing is become less affordable over time."
            >
              <p>
                {marketRentKeyInsights
                  ? `In ${marketRentKeyInsights.latestYear}, the largest share of rental units in ${currentLocation} fell within the ${marketRentKeyInsights.largestCategoryLabel} range. Compared to ${marketRentKeyInsights.earliestYear}, there has been ${marketRentKeyInsights.overallChange} in higher-rent categories.`
                  : marketRentNarrative || 'Select a location to view housing units by rent insights and trends.'}
              </p>
            </OverviewContent>
          ) : (
            <OverviewContent
              title="Overview"
              description="This chart shows the number of housing units in this location grouped by rent price over time. Tracking rent categories helps show how the rental market is changing, and whether housing is become less affordable over time."
            >
              <p>
                {marketRentKeyInsights
                  ? `In ${marketRentKeyInsights.latestYear}, the largest share of rental units in ${currentLocation} fell within the ${marketRentKeyInsights.largestCategoryLabel} range. Compared to ${marketRentKeyInsights.earliestYear}, there has been ${marketRentKeyInsights.overallChange} in higher-rent categories.`
                  : marketRentNarrativeStacked || 'Select a location to view housing units by rent insights and trends.'}
              </p>
            </OverviewContent>
          )
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance"
            description="Housing affordability is closely connected to economic stability and community well-being. Changes in rent category distributions reflect changes in housing costs, market demand, higher-end developments, and inflation."
          >
            <div className="methodology-details-simple">
              <h5>Climate &amp; Land Use Impact</h5>
              <p>If rents rise significantly in one area, residents may move farther away in search of affordable housing. This increases commute distances and emissions from transportation.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Planning &amp; Policy</h5>
              <p>Governments can use rent data to understand housing market pressures and inform zoning decisions, housing production strategies, and affordability programs.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity"
            description="Changes in rent categories affect household stability differently depending on their income bracket."
          >
            <div className="methodology-details-simple">
              <h5>Affordability Pressure</h5>
              <p>
                As more units shift into higher rent categories, lower-income households face increased financial stress or risk of displacement. Additionally, this increases the number of households that are rent-burdened, or households spending over 30% of their income on rent.
              </p>
            </div>
            <div className="methodology-details-simple">
              <h5>Housing Stability &amp; Access</h5>
              <p>
                Limited availability of lower-cost rental units reduces housing options for groups with lower incomes, such as students, seniors, and working-class residents. Rising rents also increase housing insecurity and make it harder for families to remain in their communities.
              </p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="The chart uses Census survey data to estimate the number of rental housing units in each rent category over time."
            dataSources={[
              'Data aggregated by year and standardized rent category',
              'Source: American Community Survey (ACS) DP04 table'
            ]}
            methodology={[
              'Data is fetched from the `market_rent_percent` table for the selected location\'s `geofips`.',
              'Raw ACS data categories changed in 2015. To ensure consistency across all years (2010-2022), categories are standardized to the 2015+ structure.',
              'Specifically, for years prior to 2015, the original "$500 to $749" and "$750 to $999" categories are combined into a single "$500 to $999" category to match the 2015+ data structure.',
              'The line chart displays the number of housing units over time for each standardized category as separate lines.',
              'The stacked area chart displays the same data with categories stacked to show total housing units and the proportion of each category.',
              'Missing ACS rent categories remain missing in the chart; they are not stacked as zero.'
            ]}
          />
        }
        activeView={activeView}
        onViewChange={toggleView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        appendLocationToTitle={true}
        onDownloadPNG={() => makeDownloadPNG('#market-rent-chart', 'Housing_Units_by_Rent_Category', currentLocation)}
        onDownloadCSV={() => {
          if (!marketRentData.length) return;
          const rows = marketRentData.map((r) => ({ year: r.year, rent_category: r.rent_category, estimate: r.estimate, percent: r.percent }));
          downloadDataAsCSV(rows, formatFilename(currentLocation, 'Housing_Units_by_Rent_Category_Data'));
        }}
        pngFilename={formatFilename(currentLocation, 'Housing_Units_by_Rent_Category')}
        csvFilename={formatFilename(currentLocation, 'Housing_Units_by_Rent_Category_Data')}
      />
      </SectionWrapper>

      {/* HCD Annual Progress Report — affordable housing production */}
      <SectionWrapper
        sectionId="affordable_housing_production"
        bgMap={BUILDINGS_SECTION_BG}
        refs={sectionObserverRefs}
        visible={sectionVisible}
        getSectionStyle={getSectionStyle}
      >
        <AffordableHousingAprSection />
      </SectionWrapper>

      {/* Multifamily Energy Efficiency Targeting Tool - California Data */}
      <SectionWrapper sectionId="bayren_tool" bgMap={BUILDINGS_SECTION_BG} refs={sectionObserverRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
        <CaliforniaBayRENTool />
      </SectionWrapper>

      <CustomSectionsForPage pageId="buildings" locationName={currentLocation} />
      <CustomDatasetsForPage pageId="buildings" locationName={currentLocation} />
      </div>
    </div>
  );
};


